import { avisoRetencionFicha, causacionDeFactura, diferenciasConXML, panelXmlFprov } from "./fprovXML.js";
import { bandejaHTML, marcarCausadaEnBandeja } from "./bandejaFacturas.js";
import { getSaldoFactura } from "./facturas.js";
import { renderCuentasCobroTab, renderCxpDirectasTab } from "./cuentasPagar.js";
import { ajustarCostoInventario, ajustarInventario } from "../core/accounting.js";
import { desgloseIVA, etiquetaRegimen, regimenDeLinea } from "../core/iva.js";
import { eliminarAdjunto, subirAdjunto, urlFirmadaAdjunto } from "../core/storage.js";
import { renderDocFormModal } from "./documentos.js";

import { anticipoAplicado, anticipoDevuelto, anticipoDisponible, crearAsiento, generarAsientoPagoOCP, getCostoPromedio, indiceInventario, ingresarInventario } from "../core/accounting.js";
import { saveData } from "../core/cloudSync.js";
import { C, EMPRESA, ESTADO_PAGO, MEDIOS_PAGO, REGIMENES_FISCALES } from "../core/config.js";
import { state } from "../core/state.js";
import { addDias, btnStyle, escapeHTML, fmtCOP, fmtDate, fmtPrecio, formatMiles, genId, inputStyle, normalize, parseNumber, searchBox, today } from "../core/utils.js";
import { render } from "../ui/app.js";
import { calcularImpuestosCausacion, celdaTerceroCosto, destinoCostoCelda, getDescuentosFinancieros, getLiquidacionOP, getMontoExtinguidoOCP, getMontoPagadoOCP, getPagosOCP, getRetencionesOCP, getSaldoOCP, getTotalCxP, getTotalPagado, impuestosResumenRowsHTML, sincronizarOcpDeCostos } from "./documentos.js";
import { pedirDestinoCostos, sincronizarCxpDirectasOP } from "./cuentasPagar.js";
import { calcularDiasRestantes } from "./reportes.js";
import { abrirDocumentoEnApp } from "../ui/visorDoc.js";

// Cada FACTURA del proveedor es su propio mundo: una OCP causada con dos
// facturas (dos causaciones parciales) se muestra en DOS filas, cada una con
// su número, su fecha y su saldo. Un pago puede venir con imputación explícita
// (p._facturas = [{fcId, monto}], la deja el pago conjunto de seleccionadas);
// lo no imputado —pagos viejos, retenciones y descuentos— se aplica a la
// factura más antigua primero (FIFO). El registro contable sigue siendo por
// OCP, que es donde vive el pasivo.
export function filasCxPDeOcps(ocps) {
  const filas = [];
  ocps.forEach(o => {
    const facts = [...(o._facturasContables || [])].sort((x, y) => (x.fecha || "").localeCompare(y.fecha || ""));
    if (facts.length <= 1) {
      filas.push({ o, fact: facts[0] || null, totalFila: o.total, abonadoFila: getMontoPagadoOCP(o), saldoFila: getSaldoOCP(o), key: o.id + "::" + (facts[0] ? facts[0].id : "solo") });
      return;
    }
    const cap = {}, abon = {};
    facts.forEach(f => { cap[f.id] = parseNumber(f.total) || 0; abon[f.id] = 0; });
    // El "poder extintivo" de un pago es monto + descuento financiero: ambos
    // apagan pasivo. Las retenciones de la causación también, pero sin factura
    // concreta: van al fondo FIFO.
    let libre = getRetencionesOCP(o.id);
    getPagosOCP(o).forEach(p => {
      let poder = (parseNumber(p.monto) || 0) + (parseNumber(p.descuento) || 0);
      (Array.isArray(p._facturas) ? p._facturas : []).forEach(a => {
        if (!(a.fcId in abon)) return;
        const m = Math.min(parseNumber(a.monto) || 0, poder, cap[a.fcId] - abon[a.fcId]);
        abon[a.fcId] += m; poder -= m;
      });
      libre += poder;
    });
    facts.forEach(f => {
      const m = Math.min(libre, cap[f.id] - abon[f.id]);
      abon[f.id] += m; libre -= m;
    });
    facts.forEach(f => {
      filas.push({ o, fact: f, totalFila: cap[f.id], abonadoFila: abon[f.id], saldoFila: cap[f.id] - abon[f.id], multi: true, key: o.id + "::" + f.id });
    });
  });
  return filas;
}

export function renderCuentasPorPagar() {
  // Tres pestañas: facturas de proveedor (OCP causadas), CxP directas (costos
  // de OP sin orden de compra) y cuentas de cobro (su legalización).
  const cxpTab = state.cxpTab || "ocp";
  const tabsCxp = [
    ["ocp", "🧾 Facturas proveedor (OCP)"],
    ["directas", "💸 CxP directas"],
    ["cc", "📄 Cuentas de cobro"]
  ].map(([id, label]) => `<button data-action="cxp-tab" data-tab="${id}" style="padding:9px 16px;background:${cxpTab === id ? "white" : "transparent"};border:none;border-bottom:3px solid ${cxpTab === id ? C.accent : "transparent"};color:${cxpTab === id ? "#0E7490" : C.muted};font-weight:700;font-size:12px;cursor:pointer;font-family:inherit;">${label}</button>`).join("");
  const barraTabs = `<div style="display:flex;gap:4px;margin-bottom:10px;border-bottom:1px solid ${C.border};">${tabsCxp}</div>`;

  if (!state.cxpFilter) state.cxpFilter = "pendientes"; // pendientes | pagadas | todas
  const filter = state.cxpFilter;
  const query = state.searches.cxp || "";

  let ocps = (state.data.ocp || [])
    // Solo OCP procesadas como factura (causadas contablemente) — NIIF: sin causación no hay CxP
    .filter(o => o._procesado && o._procesado.factura);
  if (filter === "pendientes") ocps = ocps.filter(o => o.estado_pago !== "pagada" && o.estado !== "cancelada");
  else if (filter === "pagadas") ocps = ocps.filter(o => o.estado_pago === "pagada");
  // "todas" no filtra estado_pago pero sí requiere causación

  // Búsqueda
  if (query.trim()) {
    const q = normalize(query);
    ocps = ocps.filter(o => {
      const prov = state.data.proveedores.find(p => p.id === o.proveedor_id);
      return normalize(o.numero).includes(q)
          || normalize(prov ? prov.nombre : "").includes(q)
          || normalize(prov ? prov.nit : "").includes(q);
    });
  }

  // Ordenar por fecha descendente
  ocps = [...ocps].sort((a,b) => (b.fecha || "").localeCompare(a.fecha || ""));

  const totalPorPagar = getTotalCxP();
  const totalPagado = getTotalPagado();
  const countPorPagar = state.data.ocp.filter(o => o.estado_pago !== "pagada" && o.estado !== "cancelada").length;
  const countPagado = state.data.ocp.filter(o => o.estado_pago === "pagada").length;

  const header = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:9px;padding-bottom:10px;border-bottom:3px solid ${C.gold};">
      <div style="display:flex;gap:10px;align-items:center;flex:1;">
        <img src="${window.LOGO}" alt="Aura" style="height:42px;object-fit:contain;"/>
        <div>
          <h2 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">Cuentas por Pagar</h2>
          <p style="margin:3px 0 0;color:${C.muted};font-size:11.5px;">Gestión de pagos a proveedores</p>
        </div>
      </div>
      <button onclick="exportarExcelCxP()" style="background:#16a34a;color:white;border:none;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;white-space:nowrap;flex-shrink:0;">📥 Exportar Excel</button>
    </div>
  `;

  const stats = `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:10px;margin-bottom:10px;">
      <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:#92400e;font-weight:700;margin-bottom:4px;">💸 POR PAGAR</div>
        <div style="font-size:17px;font-weight:800;color:#d97706;">${fmtCOP(totalPorPagar)}</div>
        <div style="font-size:10.5px;color:#92400e;margin-top:4px;">${countPorPagar} ${countPorPagar === 1 ? "factura" : "facturas"}</div>
      </div>
      <div style="background:#d1fae5;border:1px solid #86efac;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:#065f46;font-weight:700;margin-bottom:4px;">✅ PAGADO (HISTÓRICO)</div>
        <div style="font-size:17px;font-weight:800;color:#10b981;">${fmtCOP(totalPagado)}</div>
        <div style="font-size:10.5px;color:#065f46;margin-top:4px;">${countPagado} ${countPagado === 1 ? "pago" : "pagos"} registrados</div>
      </div>
      <div style="background:#e4eaf5;border:1px solid #93c5fd;border-radius:6px;padding:11px;">
        <div style="font-size:10.5px;color:#1e40af;font-weight:700;margin-bottom:4px;">📊 TOTAL OCPs</div>
        <div style="font-size:17px;font-weight:800;color:${C.blue};">${fmtCOP(totalPorPagar + totalPagado)}</div>
        <div style="font-size:10.5px;color:#1e40af;margin-top:4px;">${state.data.ocp.length} órdenes de compra</div>
      </div>
    </div>
  `;

  // Tabs de filtro
  const filterTabs = ["pendientes","pagadas","todas"].map(f => {
    const labels = { pendientes:"💸 Por pagar", pagadas:"✅ Pagadas", todas:"📋 Todas" };
    const active = filter === f;
    return `<button data-action="cxp-filter" data-filter="${f}" style="padding:8px 14px;background:${active ? "white" : "transparent"};border:none;border-bottom:3px solid ${active ? C.accent : "transparent"};color:${active ? "#10b981" : C.muted};font-weight:700;font-size:12px;cursor:pointer;font-family:inherit;">${labels[f]}</button>`;
  }).join("");

  let list;
  if (state.data.ocp.length === 0) {
    list = `
      <div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;">
        <div style="font-size:40px;margin-bottom:10px;">💸</div>
        <div style="color:${C.muted};font-size:14px;">Sin órdenes de compra registradas</div>
      </div>
    `;
  } else if (ocps.length === 0) {
    list = `
      <div style="padding:22px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;">
        <div style="font-size:30px;margin-bottom:8px;">🎉</div>
        <div style="color:${C.text};font-size:14px;font-weight:600;">${filter === "pendientes" ? "¡No hay cuentas por pagar!" : (filter === "pagadas" ? "Sin pagos registrados aún" : "Sin resultados")}</div>
      </div>
    `;
  } else {
    const filasCxP = filasCxPDeOcps(ocps);
    const sel = state.cxpSelFact || {};
    const filasSel = filasCxP.filter(f => sel[f.key] && f.saldoFila > 0.5);

    const rows = filasCxP.map(({ o, fact, totalFila, abonadoFila, saldoFila, multi, key }) => {
      const prov = state.data.proveedores.find(p => p.id === o.proveedor_id) || {};
      const op = o._origen ? state.data.pedidos.find(p => p.id === o._origen.id) : null;
      // Estado por FILA: cada factura muestra su propia realidad de pago
      const pagoFila = saldoFila <= 0.5 ? "pagada" : (abonadoFila > 0.5 ? "parcial" : "por_pagar");
      const pinfo = ESTADO_PAGO[pagoFila] || ESTADO_PAGO.por_pagar;
      const payBtn = saldoFila > 0.5 ? `<button data-action="pagar-ocp" data-id="${o.id}" style="${btnStyle("#10b981",null,true)}">${pagoFila === "parcial" ? "💳 Abonar" : "💳 Pagar"}</button>` : "";
      let pagoInfo = "";
      if (pagoFila === "parcial") {
        pagoInfo = `<div style="font-size:10px;color:${C.muted};margin-top:3px;">Abonado: <strong style="color:#10b981;">${fmtCOP(abonadoFila)}</strong> · Saldo: <strong style="color:#d97706;">${fmtCOP(saldoFila)}</strong></div>`;
      } else if (pagoFila === "pagada" && o._pago) {
        pagoInfo = `<div style="font-size:10px;color:${C.muted};margin-top:3px;">${fmtDate(o._pago.fecha)} • ${o._pago.medio || ""}</div>`;
      }
      const factNum = fact ? (fact.factNum || (o._procesado && o._procesado.factura_numero)) : (o._procesado && o._procesado.factura_numero);
      const factFecha = fact ? fact.fecha : (o._procesado && (o._procesado.factura_fecha_doc || o._procesado.factura_fecha));
      const checkCell = saldoFila > 0.5
        ? `<input type="checkbox" data-action="cxp-fact-toggle" data-key="${key}" data-prov="${escapeHTML(o.proveedor_id || "")}" ${sel[key] ? "checked" : ""} style="width:15px;height:15px;cursor:pointer;accent-color:#10b981;"/>`
        : "";
      return `
        <tr style="border-top:1px solid ${C.border};${sel[key] && saldoFila > 0.5 ? "background:#f0fdf4;" : ""}">
          <td style="padding:6px 4px 6px 10px;text-align:center;">${checkCell}</td>
          <td style="padding:6px 10px;">
            <div style="font-weight:700;color:${C.purple};">${escapeHTML(o.numero)}${multi ? ` <span title="Esta OCP tiene varias facturas del proveedor: cada una se muestra por separado" style="font-size:9px;font-weight:800;color:#075985;background:#e0f2fe;border-radius:4px;padding:1px 4px;">${escapeHTML(String(factNum || ""))}</span>` : ""}</div>
            ${op ? `<div style="font-size:10px;color:${C.muted};margin-top:2px;">⬅ ${escapeHTML(op.numero)}</div>` : ""}
          </td>
          <td style="padding:6px 10px;color:${C.muted};">${fmtDate(factFecha || o.fecha)}</td>
          <td style="padding:6px 10px;">
            <div style="font-weight:600;color:${C.text};">${escapeHTML(prov.nombre || "—")}</div>
            ${prov.nit ? `<div style="font-size:10px;color:${C.muted};">NIT ${escapeHTML(prov.nit)}</div>` : ""}
          </td>
          <td style="padding:6px 10px;">
            ${factNum
              ? `<div style="font-weight:700;color:#12264D;font-size:11.5px;">📄 ${escapeHTML(String(factNum))}</div>
                 <div style="font-size:10px;color:${C.muted};">${fmtDate(factFecha || "")}</div>`
              : `<span style="color:${C.muted};font-size:10.5px;">—</span>`}
          </td>
          <td style="padding:6px 10px;">
            <span style="background:${pinfo.bg};color:${pinfo.color};padding:3px 9px;border-radius:6px;font-size:10.5px;font-weight:700;display:inline-block;">${pinfo.label}</span>
            ${pagoInfo}
          </td>
          <td style="padding:6px 10px;font-weight:700;font-size:14px;">${fmtCOP(totalFila)}</td>
          <td style="padding:6px 10px;text-align:right;white-space:nowrap;">${payBtn}</td>
        </tr>
      `;
    }).join("");

    let barraSel = "";
    if (filasSel.length > 0) {
      const provSel = state.data.proveedores.find(p => p.id === filasSel[0].o.proveedor_id) || {};
      const totalSel = filasSel.reduce((s, f) => s + f.saldoFila, 0);
      barraSel = `
        <div style="background:#ecfdf5;border:1px solid #10b981;border-radius:6px;padding:10px 14px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
          <div style="font-size:12px;color:#065f46;">
            <strong>${filasSel.length}</strong> factura${filasSel.length === 1 ? "" : "s"} de <strong>${escapeHTML(provSel.nombre || "—")}</strong> seleccionada${filasSel.length === 1 ? "" : "s"}
            · saldo total <strong>${fmtCOP(totalSel)}</strong>
          </div>
          <div style="display:flex;gap:8px;">
            <button data-action="cxp-pago-multi-limpiar" style="${btnStyle(null,"secondary")}">Limpiar</button>
            <button data-action="cxp-pago-multi-abrir" style="${btnStyle("#10b981")}">💳 Pagar seleccionadas</button>
          </div>
        </div>
      `;
    }

    list = barraSel + `
      <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#f1f5f9;">
              <th style="padding:6px 4px 6px 10px;" title="Selecciona facturas del mismo proveedor para pagarlas juntas"></th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">OCP</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Fecha</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Proveedor</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Nº Factura Prov.</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Estado de pago</th>
              <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">Total</th>
              <th style="padding:6px 10px;"></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }

  if (cxpTab === "directas") return header + barraTabs + renderCxpDirectasTab();
  if (cxpTab === "cc") return header + barraTabs + renderCuentasCobroTab();

  return `
    ${header}
    ${barraTabs}
    ${stats}
    <div style="display:flex;gap:6px;margin-bottom:10px;border-bottom:1px solid ${C.border};">${filterTabs}</div>
    ${searchBox("search-cxp", query, "Buscar por número OCP, proveedor o NIT...", "#10b981")}
    ${list}
  `;
}

export function renderCargarFirmadoRC(rcId) {
  const rc = (state.data.remisiones||[]).find(r=>r.id===rcId);
  if (!rc) return;
  const tieneFirmado = !!rc._docFirmado;

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1100;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:460px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.22);">
        <div style="padding:10px 12px;border-bottom:1px solid #e2e8f0;background:#f8fafc;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:16px;font-weight:800;color:#12264D;">📋 Remisión Firmada — ${escapeHTML(rc.numero||"RC")}</div>
            <div style="font-size:10.5px;color:#6b7280;margin-top:2px;">Documento de entrega firmado por el cliente</div>
          </div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:#6b7280;cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          ${tieneFirmado ? `
          <div style="background:#f8fafc;border:1px solid #bbf7d0;border-radius:6px;padding:12px 14px;margin-bottom:9px;display:flex;align-items:center;gap:10px;">
            <span style="font-size:17px;">✅</span>
            <div>
              <div style="font-weight:700;color:#065f46;font-size:12px;">${escapeHTML(rc._docFirmado.nombre)}</div>
              <div style="font-size:10.5px;color:#6b7280;">Cargado: ${fmtDate(rc._docFirmado.fechaCarga)} · ${rc._docFirmado.tipo?.toUpperCase()}</div>
            </div>
          </div>
          <div style="display:flex;gap:8px;margin-bottom:9px;">
            <button onclick="verDocFirmadoRC('${rcId}')" style="flex:1;background:#12264D;color:white;border:none;padding:8px;border-radius:7px;font-weight:700;cursor:pointer;font-size:11.5px;">👁️ Ver documento</button>
            <button data-action="eliminar-firmado-rc" data-id="${rcId}" style="background:#fee2e2;border:1px solid #fca5a5;color:#dc2626;padding:8px 14px;border-radius:7px;font-weight:700;cursor:pointer;font-size:11.5px;">✕ Eliminar</button>
          </div>` : ""}
          <div style="border:2px dashed #bfdbfe;border-radius:6px;padding:11px;text-align:center;margin-bottom:9px;">
            <div style="font-size:32px;margin-bottom:8px;">📎</div>
            <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:4px;">${tieneFirmado?"Reemplazar":"Cargar"} documento firmado</div>
            <div style="font-size:10.5px;color:#64748b;margin-bottom:12px;">PDF, JPG o PNG — Se guardará como adjunto a la factura electrónica</div>
            <input type="file" id="upload-firmado-rc" accept=".pdf,.jpg,.jpeg,.png" style="display:none;" onchange="procesarFirmadoRC('${rcId}')"/>
            <button onclick="document.getElementById('upload-firmado-rc').click()" style="background:#12264D;color:white;border:none;padding:9px 20px;border-radius:6px;font-weight:700;cursor:pointer;font-size:12px;">📁 Seleccionar archivo</button>
          </div>
          <div style="font-size:10.5px;color:#64748b;text-align:center;">El documento firmado quedará adjunto en la factura electrónica generada desde esta remisión.</div>
        </div>
      </div>
    </div>
  `;
  render();
}

export async function procesarFirmadoRC(rcId) {
  const input = document.getElementById("upload-firmado-rc");
  if (!input || !input.files || !input.files[0]) return;
  const file = input.files[0];
  const tipos = ["application/pdf","image/jpeg","image/jpg","image/png"];
  if (!tipos.includes(file.type)) { alert("Solo PDF, JPG o PNG"); return; }
  if (file.size > 10 * 1024 * 1024) { alert("El archivo no puede superar 10 MB"); return; }

  const docFirmado = {
    id: genId(),
    nombre: file.name,
    tipo: file.type.includes("pdf") ? "pdf" : "imagen",
    tamaño: file.size,
    fechaCarga: today()
  };

  const guardar = () => {
    const rc = (state.data.remisiones||[]).find(r=>r.id===rcId);
    if (!rc) return;
    // Al reemplazar, borrar el adjunto anterior del bucket (best-effort)
    if (rc._docFirmado && rc._docFirmado.storagePath && rc._docFirmado.storagePath !== docFirmado.storagePath) {
      eliminarAdjunto(rc._docFirmado.storagePath);
    }
    const updatedRC = { ...rc, _docFirmado: docFirmado };
    saveData("remisiones", state.data.remisiones.map(r => r.id===rcId ? updatedRC : r));
    // Si hay factura electrónica relacionada, adjuntar también ahí (solo la referencia)
    if (rc._facturaId) {
      const fact = (state.data.facturas||[]).find(f=>f.id===rc._facturaId);
      if (fact) {
        const docs = [...(fact._documentos||[]).filter(d=>!d._esFirmadoRC), { ...docFirmado, _esFirmadoRC:true }];
        saveData("facturas", state.data.facturas.map(f=>f.id===fact.id ? {...f,_documentos:docs} : f));
      }
    }
    state.modal = null;
    render();
    alert(`✅ Documento firmado "${file.name}" cargado correctamente.`);
  };

  // El archivo vive en Supabase Storage (bucket privado), no en base64 dentro de
  // los datos: evita reventar la cuota de localStorage y viaja con el tenant.
  try {
    docFirmado.storagePath = await subirAdjunto("remisiones-firmadas", docFirmado.id, file);
    guardar();
  } catch (err) {
    console.error("⚠️ No se pudo subir el documento al bucket de Storage:", err.message || err);
    if (file.size > 2 * 1024 * 1024) {
      alert("⚠️ No se pudo subir el documento a la nube y es demasiado grande para guardarlo localmente (máx. 2 MB en modo local). Verifica que el bucket 'adjuntos' exista en Supabase.");
      return;
    }
    // Respaldo temporal mientras el bucket no exista: base64 como antes
    const reader = new FileReader();
    reader.onload = function(e) {
      docFirmado.base64 = e.target.result;
      guardar();
    };
    reader.readAsDataURL(file);
  }
}

export async function verDocFirmadoRC(rcId) {
  const rc = (state.data.remisiones||[]).find(r=>r.id===rcId);
  const doc = rc && rc._docFirmado;
  if (!doc || (!doc.base64 && !doc.storagePath)) { alert("No hay documento disponible"); return; }
  const titulo = `Remisión firmada ${rc.numero || ""}`.trim();
  if (doc.storagePath) {
    try {
      const url = await urlFirmadaAdjunto(doc.storagePath, 300);
      // En el celular / app instalada se ve adentro; en escritorio, en pestaña.
      if (!abrirDocumentoEnApp(titulo, { url })) window.open(url, "_blank");
    } catch (err) {
      alert("⚠️ No se pudo abrir el documento: " + (err.message || err));
    }
    return;
  }
  const html = `<html><body style="margin:0;background:#000;"><img src="${rc._docFirmado.base64}" style="max-width:100%;display:block;margin:auto;" onerror="this.style.display='none';document.body.innerHTML='<embed src=\\'${rc._docFirmado.base64}\\' width=100% height=100% />';"/></body></html>`;
  if (abrirDocumentoEnApp(titulo, { html })) return;
  const win = window.open();
  if (win) {
    win.document.write(html);
  }
}

export function getCantidadFacturadaOCP(ocp, articulo) {
  return ((ocp._facturasContables)||[])
    .reduce((s,f)=>s+((f.items||[]).find(i=>i.articulo===articulo)?.cantidad||0), 0);
}

export function getMontoFacturadoOCP(ocp) {
  return ((ocp._facturasContables)||[]).reduce((s,f)=>s+(parseNumber(f.total)||0), 0);
}

// ─── ¿Esta OCP está pendiente de causar? ─────────────────────────────────────
// Una sola puerta para el contador, el listado y el panel del proveedor. Antes
// cada uno preguntaba solo «¿no informativa y no facturada?», así que las
// órdenes CANCELADAS seguían saliendo como «por causar»: de las 18 que había
// en la lista, 7 estaban canceladas (OCP-0289, OCP-0300…). Una orden cancelada
// no se compró: no hay factura de proveedor que causar.
export function ocpPorCausar(o) {
  return !!o && !esOcpInformativa(o) && o.estado !== "cancelada" && !ocpTotalmenteFacturada(o);
}

export function ocpTotalmenteFacturada(ocp) {
  if (ocp._procesado && ocp._procesado.factura) return true;
  return (ocp.items||[]).every(it =>
    getCantidadFacturadaOCP(ocp, it.articulo) >= (parseNumber(it.cantidad)||0) - 0.001
  );
}

// ─── Las líneas seleccionadas para causar ────────────────────────────────────
// Devuelve el subtotal y las líneas con el nombre del artículo, que es lo que
// permite resolver el IVA de cada una. La suma estaba copiada en cinco sitios;
// ahora hay una sola, porque un IVA distinto en cada pantalla de la misma
// causación sería peor que el error que se está corrigiendo.
// Las líneas de una OCP que entran en ESTA factura: las marcadas y con
// cantidad. El proveedor puede facturar solo parte de la orden (un artículo de
// los dos, por ejemplo): el que se desmarca no se causa y queda pendiente en la
// orden para la factura que lo traiga. Es la única puerta: la tarjeta, el pie,
// la revisión y la causación la usan todas.
export function fprovItemsMarcados(s) {
  return ((s && s.items) || []).filter(it => it.on !== false && (parseNumber(it.cant) || 0) > 0);
}

// ¿Hay algo que causar? Una OCP marcada con al menos un artículo marcado.
export function fprovHayLineas(fp) {
  return Object.values((fp && fp.ocps) || {}).some(s => s.on && fprovItemsMarcados(s).length > 0);
}

export function fprovSeleccion(fp) {
  const lineas = [];
  Object.entries((fp && fp.ocps) || {}).forEach(([, s]) => {
    if (!s.on) return;
    fprovItemsMarcados(s).forEach(it => {
      const total = Math.round((parseNumber(it.cant) || 0) * (parseNumber(it.pu) || 0));
      if (total) lineas.push({ articulo: it.art, total });
    });
  });
  return { sub: lineas.reduce((a, l) => a + l.total, 0), lineas };
}

export function fprovInitOcp(ocpId) {
  const ocp = (state.data.ocp||[]).find(o=>o.id===ocpId);
  if (!ocp || state.fprov.ocps[ocpId]) return;
  state.fprov.ocps[ocpId] = {
    on: false,
    items: (ocp.items||[]).map(it => {
      const pend = Math.max(0,(parseNumber(it.cantidad)||0) - getCantidadFacturadaOCP(ocp, it.articulo));
      return { art:it.articulo, desc:it.descripcion||"", cant:pend, pu:parseNumber(it.precio_unitario)||0, on:true };
    }).filter(it=>it.cant>0)
  };
}

export function fprovSeleccionarOcp(ocpId) {
  const ocp = (state.data.ocp||[]).find(o=>o.id===ocpId);
  if (!ocp) return;
  if (!state.fprov) state.fprov = { provId:"", numFact:"", fechaFact:today(), ocps:{}, busqOcp:"" };
  const fp = state.fprov;

  // Si hay un proveedor distinto activo → no permitir mezclar
  if (fp.provId && fp.provId !== ocp.proveedor_id) {
    alert("⚠️ Solo puedes cruzar OCPs del mismo proveedor. Limpia la selección actual primero.");
    return;
  }

  // Fijar el proveedor
  fp.provId = ocp.proveedor_id;

  // Inicializar los datos de esta OCP si no existen
  fprovInitOcp(ocpId);

  // Marcar / desmarcar la OCP
  fp.ocps[ocpId].on = !fp.ocps[ocpId].on;
  render();
}

export function fprovReset() {
  state.fprov = null;
  state.modal  = null;
}

// ─── Módulo Facturas de Proveedor (Compras) ─────────────────────────────────
// Antes vivía como una pestaña dentro de Contabilidad. Causar la factura del
// proveedor es trabajo de compras —lo hace quien recibe la mercancía y la
// factura—, así que está donde se usa. Dos pestañas: lo que falta por causar y
// lo ya causado.
export function renderFacturasProveedor() {
  const tab = state.factProvTab === "hechas" ? "hechas" : "por_causar";
  const pendientes = (state.data.ocp || []).filter(ocpPorCausar).length;
  const hechas = (state.data.ocp || []).reduce((s, o) => s + ((o._facturasContables || []).length), 0);

  const boton = (id, label, cuenta) => `
    <button data-action="fact-prov-tab" data-tab="${id}" style="
      padding:10px 18px;background:${tab===id?"white":"transparent"};
      border:none;border-bottom:3px solid ${tab===id?C.accent:"transparent"};
      color:${tab===id?"#0E7490":C.muted};font-weight:700;font-size:12px;cursor:pointer;margin-bottom:-1px;font-family:inherit;
    ">${label} <span style="font-weight:400;color:#94a3b8;">${cuenta}</span></button>`;

  return `
    <div>
      <h2 style="margin:0 0 3px;font-size:19px;color:${C.text};font-weight:800;">🧾 Facturas de Proveedor</h2>
      <div style="font-size:11.5px;color:${C.muted};margin-bottom:12px;">
        Causa la factura del proveedor contra sus órdenes de compra. Al causarla queda el asiento contable y la cuenta por pagar.
      </div>
      <div style="border-bottom:1px solid ${C.border};margin-bottom:12px;">
        ${boton("por_causar", "📦 Por causar", pendientes)}
        ${boton("hechas", "🗂️ Causaciones hechas", hechas)}
      </div>
      ${tab === "hechas" ? renderCausacionesHechas() : renderFacturasProveedorTab()}
    </div>`;
}

// Listado completo de causaciones ya registradas, con su proveedor, su OCP y el
// asiento que generaron. Es el respaldo de lo que ya se contabilizó.
export function renderCausacionesHechas() {
  const q = normalize(state.busqCausaciones || "");
  let filas = [];
  (state.data.ocp || []).forEach(ocp => {
    const prov = (state.data.proveedores || []).find(x => x.id === ocp.proveedor_id) || {};
    (ocp._facturasContables || []).forEach(fc => {
      filas.push({ ...fc, ocpNum: ocp.numero, ocpId: ocp.id, provNom: prov.nombre || "—", provNit: prov.nit || "" });
    });
  });
  filas.sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""));
  if (q) {
    filas = filas.filter(f => normalize(f.factNum).includes(q) || normalize(f.provNom).includes(q) || normalize(f.ocpNum).includes(q));
  }

  const total = filas.reduce((s, f) => s + (parseNumber(f.total) || 0), 0);

  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:8px;overflow:hidden;">
      <div style="padding:9px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
        <input type="text" placeholder="🔍 Buscar por factura, proveedor u OCP…" value="${escapeHTML(state.busqCausaciones||"")}"
          oninput="state.busqCausaciones=this.value;render();"
          style="flex:1;min-width:220px;padding:6px 10px;border:1px solid ${C.border};border-radius:7px;font-size:11.5px;outline:none;"/>
        <div style="font-size:11.5px;color:${C.muted};">
          ${filas.length} causaci${filas.length===1?"ón":"ones"} · <b style="color:${C.text};">${fmtCOP(total)}</b>
        </div>
      </div>
      ${filas.length === 0 ? `
        <div style="text-align:center;padding:26px;color:${C.muted};font-size:12px;">
          ${q ? "Sin resultados para esa búsqueda." : "Todavía no se ha causado ninguna factura de proveedor."}
        </div>` : `
        <div style="overflow-x:auto;">
          <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
            <thead>
              <tr style="background:#f8fafc;">
                ${["Factura","Proveedor","OCP","Fecha","Subtotal","IVA","Total",""].map((h,i)=>
                  `<th style="padding:8px 10px;text-align:${i>=4&&i<=6?"right":"left"};font-size:10.5px;font-weight:700;color:${C.text};">${h}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${filas.map(f => `
                <tr style="border-top:1px solid ${C.border};">
                  <td style="padding:7px 10px;font-weight:700;color:${C.mathiz};">📄 ${escapeHTML(f.factNum||"—")}</td>
                  <td style="padding:7px 10px;">${escapeHTML(f.provNom)}${f.provNit?`<div style="font-size:10px;color:${C.muted};">NIT ${escapeHTML(f.provNit)}</div>`:""}</td>
                  <td style="padding:7px 10px;">
                    <span data-action="ir-a-documento" data-tipo="OCP" data-id="${f.ocpId}"
                      style="color:${C.purple};font-weight:700;cursor:pointer;text-decoration:underline;">${escapeHTML(f.ocpNum||"—")}</span>
                  </td>
                  <td style="padding:7px 10px;color:${C.muted};">${escapeHTML(fmtDate(f.fecha))}</td>
                  <td style="padding:7px 10px;text-align:right;">${fmtCOP(f.subtotal)}</td>
                  <td style="padding:7px 10px;text-align:right;color:${C.muted};">${fmtCOP(f.iva)}</td>
                  <td style="padding:7px 10px;text-align:right;font-weight:800;color:${C.text};">${fmtCOP(f.total)}</td>
                  <td style="padding:7px 10px;text-align:right;">
                    ${f._asientoId ? `<span style="font-size:10px;color:#047857;font-weight:700;">✓ contabilizada</span>` : `<span style="font-size:10px;color:${C.amber};font-weight:700;">sin asiento</span>`}
                  </td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>`}
    </div>`;
}

export function renderFacturasProveedorTab() {
  if (!state.fprov) state.fprov = { provId:"", numFact:"", fechaFact:today(), ocps:{}, busqOcp:"" };
  const fp   = state.fprov;
  const prov = (state.data.proveedores||[]).find(p=>p.id===fp.provId) || {};
  const provs = state.data.proveedores || [];

  // ── Inicializar items de OCPs del proveedor activo ──────────────────────
  if (fp.provId) {
    (state.data.ocp||[])
      .filter(o=>o.proveedor_id===fp.provId && ocpPorCausar(o))
      .forEach(o=>fprovInitOcp(o.id));
  }

  // ── Cálculo de totales ───────────────────────────────────────────────────
  // IVA y retenciones se calculan SIEMPRE con calcularImpuestosCausacion(),
  // que aplica automáticamente la configuración fiscal del tercero (régimen,
  // responsable de IVA, autorretenedor) + los Parámetros Fiscales vigentes.
  const _sel = fprovSeleccion(fp);
  const grandSub = _sel.sub;
  const calc = calcularImpuestosCausacion(prov, grandSub, _sel.lineas);
  const grandIva = calc.iva;
  const grandTotal = calc.total;
  const rfPct   = calc.rfPct;
  const ricaPct = calc.ricaPct;
  const rivaPct = calc.rivaPct;
  const montoRF   = calc.montoRF;
  const montoRICA = calc.montoRICA;
  const montoRIVA = calc.montoRIVA;
  const neto      = calc.neto;
  const haySelec  = fp.provId && fprovHayLineas(fp) && fp.numFact.trim();

  // ── LISTADO GLOBAL de OCPs pendientes ────────────────────────────────────
  const todasPendientes = (state.data.ocp||[])
    .filter(ocpPorCausar)
    .sort((a,b) => (a.fecha||"").localeCompare(b.fecha||""));

  const busq = (fp.busqOcp||"").toLowerCase().trim();
  const filtradas = busq
    ? todasPendientes.filter(o => {
        const p = (state.data.proveedores||[]).find(x=>x.id===o.proveedor_id)||{};
        return o.numero?.toLowerCase().includes(busq) || p.nombre?.toLowerCase().includes(busq);
      })
    : todasPendientes;

  const listadoHTML = filtradas.length === 0
    ? `<div style="text-align:center;padding:11px;color:#94a3b8;font-size:11.5px;">${busq?"Sin resultados":"Sin OCPs pendientes 🎉"}</div>`
    : filtradas.map(ocp => {
        const p   = (state.data.proveedores||[]).find(x=>x.id===ocp.proveedor_id)||{};
        const isOn  = !!(fp.ocps[ocp.id]?.on);
        const bloq  = !!(fp.provId && fp.provId !== ocp.proveedor_id); // proveedor distinto al activo
        const yaFact = getMontoFacturadoOCP(ocp);
        const totalOCP = parseNumber(ocp.total)||0;
        const pendiente = Math.max(0, totalOCP - yaFact);
        const parcial   = yaFact > 0 && !ocpTotalmenteFacturada(ocp);

        return `
        <div onclick="${bloq?"alert('⚠️ Solo OCPs del mismo proveedor. Limpia la selección primero.')":"fprovSeleccionarOcp('"+ocp.id+"')"}"
          style="display:flex;gap:8px;align-items:center;padding:8px 10px;border-bottom:1px solid #f1f5f9;cursor:${bloq?"not-allowed":"pointer"};
            background:${isOn?"#f0fdfa":bloq?"#fafafa":"white"};opacity:${bloq?".45":"1"};transition:background .15s;"
          onmouseover="if(!${bloq})this.style.background='${isOn?"#e6fff9":"#f8fafc"}'"
          onmouseout="this.style.background='${isOn?"#f0fdfa":bloq?"#fafafa":"white"}'">
          <!-- Indicador check -->
          <div style="width:18px;height:18px;border-radius:5px;border:2px solid ${isOn?"#0E7490":"#cbd5e1"};
            background:${isOn?"#0E7490":"white"};display:flex;align-items:center;justify-content:center;
            flex-shrink:0;color:white;font-size:10.5px;font-weight:800;">
            ${isOn?"✓":""}
          </div>
          <!-- Info OCP -->
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;font-size:11.5px;color:${isOn?"#0E7490":"#374151"};display:flex;align-items:center;gap:5px;">
              ${escapeHTML(ocp.numero)}
              ${parcial?`<span style="background:#fef3c7;color:#92400e;font-size:9px;font-weight:700;padding:1px 5px;border-radius:4px;">PARCIAL</span>`:""}
            </div>
            <div style="font-size:10px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHTML(p.nombre||"—")}</div>
            <div style="font-size:10px;color:#94a3b8;">${fmtDate(ocp.fecha)}</div>
          </div>
          <!-- Monto pendiente -->
          <div style="text-align:right;flex-shrink:0;">
            <div style="font-weight:700;font-size:10.5px;color:${isOn?"#0E7490":"#374151"};">${fmtCOP(pendiente)}</div>
            ${parcial?`<div style="font-size:9px;color:#4C5FA8;">causado: ${fmtCOP(yaFact)}</div>`:""}
          </div>
        </div>`;
      }).join("");

  // ── Historial compacto ────────────────────────────────────────────────────
  const hist = [];
  (state.data.ocp||[]).forEach(ocp => {
    (ocp._facturasContables||[]).forEach(fc => {
      const p = (state.data.proveedores||[]).find(x=>x.id===ocp.proveedor_id)||{};
      hist.push({...fc, ocpNum:ocp.numero, provNom:p.nombre||"—"});
    });
  });
  hist.sort((a,b)=>(b.fecha||"").localeCompare(a.fecha||""));

  const histHTML = hist.length===0
    ? `<div style="text-align:center;padding:10px;color:#94a3b8;font-size:10.5px;">Sin causaciones aún</div>`
    : hist.slice(0,10).map(fc=>`
      <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:10.5px;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;color:#12264D;">📄 ${escapeHTML(fc.factNum||"—")}</div>
          <div style="color:#64748b;font-size:10px;">${escapeHTML(fc.provNom)} · ${escapeHTML(fc.ocpNum)}</div>
        </div>
        <div style="text-align:right;flex-shrink:0;">
          <div style="font-weight:700;color:#0E7490;font-size:10.5px;">${fmtCOP(fc.total||0)}</div>
          <div style="color:#94a3b8;font-size:10px;">${fmtDate(fc.fecha)}</div>
        </div>
      </div>`).join("");

  // ── Panel derecho: OCPs del proveedor activo con detalle ─────────────────
  const ocpsDelProv = fp.provId
    ? (state.data.ocp||[]).filter(o=>o.proveedor_id===fp.provId && ocpPorCausar(o))
    : [];

  const detalleOCPs = !fp.provId
    ? `<div style="text-align:center;padding:30px 20px;color:#94a3b8;font-size:12px;">
        <div style="font-size:32px;margin-bottom:8px;">←</div>
        Selecciona una OCP de la lista o elige un proveedor
       </div>`
    : ocpsDelProv.length===0
    ? `<div style="text-align:center;padding:30px 20px;color:#94a3b8;font-size:12px;">✅ Sin OCPs pendientes para este proveedor</div>`
    : ocpsDelProv.map(ocp => {
        const s    = fp.ocps[ocp.id];
        if (!s) return "";
        const isOn = s.on;
        const yaFact = getMontoFacturadoOCP(ocp);
        // El valor de lo marcado, con su IVA. Estaba como `ocpSub * 1.19`: el
        // 19% quemado sobre TODO, que con una mata viva excluida (art. 424) da
        // un número que no existe. Se calcula por línea, como el resto.
        const ocpLineasSel = isOn
          ? fprovItemsMarcados(s).map(it => ({ articulo: it.art,
              total: Math.round((parseNumber(it.cant)||0) * (parseNumber(it.pu)||0)) }))
          : [];
        const ocpSub = ocpLineasSel.reduce((a,l)=>a+l.total,0);
        const ocpConIVA = ocpSub + (prov.responsable_iva ? desgloseIVA(ocpLineasSel).iva : 0);

        return `
        <div style="border:2px solid ${isOn?"#0E7490":"#e2e8f0"};border-radius:6px;margin-bottom:8px;overflow:hidden;transition:border-color .15s;">
          <!-- Cabecera -->
          <div style="display:flex;align-items:center;gap:10px;padding:6px 9px;background:${isOn?"#f0fdfa":"#fafafa"};cursor:pointer;"
            onclick="state.fprov.ocps['${ocp.id}'].on=!state.fprov.ocps['${ocp.id}'].on;render();">
            <div style="width:20px;height:20px;border-radius:5px;border:2px solid ${isOn?"#0E7490":"#cbd5e1"};background:${isOn?"#0E7490":"white"};display:flex;align-items:center;justify-content:center;flex-shrink:0;color:white;font-size:10.5px;font-weight:800;">${isOn?"✓":""}</div>
            <div style="flex:1;min-width:0;">
              <div style="font-weight:800;font-size:11.5px;color:${isOn?"#0E7490":"#374151"};">${escapeHTML(ocp.numero)} <span style="font-weight:400;font-size:10px;color:#94a3b8;">· ${fmtDate(ocp.fecha)}</span></div>
              <div style="font-size:10px;color:#64748b;">
                Total OCP: <strong>${fmtCOP(parseNumber(ocp.total)||0)}</strong>
                ${yaFact>0?` · <span style="color:#4C5FA8;font-weight:700;">Causado: ${fmtCOP(yaFact)}</span> · <span style="color:#dc2626;font-weight:700;">Pendiente: ${fmtCOP(Math.max(0,(parseNumber(ocp.total)||0)-yaFact))}</span>`:""}
              </div>
            </div>
            ${isOn?`<div style="font-weight:800;color:#0E7490;font-size:11.5px;flex-shrink:0;">${fmtCOP(ocpConIVA)}</div>`:""}
          </div>
          <!-- Ítems: solo si marcada -->
          ${isOn?`
          <div style="padding:6px 12px 10px;" onclick="event.stopPropagation();">
            <table style="width:100%;border-collapse:collapse;">
              <thead><tr style="background:#f8fafc;">
                <th style="padding:4px 5px;width:22px;border-bottom:1px solid #a7f3d0;" title="Marca los artículos que vienen en ESTA factura">
                  <input type="checkbox" data-fprov-todos="${ocp.id}" ${(s.items||[]).every(it=>it.on!==false)?"checked":""}
                    onclick="event.stopPropagation();var v=this.checked;state.fprov.ocps['${ocp.id}'].items.forEach(function(x){x.on=v;});render();"
                    style="width:14px;height:14px;cursor:pointer;accent-color:#0E7490;"/>
                </th>
                <th style="padding:4px 5px;text-align:left;font-size:9px;font-weight:700;color:#0E7490;text-transform:uppercase;border-bottom:1px solid #a7f3d0;">Artículo</th>
                <th style="padding:4px 5px;text-align:center;font-size:9px;font-weight:700;color:#0E7490;text-transform:uppercase;border-bottom:1px solid #a7f3d0;">Cant.</th>
                <th style="padding:4px 5px;text-align:right;font-size:9px;font-weight:700;color:#0E7490;text-transform:uppercase;border-bottom:1px solid #a7f3d0;">P. Unit.</th>
                <th style="padding:4px 5px;text-align:right;font-size:9px;font-weight:700;color:#0E7490;text-transform:uppercase;border-bottom:1px solid #a7f3d0;">Total</th>
              </tr></thead>
              <tbody>
                ${(s.items||[]).map((it,i)=>{
                  const tot=Math.round((parseNumber(it.cant)||0)*(parseNumber(it.pu)||0));
                  // Diferencia con el precio de la OCP: es lo que el proveedor
                  // facturó de más o de menos (un descuento, un reajuste). Se
                  // muestra aquí para verla ANTES de causar; al confirmar, si la
                  // mercancía ya ingresó, se corrige el costo del inventario.
                  const _itOcp = (ocp.items||[]).find(x => x.articulo === it.art);
                  const _puOcp = _itOcp ? (parseNumber(_itOcp.precio_unitario)||0) : null;
                  const _dif = _puOcp != null ? (parseNumber(it.pu)||0) - _puOcp : 0;
                  const _on = it.on !== false;
                  return `<tr data-fprov-item="${ocp.id}:${i}" data-fprov-on="${_on?"1":"0"}" style="border-bottom:1px solid #f1f5f9;${_on?"":"opacity:.45;"}">
                    <td style="padding:4px 5px;width:22px;">
                      <input type="checkbox" data-fprov-check="${ocp.id}:${i}" ${_on?"checked":""}
                        onclick="event.stopPropagation();state.fprov.ocps['${ocp.id}'].items[${i}].on=this.checked;render();"
                        title="${_on?"Este artículo viene en la factura: se causa":"No viene en esta factura: queda pendiente en la orden"}"
                        style="width:14px;height:14px;cursor:pointer;accent-color:#0E7490;"/>
                    </td>
                    <td style="padding:4px 5px;font-size:10.5px;color:#374151;max-width:130px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;${_on?"":"text-decoration:line-through;"}" title="${escapeHTML(it.art)}">
                      ${escapeHTML(it.art)}
                      ${Math.abs(_dif) >= 1 ? `<div style="font-size:9px;font-weight:700;color:${_dif<0?"#047857":"#b45309"};">
                        ${_dif<0?"▼":"▲"} ${fmtPrecio(Math.abs(_dif))} vs. OCP (${fmtPrecio(_puOcp)})</div>` : ""}
                    </td>
                    <td style="padding:4px 5px;text-align:center;">
                      <input inputmode="decimal" value="${escapeHTML(String(it.cant))}" ${_on?"":"disabled"}
                        oninput="state.fprov.ocps['${ocp.id}'].items[${i}].cant=this.value;render();"
                        style="width:58px;padding:3px 4px;border:1.5px solid #6ee7b7;border-radius:5px;font-size:10.5px;font-weight:700;text-align:center;outline:none;"
                        onfocus="this.select();"/>
                    </td>
                    <td style="padding:4px 5px;text-align:right;">
                      <input inputmode="decimal" value="${escapeHTML(String(it.pu))}" ${_on?"":"disabled"}
                        oninput="state.fprov.ocps['${ocp.id}'].items[${i}].pu=this.value;render();"
                        style="width:75px;padding:3px 4px;border:1.5px solid #6ee7b7;border-radius:5px;font-size:10.5px;font-weight:700;text-align:right;outline:none;"
                        onfocus="this.select();"/>
                    </td>
                    <td style="padding:4px 5px;text-align:right;font-size:10.5px;font-weight:700;color:${_on&&tot>0?"#0f766e":"#94a3b8"};">${_on ? fmtCOP(tot) : `<span style="font-size:9.5px;font-weight:600;">queda pendiente</span>`}</td>
                  </tr>`;
                }).join("")}
              </tbody>
            </table>
          </div>`:""}
        </div>`;
      }).join("");

  // ── Pie: totales + acción ─────────────────────────────────────────────────
  const faltante = !fp.provId
    ? "← Selecciona una OCP del listado"
    : !fp.numFact.trim()
    ? "Ingresa el Nº de factura"
    : !Object.values(fp.ocps).some(s=>s.on)
    ? "✓ Marca al menos una OCP para continuar"
    : !fprovHayLineas(fp)
    ? "✓ Marca al menos un artículo que venga en la factura"
    : "";
  const pieHTML = (haySelec ? `
    <div style="background:#f8fafc;border:1.5px solid #a7f3d0;border-radius:6px;padding:7px 10px;margin-bottom:10px;">
      <div style="display:grid;grid-template-columns:1fr auto;gap:2px 12px;font-size:11.5px;row-gap:4px;">
        ${impuestosResumenRowsHTML(calc)}
      </div>
    </div>` : "") + `
    <button onclick="${haySelec?"fprovVerConfirmacion()":""}" ${haySelec?"":"disabled"}
      style="width:100%;background:${haySelec?"#0E7490":"#cbd5e1"};color:white;border:none;padding:11px;border-radius:9px;font-weight:800;cursor:${haySelec?"pointer":"not-allowed"};font-size:14px;">
      Revisar y causar factura →
    </button>
    ${faltante?`<div style="text-align:center;padding:8px;color:#94a3b8;font-size:10.5px;margin-top:6px;">${faltante}</div>`:""}`;

  // ── Layout final: 3 columnas ──────────────────────────────────────────────
  return `
  <div style="display:grid;grid-template-columns:280px 1fr;gap:10px;align-items:start;">

    <!-- COLUMNA IZQUIERDA -->
    <div style="display:flex;flex-direction:column;gap:9px;">

      <!-- Listado de OCPs pendientes -->
      <div style="background:white;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
        <div style="padding:6px 10px;border-bottom:1px solid #f1f5f9;display:flex;justify-content:space-between;align-items:center;">
          <div style="font-size:11.5px;font-weight:800;color:#374151;">📦 OCPs por causar
            <span style="font-size:10px;font-weight:400;color:#94a3b8;margin-left:4px;">${todasPendientes.length}</span>
          </div>
          ${fp.provId?`<button onclick="fprovReset();render();" style="background:#fee2e2;border:none;padding:3px 8px;border-radius:5px;font-size:10px;font-weight:700;cursor:pointer;color:#dc2626;">✕ Limpiar</button>`:""}
        </div>
        <!-- Búsqueda -->
        <div style="padding:8px 10px;border-bottom:1px solid #f1f5f9;">
          <input type="text" placeholder="🔍 Buscar OCP o proveedor…" value="${escapeHTML(fp.busqOcp||"")}"
            oninput="state.fprov.busqOcp=this.value;render();"
            style="width:100%;padding:5px 8px;border:1px solid #e2e8f0;border-radius:7px;font-size:10.5px;outline:none;box-sizing:border-box;"/>
        </div>
        <div style="max-height:340px;overflow-y:auto;">
          ${listadoHTML}
        </div>
      </div>

      <!-- Historial causaciones -->
      <div style="background:white;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
        <div style="padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:10.5px;font-weight:800;color:#475569;">🗂️ Últimas causaciones</div>
        <div style="max-height:200px;overflow-y:auto;">
          ${histHTML}
        </div>
      </div>
    </div>

    <!-- COLUMNA DERECHA: formulario + OCPs del proveedor + totales -->
    <div>

      <!-- Las facturas que llegaron al correo de contabilidad -->
      ${fp.xml ? "" : bandejaHTML()}

      <!-- La factura electrónica del proveedor: suelta el XML o el ZIP del correo -->
      ${fp.xml ? panelXmlFprov(fp, calc) : `
      <label data-fprov-zona-xml
        ondragover="event.preventDefault();this.style.background='#e0f2fe';"
        ondragleave="this.style.background='white';"
        ondrop="event.preventDefault();this.style.background='white';fprovCargarArchivo(event.dataTransfer.files[0]);"
        style="display:flex;align-items:center;gap:10px;background:white;border:2px dashed #7dd3fc;border-radius:8px;padding:10px 14px;margin-bottom:12px;cursor:pointer;">
        <span style="font-size:22px;">📄</span>
        <span style="flex:1;font-size:11.5px;color:#0369a1;">
          <b>¿Tienes la factura electrónica?</b> Suelta aquí el XML o el ZIP tal como llegó al correo, o haz clic para elegirlo.
          <span style="display:block;color:#64748b;font-size:10.5px;">AURA elige el proveedor, pone el número y la fecha, marca lo que la factura trae y te avisa si algo no cuadra.</span>
        </span>
        <input id="fprov-xml" type="file" accept=".xml,.zip,text/xml,application/xml,application/zip" style="display:none;"
          onchange="fprovCargarArchivo(this.files[0]);this.value='';"/>
      </label>`}

      <!-- Datos de la factura + ficha proveedor -->
      <div style="background:white;border:1px solid #e2e8f0;border-radius:6px;padding:14px 16px;margin-bottom:12px;">
        <div style="display:grid;grid-template-columns:${fp.provId?"1fr 1fr 1fr":"1fr 1fr"};gap:9px;align-items:end;">

          <!-- Proveedor -->
          <div>
            <label style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.4px;display:block;margin-bottom:4px;">Proveedor</label>
            <select onchange="
              const newProv=this.value;
              if(newProv!==state.fprov.provId){
                if(Object.values(state.fprov.ocps).some(s=>s.on)){
                  if(!confirm('Cambiar proveedor limpiará la selección actual. ¿Continuar?'))return;
                }
                state.fprov.provId=newProv;state.fprov.ocps={};
              }render();"
              style="${inputStyle}font-size:11.5px;cursor:pointer;">
              <option value="">— Seleccionar —</option>
              ${provs.map(p=>`<option value="${p.id}" ${fp.provId===p.id?"selected":""}>${escapeHTML(p.nombre)}</option>`).join("")}
            </select>
          </div>

          <!-- Nº Factura -->
          <div>
            <label style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.4px;display:block;margin-bottom:4px;">Nº Factura proveedor *</label>
            <input type="text" placeholder="Ej: FV-12345" value="${escapeHTML(fp.numFact)}"
              oninput="state.fprov.numFact=this.value;fprovUpdatePie();"
              style="${inputStyle}font-size:11.5px;"/>
          </div>

          <!-- Fecha -->
          <div>
            <label style="font-size:10px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.4px;display:block;margin-bottom:4px;">Fecha factura</label>
            <input type="date" value="${fp.fechaFact}"
              onchange="state.fprov.fechaFact=this.value;"
              style="${inputStyle}font-size:11.5px;"/>
          </div>
        </div>

        <!-- Ficha tributaria (solo si hay proveedor) -->
        ${fp.provId?`
        <div style="display:flex;gap:9px;margin-top:10px;padding:6px 10px;background:#f8fafc;border:1px solid #a7f3d0;border-radius:6px;font-size:10.5px;flex-wrap:wrap;align-items:center;">
          <div><span style="color:#065f46;">NIT:</span> <strong>${escapeHTML(prov.nit||"—")}</strong></div>
          <div><span style="color:#065f46;">Régimen:</span> <strong>${escapeHTML((REGIMENES_FISCALES.find(r=>r.value===(prov.regimen_fiscal||"ordinario"))||{}).label || "Régimen Ordinario")}</strong></div>
          <div><span style="color:#065f46;">IVA:</span> <strong style="color:${calc.esResponsableIVA?"#065f46":"#94a3b8"};">${calc.esResponsableIVA?"✅ Responsable":"⛔ No responsable"}</strong></div>
          ${rfPct>0?(montoRF>0?`<div><span style="color:#dc2626;">ReteFuente:</span> <strong style="color:#dc2626;">${rfPct}%</strong></div>`:`<div title="${escapeHTML(calc.motivoRFNoAplica)}"><span style="color:#94a3b8;">ReteFuente:</span> <strong style="color:#94a3b8;text-decoration:line-through;">${rfPct}%</strong> <span style="color:#b45309;">⚠️ exonerado</span></div>`):""}
          ${ricaPct>0?(montoRICA>0?`<div><span style="color:#dc2626;">ReteICA:</span> <strong style="color:#dc2626;">${ricaPct}%</strong></div>`:`<div title="${escapeHTML(calc.motivoRICANoAplica)}"><span style="color:#94a3b8;">ReteICA:</span> <strong style="color:#94a3b8;text-decoration:line-through;">${ricaPct}%</strong> <span style="color:#b45309;">⚠️ exonerado</span></div>`):""}
          ${rivaPct>0?`<div><span style="color:#dc2626;">ReteIVA:</span> <strong style="color:#dc2626;">${rivaPct}%</strong></div>`:""}
          <div style="margin-left:auto;"><span style="color:#065f46;">Días pago:</span> <strong>${prov.dias_pago||30}d</strong></div>
        </div>
        ${avisoRetencionFicha(prov, grandSub)}`:""}
      </div>

      <!-- OCPs del proveedor activo con detalle editable -->
      ${fp.provId?`
      <div style="background:white;border:1px solid #e2e8f0;border-radius:6px;padding:14px 16px;margin-bottom:12px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
          <div style="font-size:11.5px;font-weight:800;color:#374151;">
            OCPs de <span style="color:#0E7490;">${escapeHTML(prov.nombre||"")}</span>
            <span style="font-weight:400;color:#94a3b8;font-size:10px;margin-left:4px;">${ocpsDelProv.length} disponible${ocpsDelProv.length!==1?"s":""}</span>
          </div>
          ${ocpsDelProv.length>1?`
          <button onclick="
            const allOn=Object.values(state.fprov.ocps).every(s=>s.on);
            Object.values(state.fprov.ocps).forEach(s=>s.on=!allOn);render();"
            style="background:#f1f5f9;border:none;padding:4px 10px;border-radius:6px;font-size:10px;font-weight:700;cursor:pointer;color:#475569;">
            ${Object.values(fp.ocps).every(s=>s.on)?"Deseleccionar todo":"Seleccionar todo"}
          </button>`:""}
        </div>
        ${detalleOCPs}
      </div>`:""}

      <!-- Totales y botón confirmar -->
      <div id="fprov-pie">${pieHTML}</div>
    </div>
  </div>`;
}

export function fprovUpdatePie() {
  const pieEl = document.getElementById("fprov-pie");
  if (!pieEl) return;
  const fp = state.fprov;
  if (!fp) return;
  const prov = (state.data.proveedores||[]).find(p=>p.id===fp.provId) || {};
  const _sel = fprovSeleccion(fp);
  const grandSub = _sel.sub;
  const calc = calcularImpuestosCausacion(prov, grandSub, _sel.lineas);
  const neto = calc.neto;
  const haySelec  = fp.provId && fprovHayLineas(fp) && fp.numFact.trim();
  const faltante = !fp.provId
    ? "← Selecciona una OCP del listado"
    : !fp.numFact.trim()
    ? "Ingresa el Nº de factura"
    : !Object.values(fp.ocps).some(s=>s.on)
    ? "✓ Marca al menos una OCP para continuar"
    : !fprovHayLineas(fp)
    ? "✓ Marca al menos un artículo que venga en la factura"
    : "";
  pieEl.innerHTML = (haySelec ? `
    <div style="background:#f8fafc;border:1.5px solid #a7f3d0;border-radius:6px;padding:7px 10px;margin-bottom:10px;">
      <div style="display:grid;grid-template-columns:1fr auto;gap:2px 12px;font-size:11.5px;row-gap:4px;">
        ${impuestosResumenRowsHTML(calc)}
      </div>
    </div>` : "") + `
    <button onclick="${haySelec?"fprovVerConfirmacion()":""}" ${haySelec?"":"disabled"}
      style="width:100%;background:${haySelec?"#0E7490":"#cbd5e1"};color:white;border:none;padding:11px;border-radius:9px;font-weight:800;cursor:${haySelec?"pointer":"not-allowed"};font-size:14px;">
      Revisar y causar factura →
    </button>
    ${faltante?`<div style="text-align:center;padding:8px;color:#94a3b8;font-size:10.5px;margin-top:6px;">${faltante}</div>`:""}`;
}

export function fprovVerConfirmacion() {
  const fp   = state.fprov;
  const prov = (state.data.proveedores||[]).find(p=>p.id===fp.provId)||{};

  // Calculamos primero el subtotal agregado de todas las OCPs seleccionadas
  // para decidir UNA sola vez (a nivel de factura) si aplican IVA/retenciones,
  // y luego usamos esa misma decisión (vía calc.ivaPct/esResponsableIVA) en
  // cada línea, para que el detalle y el total siempre coincidan.
  const _selPre = fprovSeleccion(fp);
  const grandSubPre = _selPre.sub;
  const calcPreview = calcularImpuestosCausacion(prov, grandSubPre, _selPre.lineas);

  let grandSub=0, grandIva=0;
  const ocpLines = [];
  Object.entries(fp.ocps).forEach(([ocpId,s]) => {
    if (!s.on) return;
    const ocp = (state.data.ocp||[]).find(o=>o.id===ocpId);
    if (!ocp) return;
    const items = fprovItemsMarcados(s);
    if (!items.length) return; // marcada, pero sin ningún artículo en esta factura
    const lineas = items.map(it => ({ articulo: it.art,
      total: Math.round((parseNumber(it.cant)||0) * (parseNumber(it.pu)||0)) }));
    const sub = lineas.reduce((a,l)=>a+l.total,0);
    // Por línea también en el detalle por orden: la matera lleva IVA y la mata
    // viva no, así que el IVA de la OCP no es el 19% de su subtotal.
    const iv  = calcPreview.esResponsableIVA ? desgloseIVA(lineas).iva : 0;
    grandSub+=sub; grandIva+=iv;
    ocpLines.push({ ocp, items, sub, iv, tot:sub+iv });
  });
  const _selDet = fprovSeleccion(fp);
  const calc = calcularImpuestosCausacion(prov, grandSub, _selDet.lineas);
  const grandTotal = calc.total;
  const rfPct = calc.rfPct, ricaPct = calc.ricaPct, rivaPct = calc.rivaPct;
  const montoRF = calc.montoRF, montoRICA = calc.montoRICA, montoRIVA = calc.montoRIVA;
  const neto = calc.neto;

  // Asiento preview
  const movs=[];
  if (EMPRESA.regimen_iva==="responsable") {
    movs.push({cta:"1435",nom:"Inventario / costo compra",d:grandSub,c:0});
    if (grandIva>0) movs.push({cta:"1355",nom:"IVA descontable",d:grandIva,c:0});
  } else {
    movs.push({cta:"1435",nom:"Inventario (inc. IVA no deducible)",d:grandSub+grandIva,c:0});
  }
  movs.push({cta:"2205",nom:"Proveedores CxP — neto a pagar",d:0,c:neto});
  if (montoRF>0)   movs.push({cta:"2365",nom:`ReteFuente ${rfPct}%`,d:0,c:montoRF});
  if (montoRIVA>0) movs.push({cta:"2367",nom:`ReteIVA ${rivaPct}%`,d:0,c:montoRIVA});
  if (montoRICA>0) movs.push({cta:"2368",nom:`ReteICA ${ricaPct}%`,d:0,c:montoRICA});
  const totD=movs.reduce((s,m)=>s+m.d,0);
  const totC=movs.reduce((s,m)=>s+m.c,0);
  const ok=Math.abs(totD-totC)<1;

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1100;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:600px;max-width:100%;max-height:92vh;overflow-y:auto;box-shadow:0 24px 60px rgba(0,0,0,.25);">
        <div style="padding:14px 20px;border-bottom:1px solid #e2e8f0;background:#f8fafc;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:12px;font-weight:800;color:#065f46;">✅ Confirmar causación</div>
            <div style="font-size:10.5px;color:#6b7280;margin-top:2px;">${escapeHTML(prov.nombre||"")} · Fact. <strong>${escapeHTML(fp.numFact)}</strong> · ${fp.fechaFact}</div>
          </div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:17px;color:#6b7280;cursor:pointer;line-height:1;">✕</button>
        </div>
        <div style="padding:10px 12px;">
          ${ocpLines.map(l=>`
          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:6px 9px;margin-bottom:8px;">
            <div style="font-weight:700;color:#4C5FA8;font-size:10.5px;margin-bottom:5px;">${escapeHTML(l.ocp.numero)}</div>
            ${l.items.map(it=>`
            <div style="display:flex;gap:6px;font-size:10.5px;color:#374151;margin-bottom:2px;">
              <span style="flex:1;">${escapeHTML(it.art)}</span>
              <span style="color:#64748b;">×${it.cant}</span>
              <span style="color:#64748b;">${fmtPrecio(it.pu)}</span>
              <span style="font-weight:700;min-width:65px;text-align:right;">${fmtCOP(Math.round((parseNumber(it.cant)||0)*(parseNumber(it.pu)||0)))}</span>
            </div>`).join("")}
            <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:5px;padding-top:4px;border-top:1px solid #e2e8f0;font-size:10px;color:#64748b;">
              Sub: ${fmtCOP(l.sub)} · IVA: ${fmtCOP(l.iv)} · <strong style="color:#374151;">${fmtCOP(l.tot)}</strong>
            </div>
          </div>`).join("")}
          <div style="background:#f8fafc;border:1px solid #a7f3d0;border-radius:6px;padding:7px 10px;margin-bottom:12px;font-size:11.5px;">
            <div style="display:grid;grid-template-columns:1fr auto;gap:2px 10px;row-gap:3px;">
              ${impuestosResumenRowsHTML(calc)}
            </div>
          </div>
          <details style="margin-bottom:12px;">
            <summary style="cursor:pointer;font-size:10.5px;font-weight:700;color:#475569;padding:5px 0;">🧾 Asiento contable ${ok?'<span style="color:#16a34a;">✅ Balanceado</span>':'<span style="color:#dc2626;">⚠️</span>'}</summary>
            <div style="border:1px solid #e2e8f0;border-radius:7px;overflow:hidden;margin-top:7px;font-size:10.5px;">
              <table style="width:100%;border-collapse:collapse;">
                <thead><tr style="background:#f8fafc;">
                  <th style="padding:5px 8px;text-align:left;font-weight:700;color:#64748b;">Cuenta</th>
                  <th style="padding:5px 8px;text-align:right;font-weight:700;color:#12264D;">Débito</th>
                  <th style="padding:5px 8px;text-align:right;font-weight:700;color:#059669;">Crédito</th>
                </tr></thead>
                <tbody>
                  ${movs.map(m=>`<tr style="border-top:1px solid #f1f5f9;">
                    <td style="padding:5px 8px;">${m.cta} — ${m.nom}</td>
                    <td style="padding:5px 8px;text-align:right;color:${m.d>0?"#12264D":"#94a3b8"};">${m.d>0?fmtCOP(m.d):"—"}</td>
                    <td style="padding:5px 8px;text-align:right;color:${m.c>0?"#059669":"#94a3b8"};">${m.c>0?fmtCOP(m.c):"—"}</td>
                  </tr>`).join("")}
                  <tr style="background:#f8fafc;font-weight:800;border-top:2px solid #e2e8f0;">
                    <td style="padding:5px 8px;">TOTAL</td>
                    <td style="padding:5px 8px;text-align:right;color:#12264D;">${fmtCOP(totD)}</td>
                    <td style="padding:5px 8px;text-align:right;color:#059669;">${fmtCOP(totC)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
          <div style="display:flex;gap:8px;">
            <button data-action="close-modal" style="background:#f1f5f9;border:none;padding:9px 18px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;color:#475569;">← Corregir</button>
            <button onclick="fprovConfirmar()" style="flex:1;background:#0E7490;color:white;border:none;padding:10px;border-radius:6px;font-weight:800;cursor:pointer;font-size:14px;">
              ✅ Confirmar — Causar ${fmtCOP(grandTotal)}
            </button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

// Aviso cuando la factura es de un mes ya cerrado: el asiento no puede ir a ese
// mes, así que quedó en el periodo abierto con la fecha del día. La factura
// conserva su número y su fecha; el Libro Diario muestra ambas.
function avisoReclasificacion(lista) {
  if (!lista || lista.length === 0) return "";
  const detalle = lista.map(r =>
    `• Factura ${r.factura} (${r.ocp}) — del ${fmtDate(r.doc)}, contabilizada el ${fmtDate(r.asiento)}`).join("\n");
  return "\n\n📅 La factura es de un mes que ya está cerrado, así que el asiento quedó registrado " +
    "en el periodo abierto:\n\n" + detalle +
    "\n\nLa factura conserva su número y su fecha; en el Libro Diario aparece la fecha del documento.";
}

export async function fprovConfirmar() {
  const fp   = state.fprov;
  const prov = (state.data.proveedores||[]).find(p=>p.id===fp.provId)||{};

  // Decidimos UNA sola vez (sobre el subtotal total de la factura) si aplican
  // IVA/retenciones, igual que en la pantalla de revisión — así el asiento
  // generado siempre coincide con lo que el usuario confirmó en pantalla.
  const _selPre = fprovSeleccion(fp);
  const grandSubPre = _selPre.sub;
  const calc = calcularImpuestosCausacion(prov, grandSubPre, _selPre.lineas);
  // Con el XML cargado: la misma factura no se causa dos veces, y lo que no
  // cuadra con ella se confirma a propósito, no por descuido.
  if (fp.xml) {
    const ya = causacionDeFactura(fp.xml, fp.provId);
    if (ya) { alert(`⚠️ La factura ${fp.xml.numero} ya se causó en la ${ya.ocp.numero} el ${fmtDate(ya.fc.fecha)}. No se puede causar dos veces.`); return; }
    const dif = diferenciasConXML(fp.xml, { subtotal: calc.sub, iva: calc.iva, total: calc.total }).filter(d => !d.cuadra);
    if (dif.length && !confirm("⚠️ Lo que vas a causar no cuadra con la factura electrónica:\n\n" +
        dif.map(d => `${d.concepto}: factura ${fmtCOP(d.factura)} · se causaría ${fmtCOP(d.aura)}`).join("\n") +
        "\n\n¿Causar de todas formas?")) return;
  }
  let __fprovError = false;
  const __reclasificados = [];
  const __ajustesCosto = [];
  const ivaAplica  = calc.esResponsableIVA;
  const rfAplica   = calc.montoRF   > 0;
  const ricaAplica = calc.montoRICA > 0;
  const rivaAplica = calc.montoRIVA > 0;
  const rfPct = calc.rfPct, ricaPct = calc.ricaPct, rivaPct = calc.rivaPct, ivaPct = calc.ivaPct;

  let ocpList = [...(state.data.ocp||[])];

  for (const [ocpId,s] of Object.entries(fp.ocps)) {
    if (!s.on) continue;
    const ocpIdx = ocpList.findIndex(o=>o.id===ocpId);
    if (ocpIdx<0) continue;
    const ocp = ocpList[ocpIdx];

    const items = fprovItemsMarcados(s)
      .map(it=>({ articulo:it.art, descripcion:it.desc||"", cantidad:parseNumber(it.cant)||0,
        precio_unitario:parseNumber(it.pu)||0,
        total:Math.round((parseNumber(it.cant)||0)*(parseNumber(it.pu)||0)) }));
    // Marcada, pero ninguno de sus artículos viene en esta factura: no se causa
    // nada de ella (ni un asiento en cero).
    if (!items.length) continue;

    const sub  = items.reduce((a,it)=>a+it.total, 0);
    // El IVA de ESTA orden, por línea. Con el 19% sobre su subtotal —como
    // estaba— la matera y la mata viva se gravaban igual, y el asiento llevaba
    // a la 1355 un IVA descontable que el proveedor nunca cobró.
    const iv   = ivaAplica ? desgloseIVA(items).iva : 0;
    const tot  = sub+iv;
    const montoRF   = rfAplica   ? Math.round(sub*rfPct/100)   : 0;
    const montoRICA = ricaAplica ? Math.round(sub*ricaPct/100) : 0;
    const montoRIVA = rivaAplica ? Math.round(iv*rivaPct/100)  : 0;
    const neto = tot - montoRF - montoRICA - montoRIVA;

    // Construir movimientos del asiento
    const movimientos = [];
    if (EMPRESA.regimen_iva==="responsable") {
      movimientos.push({cuenta:"1435",debito:sub,credito:0,tercero_id:fp.provId});
      if (iv>0) movimientos.push({cuenta:"1355",debito:iv,credito:0,tercero_id:fp.provId});
    } else {
      movimientos.push({cuenta:"1435",debito:sub+iv,credito:0,tercero_id:fp.provId});
    }
    movimientos.push({cuenta:"2205",debito:0,credito:neto,tercero_id:fp.provId});
    if (montoRF>0)   movimientos.push({cuenta:"2365",debito:0,credito:montoRF,tercero_id:fp.provId});
    if (montoRIVA>0) movimientos.push({cuenta:"2367",debito:0,credito:montoRIVA,tercero_id:fp.provId});
    if (montoRICA>0) movimientos.push({cuenta:"2368",debito:0,credito:montoRICA,tercero_id:fp.provId});

    let asientoId=null;
    try {
      const ast = await crearAsiento(
        `Factura ${fp.numFact} — ${prov.nombre||""} — OCP ${ocp.numero}`,
        {tipo:"OCP",id:ocpId,numero:ocp.numero}, movimientos, fp.fechaFact);
      asientoId = ast.id;
      // La factura es de un mes ya cerrado: el asiento quedó en el periodo
      // abierto. Se avisa, para que nadie descubra la diferencia de fechas
      // después, cuadrando el mes.
      if (ast.fecha_documento) __reclasificados.push({ ocp: ocp.numero, factura: fp.numFact, doc: ast.fecha_documento, asiento: ast.fecha });
    } catch(e){
      // Sin asiento no hay causación: no marcar la OCP como causada ni continuar en silencio
      console.error("asiento error:", e);
      alert("⚠️ No se pudo causar la factura para la OCP " + ocp.numero + ": " + e.message);
      __fprovError = true;
      break;
    }

    const facturasContables = [...(ocp._facturasContables||[]),
      {id:genId(), factNum:fp.numFact, fecha:fp.fechaFact, items, subtotal:sub, iva:iv, total:tot, _asientoId:asientoId,
       // Con el XML: el CUFE es la identidad de la factura ante la DIAN, y con
       // él no se causa dos veces (causacionDeFactura en fprovXML.js).
       ...(fp.xml ? { cufe: fp.xml.cufe || null, vence: fp.xml.vence || null,
         pdf: (fp.recibida && fp.recibida.pdf_url) || null,
         xml: { numero: fp.xml.numero, nit: fp.xml.proveedor && fp.xml.proveedor.nit, total: fp.xml.totales.total } } : {})}];

    const totCausado = (ocp.items||[]).every(it => {
      const cantFact = facturasContables.reduce((a,f)=>a+((f.items||[]).find(i=>i.articulo===it.articulo)?.cantidad||0),0);
      return cantFact >= (parseNumber(it.cantidad)||0) - 0.001;
    });

    ocpList[ocpIdx] = {...ocp, _facturasContables:facturasContables,
      _procesado:{...(ocp._procesado||{}), factura:totCausado,
        factura_fecha:fp.fechaFact, factura_numero:fp.numFact, factura_fecha_doc:fp.fechaFact}};

    // ── La mercancía ya ingresó a un costo distinto al facturado ────────────
    // El ingreso mueve el inventario al precio de la OCP, pero el proveedor
    // puede facturar por otro valor (un descuento, un reajuste). En ese caso el
    // costo promedio del artículo quedó mal y hay que corregirlo por la
    // diferencia: son las mismas unidades, costaron otra cosa.
    //
    // La contabilidad NO se toca: el asiento de arriba ya se hizo por el valor
    // real de la factura. Esto solo alinea el inventario con esa realidad.
    const yaIngresada = (ocp._recepciones||[]).length > 0 || !!(ocp._procesado && ocp._procesado.ingreso);
    if (yaIngresada) {
      items.forEach(itFact => {
        const itOcp = (ocp.items||[]).find(x => x.articulo === itFact.articulo);
        if (!itOcp) return;
        const puOcp = parseNumber(itOcp.precio_unitario) || 0;
        const delta = (itFact.precio_unitario - puOcp) * itFact.cantidad;
        if (Math.abs(delta) < 1) return;             // sin diferencia real
        const r = ajustarCostoInventario(itFact.articulo, delta,
          {tipo:"OCP", id:ocpId, numero:ocp.numero, factura:fp.numFact}, fp.fechaFact);
        if (r) __ajustesCosto.push({articulo:itFact.articulo, delta, antes:r.costoAnterior, ahora:r.costoPromedio});
      });
    }
  }

  saveData("ocp", ocpList);
  if (__fprovError) { render(); return; }
  // La factura que vino del correo sale de la bandeja.
  if (fp.xml && fp.xml.cufe) {
    marcarCausadaEnBandeja(fp.xml.cufe, { factura: fp.numFact,
      ocps: Object.entries(fp.ocps).filter(([, s]) => s.on && fprovItemsMarcados(s).length)
        .map(([id]) => (ocpList.find(o => o.id === id) || {}).numero).filter(Boolean) });
  }
  fprovReset();
  render();
  if (__ajustesCosto.length) {
    const detalle = __ajustesCosto.map(a =>
      `• ${a.articulo}: ${a.delta < 0 ? "−" : "+"}${fmtCOP(Math.abs(a.delta))} — costo promedio ${fmtCOP(a.antes)} → ${fmtCOP(a.ahora)}`).join("\n");
    alert("✅ Factura causada correctamente.\n\n" +
      "La mercancía ya estaba ingresada a otro costo, así que se corrigió el costo promedio del inventario con la diferencia facturada:\n\n" +
      detalle + "\n\n(La contabilidad quedó por el valor real de la factura; esto solo alinea el inventario.)" +
      avisoReclasificacion(__reclasificados));
  } else {
    alert("✅ Factura causada correctamente." + avisoReclasificacion(__reclasificados));
  }
}

export function getCantidadIngresadaOCP(ocp, articulo) {
  return ((ocp && ocp._recepciones) || [])
    .reduce((s, r) => s + ((r.items || []).find(i => i.articulo === articulo)?.cantidad || 0), 0);
}

// ─── La orden de compra INFORMATIVA ─────────────────────────────────────────
// Nace de un costo que se pidió SIN IVA, o del papel que acompaña a una cuenta
// de cobro. Es el compromiso por escrito con el proveedor —qué tiene que
// cumplir— y nada más: lleva su propia serie (OCI-xxxx) para no gastar el
// consecutivo de las órdenes oficiales, NO entra a la bodega (no se ingresa),
// NO se causa (sin IVA no hay descontable) y NO se paga por aquí (lo que se
// paga es la cuenta de cobro). Solo se CIERRA cuando el proveedor cumple.
export function esOcpInformativa(o) {
  return !!(o && o._informativa);
}

export function ocpInformativaCerrada(o) {
  return !!(o && o._informativa && o._cerrada);
}

export function ocpTotalmenteIngresada(ocp) {
  return (ocp.items || []).every(it => {
    const sol = parseNumber(it.cantidad) || 0;
    const rec = getCantidadIngresadaOCP(ocp, it.articulo);
    return rec >= sol;
  });
}

// ─── Qué se recibió en esta OCP y dónde está esa mercancía ahora ────────────
// Se lee del KARDEX, que es donde quedan los movimientos, y no de una suma de
// documentos: si la mercancía entró y volvió a salir, el stock en cero es lo
// correcto y hay que poder verlo. Solo se miran las salidas posteriores al
// ingreso — las anteriores se llevaron otras unidades, no estas.
export function resumenIngresoOCP(ocp) {
  const recepciones = (ocp && ocp._recepciones) || [];
  const lineas = [];
  recepciones.forEach(r => (r.items || []).forEach(it => {
    lineas.push(`• ${fmtDate(r.fecha)}: ${it.articulo} × ${formatMiles(parseNumber(it.cantidad) || 0)}`);
  }));
  if (!lineas.length) return "Esta OCP no tiene recepciones registradas.";

  const desde = recepciones.map(r => r.fecha).filter(Boolean).sort()[0] || "";
  const arts = [...new Set(recepciones.flatMap(r => (r.items || []).map(i => i.articulo)).filter(Boolean))];
  const inv = state.data.inventario || [];
  const destino = arts.map(art => {
    const fila = inv[indiceInventario(inv, art)];
    const stock = parseNumber(fila && fila.stock) || 0;
    const salidas = ((fila && fila._movimientos) || [])
      .filter(m => (parseNumber(m.cantidad) || 0) < 0 && m.origen && m.origen.numero)
      .filter(m => !desde || !m.fecha || m.fecha >= desde)
      .map(m => `${m.origen.numero} (${formatMiles(Math.abs(parseNumber(m.cantidad) || 0))})`);
    return `• ${art}: quedan ${formatMiles(stock)} en bodega` +
      (salidas.length ? `, después de salir con ${salidas.join(", ")}` : "");
  });
  return "Lo que ya se recibió:\n" + lineas.join("\n") +
         "\n\nDónde está esa mercancía hoy:\n" + destino.join("\n");
}

export function cerrarIngresoModal() {
  state.ingresoModal = null;
  state.modal = null;
  render();
}

export function ingresoAbrirLinea(articulo) {
  if (!state.ingresoModal) return;
  const _ocpId = state.ingresoModal.ocpId;
  const _ocpObj = state.data.ocp.find(o => o.id === _ocpId);
  if (!_ocpObj) return;
  const _item = (_ocpObj.items||[]).find(it => it.articulo === articulo);
  if (!_item) return;
  const _sol  = parseNumber(_item.cantidad) || 0;
  const _rec  = getCantidadIngresadaOCP(_ocpObj, articulo);
  const _disp = Math.max(0, _sol - _rec);
  if (_disp <= 0) { alert("Ya se ingresó toda la cantidad de este ítem."); return; }
  const _cantStr = (state.ingresoModal.cantidades||{})[articulo];
  const _cant = _cantStr !== undefined && _cantStr !== "" ? Math.min(parseNumber(_cantStr)||0, _disp) : _disp;
  if (_cant <= 0) { alert("La cantidad a ingresar debe ser mayor a 0."); return; }
  const _precio = parseNumber((state.ingresoModal.precios||{})[articulo] !== undefined
    ? (state.ingresoModal.precios||{})[articulo]
    : (_item.precio_unitario ?? _item.costo)) || 0;
  state.ingresoModal.pendingConfirm = {
    modo: "linea",
    items: [{ articulo, cantidad: _cant, precio: _precio }]
  };
  renderIngresoConfirmModal();
}

export function ingresoAbrirTodos() {
  if (!state.ingresoModal) return;
  const _ocpId = state.ingresoModal.ocpId;
  const _ocpObj = state.data.ocp.find(o => o.id === _ocpId);
  if (!_ocpObj) return;
  const _sel   = state.ingresoModal.selected || {};
  const _cants = state.ingresoModal.cantidades || {};
  const _prec  = state.ingresoModal.precios || {};
  const _itemsSel = Object.keys(_sel).filter(a => _sel[a]);
  if (_itemsSel.length === 0) return;
  const _recItems = [];
  _itemsSel.forEach(art => {
    const _it   = (_ocpObj.items||[]).find(it => it.articulo === art);
    if (!_it) return;
    const _sol  = parseNumber(_it.cantidad) || 0;
    const _rec  = getCantidadIngresadaOCP(_ocpObj, art);
    const _disp = Math.max(0, _sol - _rec);
    if (_disp <= 0) return;
    const _cStr = _cants[art];
    const _cant = _cStr !== undefined && _cStr !== "" ? Math.min(parseNumber(_cStr)||0, _disp) : _disp;
    if (_cant <= 0) return;
    const _precio = parseNumber(_prec[art] !== undefined ? _prec[art] : (_it.precio_unitario ?? _it.costo)) || 0;
    _recItems.push({ articulo: art, cantidad: _cant, precio: _precio });
  });
  if (_recItems.length === 0) { alert("No hay cantidades válidas para ingresar."); return; }
  state.ingresoModal.pendingConfirm = { modo: "todos", items: _recItems };
  renderIngresoConfirmModal();
}

// ─── La puerta del ingreso de mercancía de una OCP ──────────────────────────
// Kardex (entrada al costo de la línea), historial de recepciones de la OCP y
// su estado. La usan el ingreso de siempre y la recepción (RP) del flujo
// piloto, para que los dos caminos dejen exactamente lo mismo. `extra` se
// anota en la recepción (la RP del piloto deja su número).
export function aplicarIngresoOCP(ocpId, items, extra = {}) {
  const _ocp = state.data.ocp.find(o => o.id === ocpId);
  if (!_ocp) return null;
  items.forEach(it => {
    // Usar el precio_unitario del ítem de la OCP como costo de entrada (lo que pagamos al proveedor)
    const costoEntrada = parseNumber(it.precio) || parseNumber(it.costo) || parseNumber(it.precio_unitario) || getCostoPromedio(it.articulo);
    ingresarInventario(
      it.articulo,
      parseNumber(it.cantidad),
      costoEntrada,
      { tipo:"OCP", id:ocpId, numero:_ocp.numero },
      today()
    );
  });
  const _recepcion = { fecha: today(), items, ...extra };
  const _updOcp = { ..._ocp, _recepciones: [...(_ocp._recepciones||[]), _recepcion] };
  if (ocpTotalmenteIngresada(_updOcp)) {
    _updOcp._procesado = { ...(_updOcp._procesado||{}), ingreso: true, ingreso_fecha: today() };
    _updOcp.estado = "recibida";
    _updOcp._locked = true;
  } else {
    _updOcp._procesado = { ...(_updOcp._procesado||{}), ingreso: false };
  }
  saveData("ocp", state.data.ocp.map(o => o.id === ocpId ? _updOcp : o));
  return _updOcp;
}

export function ingresoConfirmar() {
  if (!state.ingresoModal || !state.ingresoModal.pendingConfirm) return;
  const _pc    = state.ingresoModal.pendingConfirm;
  const _ocpId = state.ingresoModal.ocpId;
  const _updOcp = aplicarIngresoOCP(_ocpId, _pc.items);
  if (!_updOcp) return;
  if (_pc.modo === "linea" && _pc.items[0]) {
    const _artC = _pc.items[0].articulo;
    delete (state.ingresoModal.selected||{})[_artC];
    delete (state.ingresoModal.cantidades||{})[_artC];
  } else {
    state.ingresoModal.selected   = {};
    state.ingresoModal.cantidades = {};
    state.ingresoModal.precios    = {};
  }
  delete state.ingresoModal.pendingConfirm;
  if (_updOcp.estado === "recibida") {
    state.ingresoModal = null;
    state.modal = null;
    render();
  } else {
    renderIngresoMercanciaModal();
  }
}

export function ingresoCancelarConfirm() {
  if (!state.ingresoModal) return;
  delete state.ingresoModal.pendingConfirm;
  renderIngresoMercanciaModal();
}

export function handleIngresoToggle(el, art) {
  if (!state.ingresoModal) return;
  if (!state.ingresoModal.selected) state.ingresoModal.selected = {};
  state.ingresoModal.selected[art] = el.checked;
  renderIngresoMercanciaModal();
}

export function handleIngresoInput(el, art, field) {
  if (!state.ingresoModal) return;
  if (field === "cantidad") {
    if (!state.ingresoModal.cantidades) state.ingresoModal.cantidades = {};
    const maxVal = parseFloat(el.max) || Infinity;
    let val = parseFloat(el.value) || 0;
    if (val > maxVal) { val = maxVal; el.value = maxVal; }
    state.ingresoModal.cantidades[art] = val > 0 ? String(val) : "";
  } else if (field === "precio") {
    if (!state.ingresoModal.precios) state.ingresoModal.precios = {};
    state.ingresoModal.precios[art] = el.value;
  }
}

export function renderIngresoConfirmModal() {
  const im = state.ingresoModal;
  if (!im || !im.pendingConfirm) return;
  const ocp = state.data.ocp.find(o => o.id === im.ocpId);
  if (!ocp) return;
  const pc = im.pendingConfirm;

  // Calcular total
  let totalUnidades = 0;
  let totalCosto = 0;
  pc.items.forEach(it => {
    totalUnidades += it.cantidad;
    totalCosto += it.cantidad * it.precio;
  });

  const filas = pc.items.map(it => {
    const subtotal = it.cantidad * it.precio;
    return `<tr>
      <td style="padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;color:#1e293b;">${escapeHTML(it.articulo)}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;text-align:right;font-weight:700;color:#12264D;">${it.cantidad}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;text-align:right;color:#475569;">$${formatMiles(it.precio)}</td>
      <td style="padding:6px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;text-align:right;color:#047857;font-weight:600;">$${formatMiles(subtotal)}</td>
    </tr>`;
  }).join("");

  const tituloModo = pc.modo === "linea" ? "línea" : `${pc.items.length} ítems seleccionados`;

  state.modal = `
    <div data-action="prevent-close" onclick="event.stopPropagation()"
         style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(0,0,0,.5);display:flex;
                align-items:center;justify-content:center;z-index:3000;">
      <div style="background:white;border-radius:16px;box-shadow:0 20px 60px rgba(0,0,0,.25);
                  width:560px;max-width:95vw;max-height:90vh;overflow-y:auto;">

        <!-- Header -->
        <div style="background:linear-gradient(135deg,#12264D,#2B4A82);border-radius:16px 16px 0 0;
                    padding:20px 24px;color:white;">
          <div style="font-size:16px;font-weight:800;margin-bottom:4px;">📋 Confirmar ingreso de mercancía</div>
          <div style="font-size:11.5px;opacity:.85;">OCP ${ocp.numero||ocp.id} · ${tituloModo}</div>
        </div>

        <!-- Cuerpo -->
        <div style="padding:20px 24px;">
          <p style="margin:0 0 9px;font-size:12px;color:#475569;">
            Revisa el detalle a continuación. Al confirmar, los productos ingresarán al inventario y
            se registrará la recepción en la OCP.
          </p>

          <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
            <thead>
              <tr style="background:#f8fafc;">
                <th style="padding:6px 10px;text-align:left;font-size:10.5px;color:#64748b;font-weight:700;text-transform:uppercase;">Producto</th>
                <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:#64748b;font-weight:700;text-transform:uppercase;">Cantidad</th>
                <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:#64748b;font-weight:700;text-transform:uppercase;">Precio unit.</th>
                <th style="padding:6px 10px;text-align:right;font-size:10.5px;color:#64748b;font-weight:700;text-transform:uppercase;">Subtotal</th>
              </tr>
            </thead>
            <tbody>${filas}</tbody>
            <tfoot>
              <tr style="background:#f8fafc;">
                <td colspan="1" style="padding:6px 10px;font-size:12px;font-weight:700;color:#15803d;">TOTAL</td>
                <td style="padding:6px 10px;text-align:right;font-size:12px;font-weight:800;color:#12264D;">${totalUnidades}</td>
                <td></td>
                <td style="padding:6px 10px;text-align:right;font-size:14px;font-weight:800;color:#15803d;">$${formatMiles(totalCosto)}</td>
              </tr>
            </tfoot>
          </table>

          <div style="background:#f8fafc;border:1px solid #fde047;border-radius:6px;
                      padding:7px 10px;margin-top:14px;font-size:11.5px;color:#854d0e;">
            ⚠️ Esta acción no se puede deshacer. Los saldos de inventario se actualizarán de inmediato.
          </div>
        </div>

        <!-- Footer -->
        <div style="padding:16px 24px;border-top:1px solid #f1f5f9;display:flex;gap:10px;justify-content:flex-end;">
          <button onclick="ingresoCancelarConfirm()"
                  style="background:#f1f5f9;color:#475569;border:1px solid #e2e8f0;
                         padding:10px 20px;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer;">
            ← Volver
          </button>
          <button onclick="ingresoConfirmar()"
                  style="background:linear-gradient(135deg,#12264D,#0B132B);color:white;border:none;
                         padding:10px 24px;border-radius:6px;font-size:12px;font-weight:700;
                         cursor:pointer;box-shadow:0 2px 8px rgba(5,150,105,.3);">
            ✅ Confirmar ingreso
          </button>
        </div>
      </div>
    </div>`;
  render();
}

export function renderIngresoMercanciaModal() {
  const im = state.ingresoModal;
  if (!im) return;
  const ocp = state.data.ocp.find(o => o.id === im.ocpId);
  if (!ocp) { state.modal = null; render(); return; }

  const prov = (state.data.proveedores || []).find(p => p.id === ocp.proveedor_id);
  const items = ocp.items || [];
  const sel   = im.selected   || {};
  const cants = im.cantidades || {};
  const precios = im.precios  || {};
  const haySel  = Object.values(sel).some(Boolean);

  // Hay items con cantidad disponible
  const itemsDisponibles = items.filter(it => {
    const sol  = parseNumber(it.cantidad) || 0;
    const rec  = getCantidadIngresadaOCP(ocp, it.articulo);
    return sol - rec > 0;
  });

  const filas = items.map((it, idx) => {
    const articulo  = it.articulo || "";
    const solicit   = parseNumber(it.cantidad) || 0;
    const ingresado = getCantidadIngresadaOCP(ocp, articulo);
    const disponible = Math.max(0, solicit - ingresado);
    const precio    = parseNumber(precios[articulo] !== undefined ? precios[articulo] : (it.precio_unitario ?? it.costo)) || 0;
    const checked   = !!sel[articulo];
    const completo  = disponible === 0;

    const rowBg = completo ? "#f0fdf4" : checked ? "#eff6ff" : "white";
    const rowOp = completo ? "0.7" : "1";

    return `<tr style="border-bottom:1px solid #e5e7eb;background:${rowBg};opacity:${rowOp};transition:background .15s;">
      <td style="padding:8px 10px;text-align:center;">
        ${completo
          ? `<span title="Completamente ingresado" style="font-size:16px;">✅</span>`
          : `<input type="checkbox"
               onchange="handleIngresoToggle(this, '${escapeHTML(articulo).replace(/'/g, '&#39;')}')"
               ${checked ? "checked" : ""} style="width:18px;height:18px;cursor:pointer;">`}
      </td>
      <td style="padding:8px 10px;">
        <div style="font-weight:700;font-size:12px;color:#1f2937;">${escapeHTML(articulo)}</div>
        ${it.descripcion ? `<div style="font-size:10px;color:#6b7280;">${escapeHTML(it.descripcion)}</div>` : ""}
      </td>
      <td style="padding:8px 10px;text-align:right;font-weight:600;">${solicit} ${escapeHTML(it.unidad||"")}</td>
      <td style="padding:8px 10px;text-align:right;color:#059669;font-weight:700;">${ingresado}</td>
      <td style="padding:8px 10px;text-align:right;font-weight:800;color:${disponible>0?"#12264D":"#059669"};">${disponible}</td>
      <td style="padding:8px 10px;text-align:right;">
        ${checked && !completo
          ? `<input type="number"
               oninput="handleIngresoInput(this, '${escapeHTML(articulo).replace(/'/g, '&#39;')}', 'precio')"
               value="${precio}" min="0" step="1"
               style="width:90px;padding:4px 6px;border:1px solid #12264D;border-radius:6px;font-size:11.5px;text-align:right;">`
          : `<span style="font-size:11.5px;color:#6b7280;">${fmtCOP(precio)}</span>`}
      </td>
      <td style="padding:8px 10px;text-align:right;">
        ${!completo
          ? `<input type="number"
               oninput="handleIngresoInput(this, '${escapeHTML(articulo).replace(/'/g, '&#39;')}', 'cantidad')"
               value="${cants[articulo] ?? ""}" min="0" max="${disponible}" step="1"
               placeholder="${disponible}" ${checked ? "" : "disabled"}
               style="width:80px;padding:4px 6px;border:1px solid ${checked?"#12264D":"#e5e7eb"};border-radius:6px;font-size:11.5px;text-align:right;background:${checked?"white":"#f9fafb"};">`
          : `<span style="font-size:10.5px;color:#059669;font-weight:700;">✔ Completo</span>`}
      </td>
      <td style="padding:8px 10px;text-align:center;">
        ${!completo
          ? (checked
            ? `<button onclick="ingresoAbrirLinea('${escapeHTML(articulo).replace(/\\/g,'\\\\').replace(/'/g,"\\'")}')"
               style="padding:4px 10px;font-size:10.5px;font-weight:700;border:none;border-radius:6px;cursor:pointer;
                      background:#12264D;color:white;">
               📥 Ingresar</button>`
            : `<button disabled
               style="padding:4px 10px;font-size:10.5px;font-weight:700;border:none;border-radius:6px;cursor:not-allowed;
                      background:#e5e7eb;color:#9ca3af;">
               📥 Ingresar</button>`)
          : ""}
      </td>
    </tr>`;
  }).join("");

  const historial = (ocp._recepciones || []).length > 0 ? `
    <details style="margin-top:14px;">
      <summary style="font-size:10.5px;font-weight:700;color:#6b7280;cursor:pointer;padding:4px 0;">
        📋 Historial de recepciones (${ocp._recepciones.length})
      </summary>
      <div style="margin-top:6px;font-size:10.5px;color:#374151;">
        ${ocp._recepciones.map((r,i) => `
          <div style="padding:6px 10px;background:#f9fafb;border-radius:6px;margin-bottom:4px;border-left:3px solid #12264D;">
            <strong>#${i+1}</strong> — ${r.fecha} &nbsp;·&nbsp;
            ${(r.items||[]).map(ri => `${escapeHTML(ri.articulo)}: ${ri.cantidad} uds @ ${fmtCOP(ri.precio)}`).join(" | ")}
          </div>`).join("")}
      </div>
    </details>` : "";

  state.modal = `
    <div style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1100;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:960px;max-width:100%;max-height:92vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,.22);">

        <!-- Header -->
        <div style="padding:14px 20px;border-bottom:1px solid #e5e7eb;background:linear-gradient(135deg,#12264D15,#2B4A8208);border-radius:14px 14px 0 0;flex-shrink:0;">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;">
            <div>
              <div style="font-size:17px;font-weight:800;color:#12264D;">📦 Ingreso de Mercancía</div>
              <div style="font-size:11.5px;color:#6b7280;margin-top:2px;">
                ${escapeHTML(ocp.numero)} &nbsp;·&nbsp; ${escapeHTML(prov?.nombre||"—")} &nbsp;·&nbsp; ${ocp.fecha||""}
                &nbsp;·&nbsp; <span style="font-weight:700;color:#f59e0b;">${ocp.items?.length||0} ítems</span>
              </div>
            </div>
            <button onclick="cerrarIngresoModal()" style="background:transparent;border:none;font-size:16px;color:#6b7280;cursor:pointer;">✕</button>
          </div>
          ${itemsDisponibles.length === 0
            ? `<div style="margin-top:8px;padding:6px 12px;background:#d1fae5;border-radius:6px;font-size:11.5px;font-weight:700;color:#047857;">✅ Todos los ítems han sido completamente ingresados</div>`
            : `<div style="margin-top:8px;font-size:10.5px;color:#6b7280;">
                Selecciona uno o varios ítems con el checkbox para habilitarlos y escribir la cantidad a ingresar.
                Si no escribes una cantidad, se ingresará el total disponible.
               </div>`}
        </div>

        <!-- Tabla -->
        <div style="flex:1;overflow-y:auto;padding:0;">
          <table style="width:100%;border-collapse:collapse;">
            <thead style="position:sticky;top:0;z-index:10;background:#f8fafc;">
              <tr style="border-bottom:2px solid #e5e7eb;">
                <th style="padding:8px 10px;width:44px;text-align:center;vertical-align:middle;">
                  ${itemsDisponibles.length > 0 ? (() => {
                    // Guardar lista de artículos en variable global para evitar
                    // comillas dobles de JSON.stringify dentro del atributo onchange
                    window.__ingresoArts = itemsDisponibles.map(it => it.articulo);
                    const allSel = Object.values(sel).filter(Boolean).length === itemsDisponibles.length;
                    return `<label style="display:flex;align-items:center;justify-content:center;cursor:pointer;" title="${allSel ? 'Deseleccionar todo' : 'Seleccionar todo'}"><input type="checkbox" ${allSel ? "checked" : ""} onchange="(window.__ingresoArts||[]).forEach(function(a){state.ingresoModal.selected[a]=this.checked;}.bind(this));renderIngresoMercanciaModal();" style="width:15px;height:15px;cursor:pointer;accent-color:#12264D;"/></label>`;
                  })() : ""}
                </th>
                <th style="padding:8px 10px;text-align:left;font-size:10.5px;font-weight:700;color:#6b7280;">PRODUCTO</th>
                <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;color:#6b7280;">SOLICITADO</th>
                <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;color:#059669;">INGRESADO</th>
                <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;color:#12264D;">DISPONIBLE</th>
                <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;color:#6b7280;">PRECIO</th>
                <th style="padding:8px 10px;text-align:right;font-size:10.5px;font-weight:700;color:#12264D;">A INGRESAR</th>
                <th style="padding:8px 10px;text-align:center;font-size:10.5px;font-weight:700;color:#6b7280;">ACCIÓN</th>
              </tr>
            </thead>
            <tbody>${filas}</tbody>
          </table>
        </div>

        <!-- Historial -->
        ${historial ? `<div style="padding:0 20px 8px;flex-shrink:0;">${historial}</div>` : ""}

        <!-- Footer -->
        <div style="padding:12px 20px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;flex-shrink:0;flex-wrap:wrap;gap:8px;">
          <div style="font-size:11.5px;color:#6b7280;">
            ${haySel
              ? `<span style="color:#12264D;font-weight:700;">${Object.values(sel).filter(Boolean).length} ítem(s) seleccionado(s)</span>`
              : "Selecciona ítems para habilitar el ingreso"}
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <button onclick="cerrarIngresoModal()" style="padding:8px 16px;border:1px solid #e5e7eb;background:white;border-radius:6px;cursor:pointer;font-size:12px;font-weight:600;">Cerrar</button>
            ${haySel
              ? `<button onclick="ingresoAbrirTodos()"
                  style="padding:8px 18px;border:none;border-radius:6px;font-size:12px;font-weight:700;cursor:pointer;
                         background:#12264D;color:white;">
                  📥 Ingresar seleccionados
                </button>`
              : `<button disabled
                  style="padding:8px 18px;border:none;border-radius:6px;font-size:12px;font-weight:700;cursor:not-allowed;
                         background:#e5e7eb;color:#9ca3af;">
                  📥 Ingresar seleccionados
                </button>`}
          </div>
        </div>
      </div>
    </div>`;
  render();

}

export function showPagarOCP(ocpId) {
  const ocp = state.data.ocp.find(o => o.id === ocpId);
  if (!ocp) return;
  const saldo = getSaldoOCP(ocp);
  state.editingPagoOcpId = ocpId;
  state.editingPago = {
    monto: saldo,
    fecha: today(),
    medio: "transferencia",
    referencia: "",
    notas: ""
  };
  renderPagoModal();
}

export function renderPagoModal() {
  const ocp = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
  if (!ocp) return;
  const proveedor = state.data.proveedores.find(p => p.id === ocp.proveedor_id) || {};
  const pago = state.editingPago;
  const op = ocp._origen ? state.data.pedidos.find(p => p.id === ocp._origen.id) : null;

  const total = parseNumber(ocp.total);
  const montoPagado = getMontoPagadoOCP(ocp);
  const saldoPendiente = getSaldoOCP(ocp);
  const pagosPrevios = getPagosOCP(ocp);

  const mediosOptions = MEDIOS_PAGO.map(m => `<option value="${m.value}"${pago.medio === m.value ? " selected" : ""}>${m.label}</option>`).join("");

  // Panel de descuentos financieros disponibles para esta OCP
  const condDescuentos = getDescuentosFinancieros(proveedor);
  const baseDescOCP = baseDescuentoOCP(ocp);
  const descuentosPanelHTML = condDescuentos.length > 0 ? (() => {
    const filas = condDescuentos.map(cond => {
      const deadline = addDias(ocp.fecha, cond.dias);
      const diasRest = deadline ? calcularDiasRestantes(deadline) : null;
      const vencido  = diasRest !== null && diasRest < 0;
      const hoyVence = diasRest === 0;
      const montoDesc = Math.round(baseDescOCP * (cond.pct/100));
      const label = cond.dias === 0 ? "Contado / anticipado" : `a ${cond.dias} días`;
      const estadoBadge = vencido
        ? `<span style="color:#ef4444;font-weight:700;font-size:10px;">⚠️ VENCIÓ hace ${Math.abs(diasRest)}d</span>`
        : hoyVence
        ? `<span style="color:#f59e0b;font-weight:700;font-size:10px;">⏰ VENCE HOY</span>`
        : diasRest !== null
        ? `<span style="color:#10b981;font-size:10px;">✅ ${diasRest}d restantes · hasta ${fmtDate(deadline)}</span>`
        : `<span style="color:#92400e;font-size:10px;">Sin fecha límite</span>`;
      const aplicarBtn = saldoPendiente > 0.5 && cond.pct > 0
        ? (vencido
          ? `<button data-action="aplicar-desc-condicion" data-pct="${cond.pct}" data-vencido="1" data-dias-vencido="${Math.abs(diasRest)}"
              style="${btnStyle("#f59e0b",null,true)}margin-top:4px;">🤝 Aplicar (renegociado)</button>`
          : `<button data-action="aplicar-desc-condicion" data-pct="${cond.pct}"
              style="${btnStyle("#10b981",null,true)}margin-top:4px;">Aplicar</button>`)
        : "";
      return `
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 10px;
          ${vencido?"opacity:0.75;":""}border-top:1px solid #fde68a;">
          <div>
            <span style="font-size:11.5px;font-weight:700;color:#78350f;">${escapeHTML(cond.label)}</span>
            <span style="font-size:10.5px;color:#92400e;margin-left:6px;">— ${label}</span>
            <div style="margin-top:3px;">${estadoBadge}</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:12px;font-weight:800;color:${vencido?"#ef4444":"#10b981"};">${cond.pct}%</div>
            ${montoDesc>0 ? `<div style="font-size:10px;color:#065f46;">= ${fmtCOP(montoDesc)}</div>` : ""}
            ${aplicarBtn}
          </div>
        </div>
      `;
    }).join("");
    return `
      <div style="background:#fef3c7;border:1px solid #f59e0b;border-radius:6px;margin-bottom:10px;overflow:hidden;">
        <div style="background:#f59e0b;padding:8px 14px;">
          <span style="font-size:11.5px;font-weight:700;color:white;">💸 DESCUENTOS FINANCIEROS DISPONIBLES</span>
        </div>
        ${filas}
        <div style="padding:7px 12px;font-size:10px;color:#78350f;border-top:1px solid #fde68a;">
          ℹ️ Los porcentajes se calculan sobre el <strong>subtotal sin IVA</strong> pendiente (${fmtCOP(baseDescOCP)}).
          Las retenciones se calculan sobre el subtotal SIN descuento y ya están restadas del saldo.
          Un plazo vencido se puede aplicar igual si lo renegociaste con el proveedor: queda registrado como renegociado.
        </div>
      </div>
    `;
  })() : "";

  const descAplicado = parseNumber(pago.descuentoAplicado) || 0;
  const descAplicadoHTML = descAplicado > 0 ? `
    <div style="margin-bottom:12px;padding:10px;background:#ecfdf5;border:1px solid #10b981;border-radius:6px;display:flex;justify-content:space-between;align-items:center;gap:8px;">
      <div style="font-size:11.5px;color:#065f46;">
        💚 Descuento financiero aplicado: <strong>${fmtCOP(descAplicado)}</strong><br/>
        <span style="font-size:10.5px;">Pagas ${fmtCOP(parseNumber(pago.monto))} y el pasivo se extingue por ${fmtCOP(parseNumber(pago.monto) + descAplicado)} (el descuento se contabiliza como ingreso financiero, cuenta 4210).</span>
      </div>
      <button data-action="quitar-descuento-pago" style="${btnStyle(null,"secondary")}white-space:nowrap;">✕ Quitar</button>
    </div>
  ` : "";

  const descManualHTML = saldoPendiente > 0.5 ? `
    <div style="margin-bottom:12px;padding:10px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;">
      <label style="font-size:10.5px;color:#92400e;font-weight:700;">🤝 Descuento negociado con el proveedor (monto en pesos)</label>
      <div style="display:flex;gap:8px;align-items:stretch;margin-top:4px;">
        <input data-pago="descuentoManual" value="${escapeHTML(String(pago.descuentoManual || ""))}" inputmode="numeric" placeholder="Ej: 58000" style="${inputStyle}flex:1;"/>
        <button data-action="aplicar-desc-manual" style="${btnStyle("#d97706",null,true)};white-space:nowrap;">Aplicar</button>
      </div>
      <div style="font-size:10px;color:#92400e;margin-top:4px;">
        Para descuentos por pronto pago reactivados en una negociación aunque el plazo original ya venció:
        el monto a pagar queda en saldo − descuento y la factura queda saldada.
      </div>
    </div>
  ` : "";

  const historialHTML = pagosPrevios.length > 0 ? `
    <div style="margin-bottom:10px;">
      <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">📜 HISTORIAL DE ABONOS</div>
      <div style="border:1px solid ${C.border};border-radius:6px;overflow:hidden;">
        ${pagosPrevios.map((p,i) => `
          <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 10px;${i>0?`border-top:1px solid ${C.border};`:""}background:${i%2===0?"#f9fafb":"white"};font-size:11.5px;">
            <div>
              <strong style="color:${C.text};">${fmtCOP(p.monto)}</strong>
              <span style="color:${C.muted};margin-left:6px;">${fmtDate(p.fecha)}</span>
              ${p.medio ? `<span style="color:${C.muted};margin-left:6px;">· ${(MEDIOS_PAGO.find(m=>m.value===p.medio)||{}).label || p.medio}</span>` : ""}
            </div>
            ${p.referencia ? `<div style="color:${C.muted};font-size:10.5px;">Ref: ${escapeHTML(p.referencia)}</div>` : ""}
          </div>
        `).join("")}
      </div>
    </div>
  ` : "";

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:580px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:#f8fafc;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">💳 Registrar abono / pago de OCP</h3>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          <div style="background:#f8fafc;border-radius:6px;padding:10px;margin-bottom:10px;">
            <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">ORDEN DE COMPRA</div>
            <div style="font-size:16px;font-weight:700;color:${C.text};">${escapeHTML(ocp.numero)}</div>
            <div style="font-size:12px;color:${C.muted};margin-top:4px;">${escapeHTML(proveedor.nombre || "—")} • ${fmtDate(ocp.fecha)}</div>
            ${op ? `<div style="font-size:10.5px;color:${C.muted};margin-top:4px;">⬅ Asociada a ${escapeHTML(op.numero)}</div>` : ""}
            <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-top:12px;padding-top:10px;border-top:1px solid ${C.border};text-align:center;">
              <div>
                <div style="font-size:10px;color:${C.muted};font-weight:700;">TOTAL</div>
                <div style="font-size:12px;font-weight:800;color:${C.text};">${fmtCOP(total)}</div>
              </div>
              <div>
                <div style="font-size:10px;color:${C.muted};font-weight:700;">ABONADO</div>
                <div style="font-size:12px;font-weight:800;color:#10b981;">${fmtCOP(montoPagado)}</div>
              </div>
              <div>
                <div style="font-size:10px;color:${C.muted};font-weight:700;">SALDO</div>
                <div style="font-size:12px;font-weight:800;color:${saldoPendiente>0?"#d97706":"#10b981"};">${fmtCOP(saldoPendiente)}</div>
              </div>
            </div>
          </div>

          ${descuentosPanelHTML}

          ${descAplicadoHTML}

          ${historialHTML}

          <div style="margin-bottom:12px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Monto a abonar</label>
            <div style="display:flex;gap:8px;align-items:stretch;">
              <input data-pago="monto" value="${pago.monto}" placeholder="Monto del abono" style="${inputStyle}flex:1;"/>
              <button data-action="abono-saldo-total" style="${btnStyle("#10b981",null,true)};white-space:nowrap;">Pagar saldo total</button>
            </div>
            <div style="font-size:10px;color:${C.muted};margin-top:4px;">Si el monto es menor al saldo, la OCP quedará marcada como <strong>pago parcial</strong>.</div>
          </div>

          <div style="margin-bottom:12px;padding:10px;background:#f8fafc;border:1px solid #bfdbfe;border-radius:6px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">O calcula el abono por porcentaje (sobre el valor bruto, antes de IVA)</label>
            <div style="display:flex;gap:8px;align-items:stretch;margin-top:4px;">
              <input data-pago="porcentaje" type="number" min="1" max="100" inputmode="decimal" value="${pago.porcentaje||""}" placeholder="1 a 100" style="${inputStyle}flex:1;"/>
              <span style="display:flex;align-items:center;padding:0 6px;font-weight:700;color:${C.muted};">%</span>
              <button data-action="abono-por-porcentaje" style="${btnStyle("#2B4A82",null,true)};white-space:nowrap;">Calcular monto</button>
            </div>
            <div style="font-size:10px;color:${C.muted};margin-top:4px;">Valor bruto (subtotal sin IVA): <strong>${fmtCOP(parseNumber(ocp.subtotal))}</strong></div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:12px;">
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Fecha de pago</label>
              <input data-pago="fecha" type="date" value="${pago.fecha}" style="${inputStyle}"/>
            </div>
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Medio de pago</label>
              <select data-pago="medio" style="${inputStyle};cursor:pointer;">${mediosOptions}</select>
            </div>
          </div>

          ${(() => {
            const pctMap = { transferencia: "desc_fin_transferencia", efectivo: "desc_fin_efectivo", tarjeta: "desc_fin_tarjeta" };
            const campo = pctMap[pago.medio];
            const pct = campo ? (parseNumber(proveedor[campo]) || 0) : 0;
            if (pct <= 0) return "";
            const descuento = Math.round(parseNumber(ocp.subtotal) * pct / 100);
            const medioLabel = (MEDIOS_PAGO.find(m=>m.value===pago.medio)||{}).label || pago.medio;
            return `
              <div style="margin-bottom:12px;padding:10px;background:#f8fafc;border:1px solid #bbf7d0;border-radius:6px;">
                <div style="font-size:10.5px;color:#065f46;font-weight:700;">💚 Descuento financiero disponible — ${escapeHTML(proveedor.nombre||"")}</div>
                <div style="font-size:11.5px;color:#065f46;margin-top:4px;">${pct}% sobre el subtotal (sin IVA, ${fmtCOP(parseNumber(ocp.subtotal))}) por pago ${medioLabel} = <strong>${fmtCOP(descuento)}</strong></div>
                <button data-action="aplicar-descuento-financiero" style="${btnStyle("#10b981",null,true)}margin-top:6px;">Aplicar descuento al monto a pagar</button>
              </div>
            `;
          })()}

          ${descManualHTML}

          <div style="margin-bottom:12px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Referencia / Comprobante</label>
            <input data-pago="referencia" value="${escapeHTML(pago.referencia)}" placeholder="N° transacción, cheque, recibo..." style="${inputStyle}"/>
          </div>

          <div style="margin-bottom:9px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Notas (opcional)</label>
            <textarea data-pago="notas" rows="2" style="${inputStyle}resize:vertical;">${escapeHTML(pago.notas)}</textarea>
          </div>

          ${op ? `
            <div style="background:#e4eaf5;border:1px solid #93c5fd;border-radius:6px;padding:10px;margin-bottom:9px;font-size:11.5px;color:#1e40af;">
              💡 Al registrar el abono, este costo se imputará a la liquidación de <strong>${escapeHTML(op.numero)}</strong> para calcular la rentabilidad real.
            </div>
          ` : ""}

          <div style="display:flex;gap:10px;justify-content:flex-end;padding-top:14px;border-top:1px solid ${C.border};">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="confirmar-pago" style="${btnStyle("#10b981")}">✅ Registrar abono</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

export async function confirmarPagoOCP() {
  const ocp = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
  if (!ocp) return;
  const pago = state.editingPago;
  if (!pago.fecha) { alert("Selecciona la fecha de pago"); return; }

  const monto = parseNumber(pago.monto);
  const descuentoAplicado = parseNumber(pago.descuentoAplicado) || 0;
  if (monto <= 0) { alert("El monto del abono debe ser mayor a cero"); return; }

  const saldoPendiente = getSaldoOCP(ocp);
  if (monto + descuentoAplicado > saldoPendiente + 0.01) {
    const extinguido = descuentoAplicado > 0 ? ` + descuento ${fmtCOP(descuentoAplicado)}` : "";
    if (!confirm(`El monto (${fmtCOP(monto)}${extinguido}) es mayor al saldo pendiente (${fmtCOP(saldoPendiente)}).\n\n¿Deseas registrarlo de todas formas?`)) return;
  }

  const pagosPrevios = getPagosOCP(ocp);
  const nuevoPago = {
    monto,
    descuento: descuentoAplicado,
    fecha: pago.fecha,
    medio: pago.medio,
    referencia: pago.referencia,
    notas: pago.notas,
    registrado_en: new Date().toISOString()
  };
  const pagos = [...pagosPrevios, nuevoPago];

  const updated = {
    ...ocp,
    _pagos: pagos,
    _pago: nuevoPago // último pago, para compatibilidad con vistas existentes
  };
  // El estado sale del SALDO real (que ya descuenta retenciones y descuentos
  // financieros): con la fórmula vieja (suma de montos vs total) un pago con
  // descuento o una OCP con retenciones quedaba en "parcial" para siempre.
  const saldoTras = getSaldoOCP(updated);
  updated.estado_pago = saldoTras <= 0.5 ? "pagada" : (getMontoExtinguidoOCP(updated) > 0.5 ? "parcial" : "por_pagar");
  saveData("ocp", state.data.ocp.map(o => o.id === ocp.id ? updated : o));
  await generarAsientoPagoOCP(updated, nuevoPago, pago.descuentoAplicado || 0);
  state.modal = null;
  state.editingPagoOcpId = null;
  state.editingPago = null;
  render();
}

// ── Pago conjunto de varias facturas del mismo proveedor ────────────────────
// El usuario marca las facturas a su criterio (checkboxes de la lista de CxP)
// y las paga en un solo registro: un pago por OCP involucrada, con imputación
// explícita a las facturas elegidas (p._facturas) para que las NO elegidas
// conserven su saldo aunque sean más antiguas.
// Base de un descuento por pronto pago: el SUBTOTAL sin IVA de lo pendiente.
// El descuento se pacta sobre el valor antes de IVA; las retenciones se
// calculan sobre el subtotal SIN descuento (ya lo hizo la causación) y vienen
// restadas del saldo, así que el neto a girar es saldo − descuento.
function baseDescuentoOCP(ocp) {
  const exigible = Math.max(1, (parseNumber(ocp.total) || 0) - getRetencionesOCP(ocp.id));
  const frac = Math.min(1, getSaldoOCP(ocp) / exigible);
  return Math.round((parseNumber(ocp.subtotal) || 0) * frac);
}

function baseDescuentoFilas(filas) {
  return Math.round(filas.reduce((s, f) => {
    const sub = parseNumber((f.fact && f.fact.subtotal != null) ? f.fact.subtotal : f.o.subtotal) || 0;
    const tot = parseNumber(f.totalFila) || 0;
    return s + (tot > 0 ? sub * Math.min(1, f.saldoFila / tot) : 0);
  }, 0));
}

function filasSeleccionadasPago() {
  const sel = state.cxpSelFact || {};
  const ocps = (state.data.ocp || []).filter(o => o._procesado && o._procesado.factura && o.estado !== "cancelada");
  return filasCxPDeOcps(ocps).filter(f => sel[f.key] && f.saldoFila > 0.5);
}

export function renderPagoMultiModal() {
  const pm = state._pagoMulti;
  if (!pm) return;
  const filas = filasSeleccionadasPago();
  if (filas.length === 0) { state.modal = null; state._pagoMulti = null; render(); return; }
  const proveedor = state.data.proveedores.find(p => p.id === filas[0].o.proveedor_id) || {};
  const totalSel = filas.reduce((s, f) => s + f.saldoFila, 0);
  const descuento = Math.min(parseNumber(pm.descuento) || 0, totalSel);
  const neto = totalSel - descuento;
  const mediosOptions = MEDIOS_PAGO.map(m => `<option value="${m.value}"${pm.medio === m.value ? " selected" : ""}>${m.label}</option>`).join("");

  const filasHTML = filas.map(f => {
    const factNum = f.fact ? (f.fact.factNum || (f.o._procesado && f.o._procesado.factura_numero)) : (f.o._procesado && f.o._procesado.factura_numero);
    return `
      <tr style="border-top:1px solid ${C.border};">
        <td style="padding:5px 10px;font-weight:700;color:${C.purple};">${escapeHTML(f.o.numero)}</td>
        <td style="padding:5px 10px;">${factNum ? `📄 ${escapeHTML(String(factNum))}` : "—"}</td>
        <td style="padding:5px 10px;color:${C.muted};">${fmtDate((f.fact && f.fact.fecha) || f.o.fecha)}</td>
        <td style="padding:5px 10px;text-align:right;font-weight:700;">${fmtCOP(f.saldoFila)}</td>
      </tr>
    `;
  }).join("");

  // Condiciones de pronto pago del proveedor como atajos, calculadas sobre el
  // subtotal SIN IVA de lo seleccionado (así se pactan los prontos pagos).
  const baseDesc = baseDescuentoFilas(filas);
  const condDescuentos = getDescuentosFinancieros(proveedor);
  const atajosDesc = condDescuentos.length > 0 ? `
    <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;">
      ${condDescuentos.map(cond => `
        <button data-action="pago-multi-desc-pct" data-pct="${cond.pct}"
          style="${btnStyle("#f59e0b",null,true)}">${cond.label} ${cond.pct}% = ${fmtCOP(Math.round(baseDesc * cond.pct / 100))}</button>
      `).join("")}
    </div>
  ` : "";

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:640px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:#f8fafc;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">💳 Pago conjunto — ${escapeHTML(proveedor.nombre || "")}</h3>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          <div style="border:1px solid ${C.border};border-radius:6px;overflow:auto;margin-bottom:10px;">
            <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
              <thead><tr style="background:#f1f5f9;">
                <th style="padding:5px 10px;text-align:left;font-size:10px;color:${C.muted};">OCP</th>
                <th style="padding:5px 10px;text-align:left;font-size:10px;color:${C.muted};">Factura</th>
                <th style="padding:5px 10px;text-align:left;font-size:10px;color:${C.muted};">Fecha</th>
                <th style="padding:5px 10px;text-align:right;font-size:10px;color:${C.muted};">Saldo a pagar</th>
              </tr></thead>
              <tbody>${filasHTML}</tbody>
              <tfoot>
                <tr style="background:#f8fafc;">
                  <td colspan="3" style="padding:5px 10px;font-size:10.5px;color:${C.muted};">Subtotal sin IVA de lo seleccionado (base del descuento)</td>
                  <td style="padding:5px 10px;text-align:right;font-size:10.5px;color:${C.muted};font-weight:700;">${fmtCOP(baseDesc)}</td>
                </tr>
                <tr style="border-top:2px solid ${C.border};background:#f8fafc;">
                  <td colspan="3" style="padding:6px 10px;font-weight:700;">TOTAL SELECCIONADO</td>
                  <td style="padding:6px 10px;text-align:right;font-weight:800;">${fmtCOP(totalSel)}</td>
                </tr>
                ${descuento > 0 ? `
                  <tr style="background:#fffbeb;">
                    <td colspan="3" style="padding:6px 10px;color:#92400e;font-weight:700;">(−) Descuento financiero negociado</td>
                    <td style="padding:6px 10px;text-align:right;font-weight:800;color:#d97706;">${fmtCOP(descuento)}</td>
                  </tr>
                  <tr style="background:#ecfdf5;">
                    <td colspan="3" style="padding:6px 10px;color:#065f46;font-weight:700;">NETO A PAGAR</td>
                    <td style="padding:6px 10px;text-align:right;font-weight:800;color:#10b981;">${fmtCOP(neto)}</td>
                  </tr>
                ` : ""}
              </tfoot>
            </table>
          </div>

          <div style="margin-bottom:12px;padding:10px;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;">
            <label style="font-size:10.5px;color:#92400e;font-weight:700;">🤝 Descuento financiero (opcional, monto en pesos)</label>
            <input value="${escapeHTML(String(pm.descuento || ""))}" inputmode="numeric" placeholder="0"
              style="${inputStyle}margin-top:4px;" oninput="state._pagoMulti.descuento=this.value" onchange="renderPagoMultiModal()"/>
            ${atajosDesc}
            <div style="font-size:10px;color:#92400e;margin-top:4px;">
              Los porcentajes se calculan sobre el <strong>subtotal sin IVA</strong> de lo seleccionado (${fmtCOP(baseDesc)});
              las retenciones se calculan sobre el subtotal SIN descuento y ya están restadas del saldo.
              Si negociaste un pronto pago (aunque el plazo original haya vencido), escribe aquí el descuento total:
              se reparte entre las facturas, pagas el neto y todas quedan saldadas. Contablemente el descuento
              es un ingreso financiero (cuenta 4210).
            </div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:12px;">
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Fecha de pago</label>
              <input type="date" value="${pm.fecha}" style="${inputStyle}" onchange="state._pagoMulti.fecha=this.value"/>
            </div>
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Medio de pago</label>
              <select style="${inputStyle};cursor:pointer;" onchange="state._pagoMulti.medio=this.value">${mediosOptions}</select>
            </div>
          </div>
          <div style="margin-bottom:12px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Referencia / Comprobante</label>
            <input value="${escapeHTML(pm.referencia || "")}" placeholder="N° transacción, cheque, recibo..." style="${inputStyle}" oninput="state._pagoMulti.referencia=this.value"/>
          </div>
          <div style="margin-bottom:9px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Notas (opcional)</label>
            <textarea rows="2" style="${inputStyle}resize:vertical;" oninput="state._pagoMulti.notas=this.value">${escapeHTML(pm.notas || "")}</textarea>
          </div>

          <div style="background:#e4eaf5;border:1px solid #93c5fd;border-radius:6px;padding:10px;margin-bottom:9px;font-size:11px;color:#1e40af;">
            💡 Se registrará un pago por cada OCP involucrada, imputado a las facturas que seleccionaste:
            las facturas del mismo proveedor que NO marcaste conservan su saldo.
          </div>

          <div style="display:flex;gap:10px;justify-content:flex-end;padding-top:14px;border-top:1px solid ${C.border};">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="pago-multi-confirmar" style="${btnStyle("#10b981")}">✅ Pagar ${fmtCOP(neto)}</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

export async function confirmarPagoMulti() {
  const pm = state._pagoMulti;
  if (!pm) return;
  if (!pm.fecha) { alert("Selecciona la fecha de pago"); return; }
  const filas = filasSeleccionadasPago();
  if (filas.length === 0) { alert("No hay facturas seleccionadas con saldo"); return; }

  const totalSel = filas.reduce((s, f) => s + f.saldoFila, 0);
  const descTotal = Math.min(parseNumber(pm.descuento) || 0, totalSel);
  if (descTotal < 0) { alert("El descuento no puede ser negativo"); return; }

  // Prorratear el descuento entre las filas por su saldo; la última se lleva
  // el residuo del redondeo para que la suma cierre exacta.
  let descAsignado = 0;
  const filasConDesc = filas.map((f, i) => {
    const d = i === filas.length - 1
      ? descTotal - descAsignado
      : Math.round(descTotal * (f.saldoFila / totalSel));
    descAsignado += d;
    return { ...f, desc: d };
  });

  // Un pago por OCP: el pasivo (2205) y el asiento viven a nivel de orden.
  const porOcp = new Map();
  filasConDesc.forEach(f => {
    if (!porOcp.has(f.o.id)) porOcp.set(f.o.id, { ocp: f.o, filas: [] });
    porOcp.get(f.o.id).filas.push(f);
  });

  let ocpList = [...state.data.ocp];
  const asientosPend = [];
  porOcp.forEach(({ ocp, filas: fs }) => {
    const descOcp = fs.reduce((s, f) => s + f.desc, 0);
    const montoOcp = fs.reduce((s, f) => s + f.saldoFila, 0) - descOcp;
    const nuevoPago = {
      monto: montoOcp,
      descuento: descOcp,
      fecha: pm.fecha,
      medio: pm.medio,
      referencia: pm.referencia,
      notas: [(pm.notas || "").trim(), `Pago conjunto de ${filas.length} factura(s) seleccionadas.`,
        descOcp > 0 ? `Incluye descuento financiero negociado de ${fmtCOP(descOcp)}.` : ""].filter(Boolean).join(" "),
      registrado_en: new Date().toISOString(),
      // Imputación explícita: el poder extintivo (monto + descuento) va a las
      // facturas elegidas, no FIFO a la más antigua.
      _facturas: fs.filter(f => f.fact).map(f => ({ fcId: f.fact.id, monto: f.saldoFila }))
    };
    const idx = ocpList.findIndex(o => o.id === ocp.id);
    const pagos = [...getPagosOCP(ocpList[idx]), nuevoPago];
    const updated = { ...ocpList[idx], _pagos: pagos, _pago: nuevoPago };
    const saldoTras = getSaldoOCP(updated);
    updated.estado_pago = saldoTras <= 0.5 ? "pagada" : (getMontoExtinguidoOCP(updated) > 0.5 ? "parcial" : "por_pagar");
    ocpList[idx] = updated;
    asientosPend.push({ updated, nuevoPago, descOcp });
  });

  saveData("ocp", ocpList);
  for (const a of asientosPend) {
    await generarAsientoPagoOCP(a.updated, a.nuevoPago, a.descOcp);
  }

  state.modal = null;
  state._pagoMulti = null;
  state.cxpSelFact = {};
  render();
  const netoTotal = totalSel - descTotal;
  alert(`✅ Pago conjunto registrado: ${filas.length} factura(s), ${porOcp.size} OCP(s).\n\nPagado: ${fmtCOP(netoTotal)}${descTotal > 0 ? `\nDescuento financiero: ${fmtCOP(descTotal)} (ingreso, cuenta 4210)` : ""}\nPasivo extinguido: ${fmtCOP(totalSel)}`);
}

export function showLiquidacionOP(opId) {
  state.viewLiquidacionOpId = opId;
  const op = state.data.pedidos.find(p => p.id === opId);
  state.liqConceptos = JSON.parse(JSON.stringify((op && op._conceptosCosto) || []));
  renderLiquidacionModal();
}

export function renderLiquidacionModal() {
  const liq = getLiquidacionOP(state.viewLiquidacionOpId);
  if (!liq) return;
  const cliente = state.data.clientes.find(c => c.id === liq.op.cliente_id) || {};

  const ocpsHTML = liq.ocps.length === 0
    ? `<div style="padding:14px;text-align:center;background:#fef3c7;border:1px dashed #fcd34d;border-radius:6px;color:#92400e;font-size:12px;">⚠️ Esta OP aún no tiene OCPs asociadas. Conviértela a OCP para empezar a liquidar costos.</div>`
    : liq.ocps.map(o => {
        const prov = state.data.proveedores.find(p => p.id === o.proveedor_id) || {};
        const pago = o.estado_pago || "por_pagar";
        const pinfo = ESTADO_PAGO[pago] || ESTADO_PAGO.por_pagar;
        return `
          <div style="padding:9px;border:1px solid ${C.border};border-radius:6px;margin-bottom:8px;display:flex;justify-content:space-between;align-items:center;background:white;">
            <div style="flex:1;">
              <div style="font-weight:700;color:${C.purple};">${escapeHTML(o.numero)}</div>
              <div style="font-size:10.5px;color:${C.muted};">${escapeHTML(prov.nombre || "—")} • ${fmtDate(o.fecha)}</div>
              ${o._pago ? `<div style="font-size:10px;color:${C.muted};margin-top:3px;">💳 Pagado: ${fmtDate(o._pago.fecha)} (${escapeHTML(o._pago.referencia || "sin ref")})</div>` : ""}
            </div>
            <div style="text-align:right;">
              <div style="font-weight:700;color:${C.text};">${fmtCOP(o.total)}</div>
              <span style="background:${pinfo.bg};color:${pinfo.color};padding:2px 7px;border-radius:6px;font-size:10px;font-weight:700;">${pinfo.label}</span>
            </div>
          </div>
        `;
      }).join("");

  const margenColor = liq.margen >= 30 ? "#10b981" : (liq.margen >= 10 ? "#f59e0b" : "#ef4444");
  const margenIcon = liq.margen >= 30 ? "📈" : (liq.margen >= 10 ? "⚖️" : "📉");
  const margenInicial = liq.margenInicial;
  const margenInicialColor = margenInicial == null ? C.muted : (margenInicial >= 30 ? "#10b981" : (margenInicial >= 10 ? "#f59e0b" : "#ef4444"));

  const conceptos = state.liqConceptos || [];
  const otrosCostosHTML = `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:10px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;">OTROS COSTOS (transporte, maquila, bordado, etc.)</div>
        <button data-action="add-liq-concepto" style="${btnStyle(C.accent,null,true)}">+ Agregar costo</button>
      </div>
      ${conceptos.length === 0 ? `
        <div style="font-size:11.5px;color:${C.muted};margin-bottom:8px;">Sin costos adicionales registrados.</div>
      ` : `
        <table style="width:100%;border-collapse:collapse;font-size:11.5px;margin-bottom:8px;">
          <thead><tr style="background:#f1f5f9;">
            <th style="padding:4px 6px;text-align:left;font-size:10px;color:${C.muted};">Concepto</th>
            <th style="padding:4px 6px;text-align:left;font-size:10px;color:${C.muted};width:170px;">Proveedor / Tercero</th>
            <th style="padding:4px 6px;text-align:right;font-size:10px;color:${C.muted};">Cant.</th>
            <th style="padding:4px 6px;text-align:right;font-size:10px;color:${C.muted};">Valor</th>
            <th style="padding:4px 6px;text-align:right;font-size:10px;color:${C.muted};">Total</th>
            ${/* La misma pregunta que en el formulario de la OP, porque es el
                  mismo costo: aquí se trabaja el de una orden ya completada,
                  que es justo la que queda bloqueada para edición. */ ""}
            <th style="padding:4px 6px;text-align:left;font-size:10px;color:${C.muted};width:160px;" title="¿Este costo se compra con una orden de compra al proveedor, o se legaliza con una cuenta de cobro que el tercero firma?">¿Qué se crea?</th>
            <th></th>
          </tr></thead>
          <tbody>
            ${conceptos.map((c,i) => `
              <tr style="border-top:1px solid ${C.border};">
                <td style="padding:3px;"><input data-liq-concepto="${i}" data-liq-field="concepto" value="${escapeHTML(c.concepto||"")}" placeholder="Ej: Transporte, Bordado..." style="${inputStyle}font-size:11.5px;padding:5px 7px;"/></td>
                ${/* El tercero se ELIGE del maestro, no se teclea: con texto
                      libre nacían fichas sin cédula y sin tarifa ("ALEXANDER",
                      "Didi"), y a esas no se les puede retener ni reportarlas
                      en la exógena (ver terceroPicker.js). */ ""}
                <td style="padding:3px;">${celdaTerceroCosto(c,
                    `data-action="buscar-tercero-liq" data-idx="${i}"`,
                    `data-action="quitar-tercero-liq" data-idx="${i}"`, true)}</td>
                <td style="padding:3px;width:80px;"><input data-liq-concepto="${i}" data-liq-field="cantidad" type="number" inputmode="decimal" value="${c.cantidad!=null?c.cantidad:""}" placeholder="0" style="${inputStyle}font-size:11.5px;padding:5px 7px;text-align:right;"/></td>
                <td style="padding:3px;width:110px;"><input data-liq-concepto="${i}" data-liq-field="valor_tercero" type="number" inputmode="decimal" value="${c.valor_tercero!=null?c.valor_tercero:""}" placeholder="0" style="${inputStyle}font-size:11.5px;padding:5px 7px;text-align:right;"/></td>
                <td data-liq-total="${i}" style="padding:6px;text-align:right;font-weight:700;">${fmtCOP((parseNumber(c.cantidad)||0)*(parseNumber(c.valor_tercero)||0))}</td>
                <td style="padding:3px;">${destinoCostoCelda(c, i,
                    (campo) => `data-liq-concepto="${i}" data-liq-field="${campo}"`)}</td>
                <td style="padding:3px;text-align:center;"><button data-action="remove-liq-concepto" data-idx="${i}" style="background:transparent;border:none;color:${C.red};cursor:pointer;font-size:16px;">🗑</button></td>
              </tr>
            `).join("")}
          </tbody>
        </table>
      `}
      <div style="display:flex;justify-content:space-between;align-items:center;border-top:2px solid ${C.border};padding-top:8px;">
        <div style="font-size:10.5px;color:${C.muted};">Estos costos afectan directamente el margen de utilidad de la OP.</div>
        <div style="text-align:right;">
          <div id="liq-otros-total" style="font-weight:700;color:${C.text};">Total otros costos: ${fmtCOP(liq.otrosCostosTotal)}</div>
          <button data-action="save-liq-concepto" ${state._guardandoLiq ? "disabled" : ""} style="${btnStyle(C.amber,null,true)}margin-top:6px;${state._guardandoLiq ? "opacity:.55;cursor:wait;" : ""}">${state._guardandoLiq ? "⏳ Guardando…" : "💾 Guardar costos"}</button>
        </div>
      </div>
    </div>
  `;

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:680px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:#f8fafc;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">📊 Liquidación de ${escapeHTML(liq.op.numero)}</h3>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">

          <div style="background:#f8fafc;border-radius:6px;padding:10px;margin-bottom:10px;">
            <div style="font-size:10.5px;color:${C.muted};font-weight:700;">CLIENTE</div>
            <div style="font-size:12px;font-weight:700;color:${C.text};">${escapeHTML(cliente.nombre || "—")}</div>
            <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">${fmtDate(liq.op.fecha)} • Estado: ${liq.op.estado || "borrador"}</div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:10px;">
            <div style="padding:10px;background:${C.blue}10;border:1px solid ${C.blue}30;border-radius:6px;">
              <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">VENTA (precio cobrado)</div>
              <div style="font-size:16px;font-weight:800;color:${C.blue};">${fmtCOP(liq.ventaTotal)}</div>
            </div>
            <div style="padding:10px;background:${C.purple}10;border:1px solid ${C.purple}30;border-radius:6px;">
              <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">COSTO TOTAL (proveedores + otros costos)</div>
              <div style="font-size:16px;font-weight:800;color:${C.purple};">${fmtCOP(liq.costoTotal)}</div>
              <div style="font-size:10.5px;color:${C.muted};margin-top:6px;">
                ✅ Pagado: <strong style="color:#10b981;">${fmtCOP(liq.costoPagado)}</strong>
                <br>
                💸 Por pagar: <strong style="color:#f59e0b;">${fmtCOP(liq.costoPorPagar)}</strong>
                <br>
                ➕ Otros costos: <strong style="color:${C.amber};">${fmtCOP(liq.otrosCostosTotal)}</strong>
              </div>
            </div>
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-bottom:10px;">
            <div style="padding:10px;background:${margenInicialColor}10;border:2px solid ${margenInicialColor}40;border-radius:6px;text-align:center;">
              <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">MARGEN INICIAL (al crear la OP)</div>
              <div style="font-size:16px;font-weight:800;color:${margenInicialColor};">${margenInicial == null ? "—" : margenInicial.toFixed(1) + "%"}</div>
              <div style="font-size:10px;color:${C.muted};margin-top:4px;">Según costo de productos en maestros</div>
            </div>
            <div style="padding:10px;background:${margenColor}10;border:2px solid ${margenColor}40;border-radius:6px;text-align:center;">
              <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:4px;">MARGEN NUEVO (real, con costos asociados) ${margenIcon}</div>
              <div style="font-size:16px;font-weight:800;color:${margenColor};">${liq.margen.toFixed(1)}%</div>
              <div style="font-size:10px;color:${C.muted};margin-top:4px;">Utilidad: ${fmtCOP(liq.utilidad)}</div>
            </div>
          </div>

          ${otrosCostosHTML}

          <div style="margin-bottom:8px;">
            <div style="font-size:11.5px;color:${C.muted};font-weight:700;letter-spacing:0.5px;margin-bottom:8px;">OCPs ASOCIADAS (${liq.ocps.length})</div>
            ${ocpsHTML}
          </div>

          <div style="display:flex;gap:10px;justify-content:flex-end;padding-top:14px;border-top:1px solid ${C.border};">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

export function renderAnticiposModulo() {
  const filtro = state.anticiposFiltro || "todos";  // todos | pendientes | cruzados
  const busq   = (state.anticiposBusq  || "").toLowerCase().trim();

  // Construir lista plana de todos los anticipos de todas las OPs
  const filas = [];
  (state.data.pedidos||[]).forEach(op => {
    if (!op._anticipos || op._anticipos.length === 0) return;
    const cliente  = (state.data.clientes||[]).find(c=>c.id===op.cliente_id)||{};
    const totalOP  = parseNumber(op.total)||0;
    const totalAnt = op._anticipos.reduce((s,a)=>s+parseNumber(a.monto),0);
    op._anticipos.forEach((ant,idx) => {
      const factCruce = ant._cruzadoFactura
        ? (state.data.facturas||[]).find(f=>f.id===ant._cruzadoFactura)
        : null;
      filas.push({ op, cliente, ant, idx, totalOP, totalAnt, factCruce });
    });
  });

  // Filtros
  let filasFilt = filas;
  // "Pendiente" es tener plata viva, no la marca vieja: un anticipo aplicado a
  // medias todavía tiene saldo a favor del cliente y debe salir en la lista.
  if (filtro === "pendientes") filasFilt = filas.filter(r=>anticipoDisponible(r.ant) > 0);
  if (filtro === "cruzados")   filasFilt = filas.filter(r=>anticipoDisponible(r.ant) <= 0);
  if (busq) filasFilt = filasFilt.filter(r=>
    (r.op.numero||"").toLowerCase().includes(busq) ||
    (r.cliente.nombre||"").toLowerCase().includes(busq) ||
    (r.ant.referencia||"").toLowerCase().includes(busq) ||
    (r.factCruce?.numero||"").toLowerCase().includes(busq)
  );

  // KPIs. Se miden por lo APLICADO y lo que queda VIVO, no por la marca
  // `_cruzado` —que solo dice "ya no queda nada"—: un anticipo aplicado a
  // medias contaba entero como pendiente y cero como cruzado. El anticipo de
  // SUMATEC (OP-0211) se aplicó a MP155 por $2.283.487 y le quedaron $49.002
  // (lo retenido), y esta tarjeta seguía anunciando $2.332.489 por cruzar.
  const totalRecibido   = filas.reduce((s,r)=>s+parseNumber(r.ant.monto),0);
  const totalCruzado    = filas.reduce((s,r)=>s+anticipoAplicado(r.ant),0);
  const totalDevuelto   = filas.reduce((s,r)=>s+anticipoDevuelto(r.ant),0);
  const totalPendiente  = filas.reduce((s,r)=>s+anticipoDisponible(r.ant),0);
  const nOPs            = new Set(filas.map(r=>r.op.id)).size;

  const kpiStyle = (col,bg) => `background:${bg};border:1px solid ${col}40;border-radius:6px;padding:10px 12px;`;

  const kpis = `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px;margin-bottom:22px;">
      <div style="${kpiStyle("#059669","#f0fdf4")}">
        <div style="font-size:10.5px;font-weight:700;color:#065f46;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">💵 Total recibido</div>
        <div style="font-size:16px;font-weight:800;color:#059669;">${fmtCOP(totalRecibido)}</div>
        <div style="font-size:10.5px;color:#6b7280;margin-top:2px;">${filas.length} anticipo${filas.length!==1?"s":""} en ${nOPs} OP${nOPs!==1?"s":""}${totalDevuelto>0?` · ${fmtCOP(totalDevuelto)} devuelto${totalDevuelto>0?"s":""} al cliente`:""}</div>
      </div>
      <div style="${kpiStyle("#f59e0b","#fffbeb")}">
        <div style="font-size:10.5px;font-weight:700;color:#92400e;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">⏳ Pendiente por cruzar</div>
        <div style="font-size:16px;font-weight:800;color:#f59e0b;">${fmtCOP(totalPendiente)}</div>
        <div style="font-size:10.5px;color:#6b7280;margin-top:2px;">${filas.filter(r=>anticipoDisponible(r.ant)>0).length} anticipo${filas.filter(r=>anticipoDisponible(r.ant)>0).length!==1?"s":""} abiertos (pasivo 2710)</div>
      </div>
      <div style="${kpiStyle("#6366f1","#eef2ff")}">
        <div style="font-size:10.5px;font-weight:700;color:#4338ca;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px;">✅ Cruzado con facturas</div>
        <div style="font-size:16px;font-weight:800;color:#6366f1;">${fmtCOP(totalCruzado)}</div>
        <div style="font-size:10.5px;color:#6b7280;margin-top:2px;">${filas.filter(r=>anticipoDisponible(r.ant)<=0).length} anticipo${filas.filter(r=>anticipoDisponible(r.ant)<=0).length!==1?"s":""} consumidos</div>
      </div>
    </div>`;

  // Filtros y búsqueda
  const filtroBtn = (id,lbl,col) => {
    const act = filtro===id;
    return `<button onclick="state.anticiposFiltro='${id}';render();"
      style="padding:7px 14px;border-radius:6px;font-size:11.5px;font-weight:700;cursor:pointer;border:1.5px solid ${act?col:"#e2e8f0"};background:${act?col+"15":"white"};color:${act?col:"#64748b"};">${lbl}</button>`;
  };

  const controles = `
    <div style="display:flex;gap:8px;align-items:center;margin-bottom:10px;flex-wrap:wrap;">
      ${filtroBtn("todos",  "Todos ("+filas.length+")",              "#374151")}
      ${filtroBtn("pendientes","Pendientes ("+filas.filter(r=>anticipoDisponible(r.ant)>0).length+")","#f59e0b")}
      ${filtroBtn("cruzados",  "Cruzados ("+filas.filter(r=>anticipoDisponible(r.ant)<=0).length+")","#6366f1")}
      <div style="flex:1;min-width:180px;">
        <input type="text" placeholder="🔍 Buscar OP, cliente, referencia o factura…"
          value="${escapeHTML(state.anticiposBusq||"")}"
          oninput="state.anticiposBusq=this.value;render();"
          style="${inputStyle}font-size:11.5px;"/>
      </div>
      <button onclick="exportarExcelAnticipos()"
        style="background:#16a34a;color:white;border:none;padding:8px 14px;border-radius:6px;font-weight:700;cursor:pointer;font-size:11.5px;white-space:nowrap;">
        📥 Exportar Excel
      </button>
    </div>`;

  // Tabla de trazabilidad
  const tablaHTML = filasFilt.length === 0
    ? `<div style="text-align:center;padding:22px;color:#94a3b8;font-size:12px;background:white;border:1px solid #e2e8f0;border-radius:6px;">
        <div style="font-size:36px;margin-bottom:10px;">💵</div>
        ${filas.length===0?"No hay anticipos registrados en ninguna OP.":"Sin resultados para el filtro seleccionado."}
      </div>`
    : `<div style="background:white;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
        <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
          <thead>
            <tr style="background:#f8fafc;border-bottom:2px solid #e2e8f0;">
              <th style="padding:6px 10px;text-align:left;font-weight:700;color:#475569;">OP</th>
              <th style="padding:6px 10px;text-align:left;font-weight:700;color:#475569;">Cliente</th>
              <th style="padding:6px 10px;text-align:right;font-weight:700;color:#475569;">Total OP</th>
              <th style="padding:6px 10px;text-align:center;font-weight:700;color:#475569;">Fecha anticipo</th>
              <th style="padding:6px 10px;text-align:right;font-weight:700;color:#475569;">Monto anticipo</th>
              <th style="padding:6px 10px;text-align:right;font-weight:700;color:#475569;" title="Lo que todavía es plata del cliente: pasivo 2710 vivo">A favor</th>
              <th style="padding:6px 10px;text-align:center;font-weight:700;color:#475569;">% sobre OP</th>
              <th style="padding:6px 10px;text-align:left;font-weight:700;color:#475569;">Medio</th>
              <th style="padding:6px 10px;text-align:left;font-weight:700;color:#475569;">Referencia</th>
              <th style="padding:6px 10px;text-align:center;font-weight:700;color:#475569;">Estado</th>
              <th style="padding:6px 10px;text-align:left;font-weight:700;color:#475569;">Cruzado con</th>
              <th style="padding:6px 10px;text-align:center;font-weight:700;color:#475569;">Asiento</th>
            </tr>
          </thead>
          <tbody>
            ${filasFilt.map(r => {
              const monto  = parseNumber(r.ant.monto)||0;
              const pct    = r.totalOP>0 ? (monto/r.totalOP*100).toFixed(1) : "—";
              const medios = {transferencia:"🏦 Transf.",efectivo:"💵 Efectivo",cheque:"📝 Cheque",tarjeta:"💳 Tarjeta",pse:"🌐 PSE",otro:"📄 Otro"};
              const medioLbl = medios[r.ant.medio]||r.ant.medio||"—";
              const usado    = anticipoAplicado(r.ant);
              const devuelto = anticipoDevuelto(r.ant);
              const disp     = anticipoDisponible(r.ant);
              const estadoBg  = disp > 0 ? (usado > 0 || devuelto > 0 ? "#fff7ed" : "#fffbeb") : "#eef2ff";
              const estadoCol = disp > 0 ? (usado > 0 || devuelto > 0 ? "#b45309" : "#f59e0b") : "#6366f1";
              const estadoLbl = disp > 0
                ? (usado > 0 || devuelto > 0 ? "◐ Parcial" : "⏳ Pendiente")
                : (usado > 0 ? "✅ Aplicado" : "↩️ Devuelto");
              const astEntry  = r.ant._asientoId
                ? (state.data.asientos||[]).find(a=>a.id===r.ant._asientoId)
                : null;
              return `
              <tr style="border-bottom:1px solid #f1f5f9;${r.ant._cruzado?"background:#fafafe;":""}">
                <td style="padding:6px 10px;">
                  <div style="font-weight:800;color:#4C5FA8;">${escapeHTML(r.op.numero)}</div>
                  <div style="font-size:10px;color:#94a3b8;">${fmtDate(r.op.fecha)}</div>
                </td>
                <td style="padding:6px 10px;">
                  <div style="font-weight:600;color:#1e293b;">${escapeHTML(r.cliente.nombre||"—")}</div>
                  ${r.cliente.nit?`<div style="font-size:10px;color:#94a3b8;">NIT ${escapeHTML(r.cliente.nit)}</div>`:""}
                </td>
                <td style="padding:6px 10px;text-align:right;font-weight:700;color:#374151;">${fmtCOP(r.totalOP)}</td>
                <td style="padding:6px 10px;text-align:center;color:#64748b;">${fmtDate(r.ant.fecha)}</td>
                <td style="padding:6px 10px;text-align:right;">
                  <div style="font-weight:800;color:#059669;font-size:12px;">${fmtCOP(monto)}</div>
                  ${usado>0?`<div style="font-size:10px;color:#94a3b8;">aplicado ${fmtCOP(usado)}</div>`:""}
                  ${devuelto>0?`<div style="font-size:10px;color:#94a3b8;">devuelto ${fmtCOP(devuelto)}</div>`:""}
                </td>
                <td style="padding:6px 10px;text-align:right;font-weight:800;font-size:12px;color:${disp>0?"#b45309":"#cbd5e1"};">${disp>0?fmtCOP(disp):"—"}</td>
                <td style="padding:6px 10px;text-align:center;">
                  <span style="background:#f8fafc;color:#16a34a;padding:2px 8px;border-radius:6px;font-weight:700;font-size:10.5px;">${pct}%</span>
                </td>
                <td style="padding:6px 10px;color:#475569;">${medioLbl}</td>
                <td style="padding:6px 10px;color:#475569;max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;" title="${escapeHTML(r.ant.referencia||"")}">
                  ${escapeHTML(r.ant.referencia||"—")}
                  ${r.ant.nota?`<div style="font-size:10px;color:#94a3b8;font-style:italic;">${escapeHTML(r.ant.nota)}</div>`:""}
                </td>
                <td style="padding:6px 10px;text-align:center;">
                  <span style="background:${estadoBg};color:${estadoCol};padding:3px 10px;border-radius:6px;font-size:10.5px;font-weight:700;">${estadoLbl}</span>
                </td>
                <td style="padding:6px 10px;">
                  ${(r.ant._aplicaciones||[]).length
                    ? (r.ant._aplicaciones||[]).map(x=>`<div style="font-weight:700;color:#059669;font-size:10.5px;">💰 ${escapeHTML(x.numero||"")} · ${fmtCOP(parseNumber(x.monto))}</div>`).join("")
                    : (r.factCruce
                      ? `<div style="font-weight:700;color:#059669;font-size:10.5px;">💰 ${escapeHTML(r.factCruce.numero)}</div>
                         <div style="font-size:10px;color:#94a3b8;">${fmtDate(r.factCruce.fecha)}</div>`
                      : `<span style="color:#cbd5e1;font-size:10.5px;">—</span>`)}
                </td>
                <td style="padding:6px 10px;text-align:center;">
                  ${astEntry
                    ? `<span title="Asiento ${astEntry.numero}" style="background:#f8fafc;color:#12264D;padding:2px 7px;border-radius:6px;font-size:10px;font-weight:700;cursor:default;">${escapeHTML(astEntry.numero)}</span>`
                    : `<span style="color:#cbd5e1;font-size:10px;">—</span>`}
                </td>
              </tr>`;
            }).join("")}
          </tbody>
          <!-- Fila de totales -->
          <tfoot>
            <tr style="background:#f8fafc;border-top:2px solid #e2e8f0;">
              <td colspan="4" style="padding:6px 10px;font-weight:700;color:#475569;">${filasFilt.length} registro${filasFilt.length!==1?"s":""}</td>
              <td style="padding:6px 10px;text-align:right;font-weight:800;color:#059669;">${fmtCOP(filasFilt.reduce((s,r)=>s+parseNumber(r.ant.monto),0))}</td>
              <td colspan="6"></td>
            </tr>
          </tfoot>
        </table>
      </div>`;

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;padding-bottom:10px;border-bottom:2px solid #059669;flex-wrap:wrap;gap:9px;">
      <div>
        <h2 style="margin:0;font-size:16px;font-weight:800;color:#1e293b;">💵 Anticipos de Clientes</h2>
        <p style="margin:3px 0 0;color:#64748b;font-size:11.5px;">Trazabilidad completa — registro, estado contable (cta. 2710) y cruce con facturas</p>
      </div>
    </div>
    ${kpis}
    ${controles}
    ${tablaHTML}
  `;
}

// ─── Ajuste de precios de una OCP ya recibida ────────────────────────────────
// El proveedor cambia el precio por su cuenta (normalmente a favor: un
// descuento) y la OCP tiene que poder cuadrar con su factura para cruzarlas.
//
// La OCP recibida sigue bloqueada para edición normal —cambiar CANTIDADES
// desalinearía el inventario ya movido y el costo promedio—, pero el PRECIO se
// ajusta por esta vía controlada:
//
//   · Solo precios. Las cantidades no se tocan.
//   · Si la mercancía ya entró, se corrige el costo promedio del inventario por
//     la diferencia (mismas unidades, otro costo). Ver ajustarCostoInventario.
//   · Baja de precio (a favor): se aplica con confirmación.
//   · Subida de precio (en contra, castiga el margen): un propietario (rol
//     admin) puede aplicarla asumiéndola; cualquier otro usuario solo puede
//     DEJARLA SOLICITADA para que un propietario la autorice.
//   · Si la OCP ya fue causada no se permite: el asiento contable ya existe por
//     el valor de la factura y hay que resolverlo por contabilidad, no aquí.
//
// Todo queda en `_ajustesPrecio` (quién, cuándo, por qué, antes y después).
export function esPropietario() {
  return state.usuarioRol === "admin";
}

export function renderAjustePreciosOCP(ocpId) {
  const ocp = (state.data.ocp || []).find(o => o.id === ocpId);
  if (!ocp) return;
  const d = state.ajustePrecios || {};
  const prov = (state.data.proveedores || []).find(p => p.id === ocp.proveedor_id) || {};
  const items = (ocp.items || []).map((it, i) => {
    const puAnt = parseNumber(it.precio_unitario) || 0;
    // El texto tal cual se escribió va al input y el número al cálculo: si se
    // reemplazara por el número, un valor a medio teclear ("72250." o vacío)
    // se pisaría en el siguiente re-render.
    const escrito = d.precios && d.precios[i] != null ? String(d.precios[i]) : String(puAnt);
    const puNue = parseNumber(escrito) || 0;
    const cant = parseNumber(it.cantidad) || 0;
    return { i, art: it.articulo, cant, puAnt, puNue, escrito, difLinea: (puNue - puAnt) * cant };
  });
  const subAnt = items.reduce((s, x) => s + x.puAnt * x.cant, 0);
  const subNue = items.reduce((s, x) => s + x.puNue * x.cant, 0);
  const dif = subNue - subAnt;
  const sube = dif > 0.5;
  const propietario = esPropietario();
  const pend = ocp._ajustePrecios && ocp._ajustePrecios.estado === "pendiente" ? ocp._ajustePrecios : null;

  const filas = items.map(x => `
    <tr style="border-top:1px solid ${C.border};">
      <td style="padding:6px 8px;font-size:11.5px;">${escapeHTML(x.art || "—")}</td>
      <td style="padding:6px 8px;text-align:right;font-size:11.5px;color:${C.muted};">${x.cant}</td>
      <td style="padding:6px 8px;text-align:right;font-size:11.5px;color:${C.muted};">${fmtCOP(x.puAnt)}</td>
      <td style="padding:6px 8px;text-align:right;">
        <input inputmode="decimal" value="${escapeHTML(x.escrito)}"
          oninput="state.ajustePrecios.precios[${x.i}]=this.value;renderAjustePreciosOCP('${ocpId}');"
          style="width:105px;padding:4px 6px;border:1.5px solid ${C.amber};border-radius:6px;font-size:11.5px;font-weight:700;text-align:right;outline:none;"
          onfocus="this.select();"/>
      </td>
      <td style="padding:6px 8px;text-align:right;font-size:11.5px;font-weight:800;color:${x.difLinea < 0 ? "#047857" : x.difLinea > 0 ? "#b45309" : C.muted};">
        ${x.difLinea === 0 ? "—" : (x.difLinea < 0 ? "▼ " : "▲ ") + fmtCOP(Math.abs(x.difLinea))}
      </td>
    </tr>`).join("");

  state.modal = `
    <div data-action="cerrar-ajuste-precios" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1150;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:760px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.25);">
        <div style="padding:13px 18px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;">
          <div>
            <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:800;">💲 Ajustar precios — ${escapeHTML(ocp.numero || "")}</h3>
            <div style="font-size:11px;color:${C.muted};margin-top:2px;">${escapeHTML(prov.nombre || "")} · para cuadrar con la factura del proveedor</div>
          </div>
          <button data-action="cerrar-ajuste-precios" style="background:transparent;border:none;font-size:18px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:14px 18px;">
          ${pend ? `
            <div style="background:#fffbeb;border:1px solid #fcd34d;border-radius:8px;padding:11px 13px;margin-bottom:12px;">
              <div style="font-weight:800;color:#92400e;font-size:12.5px;">⏳ Hay un ajuste pendiente de autorización</div>
              <div style="font-size:11px;color:#92400e;margin-top:3px;">
                Solicitado por <b>${escapeHTML(pend.solicitadoPor || "—")}</b> el ${escapeHTML(fmtDate(pend.fecha))} ·
                sube el costo en <b>${fmtCOP(pend.diferencia)}</b>${pend.motivo ? ` · "${escapeHTML(pend.motivo)}"` : ""}
              </div>
              ${propietario ? `
                <div style="display:flex;gap:8px;margin-top:9px;">
                  <button data-action="autorizar-ajuste-precios" data-id="${ocpId}" style="${btnStyle("#dc2626",null,true)}">✅ Autorizar y aplicar</button>
                  <button data-action="rechazar-ajuste-precios" data-id="${ocpId}" style="${btnStyle(null,"secondary",true)}">Rechazar</button>
                </div>`
                : `<div style="font-size:10.5px;color:#92400e;margin-top:7px;">Debe autorizarlo un propietario de la empresa.</div>`}
            </div>` : ""}

          <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:8px;padding:9px 12px;font-size:11px;color:${C.muted};line-height:1.55;margin-bottom:12px;">
            Las <b>cantidades no se tocan</b>: la mercancía ya entró al inventario. Al aplicar, se corrige el
            <b>costo promedio</b> de los artículos por la diferencia — las mismas unidades, a otro costo.
          </div>

          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:#f1f5f9;">
                ${["Artículo","Cant.","Precio OCP","Precio factura","Diferencia"].map((h,i)=>
                  `<th style="padding:7px 8px;text-align:${i===0?"left":"right"};font-size:10px;font-weight:700;color:${C.text};text-transform:uppercase;">${h}</th>`).join("")}
              </tr>
            </thead>
            <tbody>${filas}</tbody>
          </table>

          <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;margin-top:12px;padding:10px 12px;border-radius:8px;background:${dif < 0 ? "#ecfdf5" : dif > 0 ? "#fef2f2" : "#f8fafc"};border:1px solid ${dif < 0 ? "#86efac" : dif > 0 ? "#fca5a5" : C.border};">
            <div style="font-size:11.5px;color:${C.muted};">
              Subtotal OCP <b style="color:${C.text};">${fmtCOP(subAnt)}</b> → según factura <b style="color:${C.text};">${fmtCOP(subNue)}</b>
            </div>
            <div style="font-size:15px;font-weight:900;color:${dif < 0 ? "#047857" : dif > 0 ? "#dc2626" : C.muted};">
              ${dif === 0 ? "Sin cambios" : (dif < 0 ? "▼ A favor " : "▲ En contra ") + fmtCOP(Math.abs(dif))}
            </div>
          </div>

          ${sube ? `
            <div style="margin-top:11px;background:#fef2f2;border:1px solid #fca5a5;border-radius:8px;padding:11px 13px;">
              <div style="font-weight:800;color:#991b1b;font-size:12.5px;">⚠️ El costo SUBE: castiga el margen del negocio</div>
              <div style="font-size:11px;color:#991b1b;margin-top:3px;line-height:1.5;">
                ${propietario
                  ? "Como propietario puedes aplicarlo asumiendo la diferencia. Queda registrado a tu nombre."
                  : "Solo un propietario de la empresa puede aprobarlo. Puedes dejarlo solicitado y te lo autorizan."}
              </div>
            </div>` : ""}

          <div style="margin-top:11px;">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Motivo / referencia de la factura ${sube ? `<span style="color:${C.red};">*</span>` : "(opcional)"}</label>
            <input value="${escapeHTML(d.motivo || "")}" placeholder="Ej: descuento del proveedor — factura FM-9001"
              oninput="state.ajustePrecios.motivo=this.value" style="${inputStyle}"/>
          </div>

          <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:15px;padding-top:12px;border-top:1px solid ${C.border};">
            <button data-action="cerrar-ajuste-precios" style="${btnStyle(null,"secondary")}">Cancelar</button>
            ${dif === 0 ? `<button disabled style="${btnStyle(null,"secondary")}opacity:.5;cursor:not-allowed;">Sin cambios</button>`
              : (!sube || propietario)
                ? `<button data-action="aplicar-ajuste-precios" data-id="${ocpId}" style="${btnStyle(sube ? "#dc2626" : "#059669")}">
                     ${sube ? "⚠️ Aplicar asumiendo el mayor costo" : "✅ Aplicar ajuste"}</button>`
                : `<button data-action="solicitar-ajuste-precios" data-id="${ocpId}" style="${btnStyle(C.amber)}">📩 Solicitar autorización</button>`}
          </div>
        </div>
      </div>
    </div>`;
  render();
}

// Aplica el ajuste: precios, totales, costo del inventario y bitácora.
function aplicarAjustePreciosOCP(ocpId, motivo, autorizadoPor) {
  const lista = [...(state.data.ocp || [])];
  const idx = lista.findIndex(o => o.id === ocpId);
  if (idx < 0) return null;
  const ocp = lista[idx];
  const nuevos = (state.ajustePrecios && state.ajustePrecios.precios) || {};
  const yaIngresada = (ocp._recepciones || []).length > 0 || !!(ocp._procesado && ocp._procesado.ingreso);

  const detalle = [];
  const items = (ocp.items || []).map((it, i) => {
    const puAnt = parseNumber(it.precio_unitario) || 0;
    const puNue = nuevos[i] != null ? parseNumber(nuevos[i]) : puAnt;
    const cant = parseNumber(it.cantidad) || 0;
    if (Math.abs(puNue - puAnt) < 0.005) return it;
    detalle.push({ articulo: it.articulo, cantidad: cant, antes: puAnt, despues: puNue, diferencia: (puNue - puAnt) * cant });
    return { ...it, precio_unitario: puNue, total: Math.round(cant * puNue) };
  });
  if (!detalle.length) return null;

  const subtotal = items.reduce((s, it) => s + (parseNumber(it.total) || 0), 0);
  const prov = (state.data.proveedores || []).find(p => p.id === ocp.proveedor_id) || {};
  // El IVA de la compra: por línea, porque un bien excluido (art. 424) o exento
  // (art. 477) no lo lleva ni aunque el proveedor sea responsable de IVA.
  const ivaLineas = desgloseIVA(items).iva;
  const iva = prov.responsable_iva ? ivaLineas : (parseNumber(ocp.iva) ? ivaLineas : 0);

  // El inventario ya recibió la mercancía al precio viejo: se corrige el costo
  // promedio por la diferencia. Sin asiento: la contabilidad de la compra se
  // hace al causar, por el valor real de la factura.
  const ajustesInv = [];
  if (yaIngresada) {
    detalle.forEach(x => {
      const r = ajustarCostoInventario(x.articulo, x.diferencia,
        { tipo: "OCP", id: ocpId, numero: ocp.numero, motivo: motivo || "Ajuste de precios" }, today());
      if (r) ajustesInv.push({ articulo: x.articulo, antes: r.costoAnterior, ahora: r.costoPromedio });
    });
  }

  const bitacora = {
    id: genId(), fecha: today(),
    usuario: state.usuarioNombre || state.usuarioEmail || "—",
    autorizadoPor: autorizadoPor || null,
    motivo: motivo || "", detalle,
    diferencia: detalle.reduce((s, x) => s + x.diferencia, 0)
  };

  lista[idx] = {
    ...ocp, items, subtotal, iva, total: subtotal + iva,
    _ajustesPrecio: [...(ocp._ajustesPrecio || []), bitacora],
    _ajustePrecios: null      // si venía de una solicitud, queda resuelta
  };
  saveData("ocp", lista);
  return { bitacora, ajustesInv };
}

export function renderAnticiposOP(opId) {
  const op = (state.data.pedidos||[]).find(p=>p.id===opId);
  if (!op) return;
  const anticipos = op._anticipos || [];
  const totalAnticipado = anticipos.reduce((s,a)=>s+parseNumber(a.monto),0);
  const totalDisponible = anticipos.reduce((s,a)=>s+anticipoDisponible(a),0);
  const medios=[{v:"transferencia",l:"Transferencia"},{v:"efectivo",l:"Efectivo"},{v:"cheque",l:"Cheque"},{v:"otro",l:"Otro"}];

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1100;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:500px;max-width:100%;max-height:88vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.22);">
        <div style="padding:10px 12px;border-bottom:1px solid #e2e8f0;background:#f8fafc;display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:16px;font-weight:800;color:#065f46;">💵 Anticipos — ${escapeHTML(op.numero||"OP")}</div>
            <div style="font-size:10.5px;color:#6b7280;margin-top:2px;">Total anticipado: <strong style="color:#059669;">${fmtCOP(totalAnticipado)}</strong>${totalDisponible > 0 ? ` · a favor del cliente <strong style="color:#b45309;">${fmtCOP(totalDisponible)}</strong>` : ""}</div>
          </div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:#6b7280;cursor:pointer;">✕</button>
        </div>
        <div style="padding:18px 20px;">
          ${anticipos.length>0 ? `
          <div style="margin-bottom:10px;">
            <div style="font-size:10.5px;font-weight:700;color:#64748b;margin-bottom:6px;text-transform:uppercase;letter-spacing:.4px;">Anticipos registrados</div>
            <div style="border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;">
              ${anticipos.map((a,i)=>{
                const usado = anticipoAplicado(a), devuelto = anticipoDevuelto(a), disp = anticipoDisponible(a);
                const intacto = usado === 0 && devuelto === 0;
                return `
              <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:6px 10px;border-bottom:1px solid #f1f5f9;">
                <div style="min-width:0;">
                  <div style="font-weight:700;font-size:12px;color:#059669;">${fmtCOP(parseNumber(a.monto))}</div>
                  <div style="font-size:10px;color:#64748b;">${fmtDate(a.fecha)} · ${a.medio||"—"}${a.referencia?" · Ref: "+escapeHTML(a.referencia):""}</div>
                  ${(usado>0||devuelto>0)?`<div style="font-size:10px;color:#64748b;">
                    ${usado>0?`Aplicado ${fmtCOP(usado)}${(a._aplicaciones||[]).length?" ("+(a._aplicaciones||[]).map(x=>escapeHTML(x.numero||"")).filter(Boolean).join(", ")+")":""}`:""}${usado>0&&devuelto>0?" · ":""}${devuelto>0?`Devuelto ${fmtCOP(devuelto)}`:""}
                  </div>`:""}
                  <div style="font-size:10px;font-weight:700;color:${disp>0?"#b45309":"#12264D"};">${disp>0?`A favor del cliente: ${fmtCOP(disp)}`:"Consumido"}</div>
                  ${a.nota?`<div style="font-size:10px;color:#94a3b8;font-style:italic;">${escapeHTML(a.nota)}</div>`:""}
                </div>
                <div style="display:flex;gap:6px;flex-shrink:0;">
                  ${disp>0?`<button data-action="devolver-anticipo" data-op-id="${opId}" data-ant-id="${a.id}" title="Devolver al cliente el saldo a favor" style="background:#fef2f2;border:1px solid #fecaca;color:#dc2626;padding:4px 10px;border-radius:6px;font-size:10.5px;font-weight:700;cursor:pointer;white-space:nowrap;">↩️ Devolver</button>`:""}
                  ${intacto?`<button data-action="eliminar-anticipo-op" data-op-id="${opId}" data-idx="${i}" title="Eliminar el anticipo (reversa el asiento)" style="background:#fee2e2;border:none;color:#dc2626;padding:4px 10px;border-radius:6px;font-size:10.5px;font-weight:700;cursor:pointer;">✕</button>`:""}
                </div>
              </div>`;}).join("")}
              <div style="padding:6px 10px;font-size:11.5px;font-weight:700;color:#059669;background:#f8fafc;">Total anticipado: ${fmtCOP(totalAnticipado)}${totalDisponible>0?` · a favor ${fmtCOP(totalDisponible)}`:""}</div>
            </div>
          </div>` : `<div style="text-align:center;padding:11px;color:#94a3b8;font-size:12px;margin-bottom:12px;">Sin anticipos registrados</div>`}

          <div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;padding:10px;">
            <div style="font-size:10.5px;font-weight:700;color:#64748b;margin-bottom:10px;text-transform:uppercase;letter-spacing:.4px;">Registrar nuevo anticipo</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
              <div>
                <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Fecha</label>
                <input type="date" id="ant-fecha" value="${today()}" style="${inputStyle}"/>
              </div>
              <div>
                <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Monto COP</label>
                <input type="number" id="ant-monto" inputmode="decimal" placeholder="0" style="${inputStyle}text-align:right;"/>
              </div>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
              <div>
                <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Medio de pago</label>
                <select id="ant-medio" style="${inputStyle}cursor:pointer;">
                  ${medios.map(m=>`<option value="${m.v}">${m.l}</option>`).join("")}
                </select>
              </div>
              <div>
                <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Referencia</label>
                <input type="text" id="ant-referencia" placeholder="# transferencia / cheque" style="${inputStyle}"/>
              </div>
            </div>
            <div style="margin-bottom:12px;">
              <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Nota (opcional)</label>
              <input type="text" id="ant-nota" placeholder="Ej: Anticipo 50% cliente" style="${inputStyle}"/>
            </div>
            <button data-action="guardar-anticipo-op" data-op-id="${opId}" style="width:100%;background:#059669;color:white;border:none;padding:9px;border-radius:6px;font-weight:700;cursor:pointer;font-size:12px;">+ Registrar anticipo</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

import { registerActions } from "../ui/actions.js";

// ─── Guardar los costos desde la liquidación ────────────────────────────────
// Hace lo MISMO que guardar la OP: el costo con destino elegido nace como orden
// de compra o como cuenta por pagar, con su id estable y las preguntas que
// queden. Separado de la acción para que esta pueda custodiarlo contra el
// doble clic (`state._guardandoLiq`).
export async function guardarCostosLiquidacion(op) {
  const conceptos = (state.liqConceptos || []).filter(c => (c.concepto && c.concepto.trim()) || parseNumber(c.cantidad) || parseNumber(c.valor_tercero));
  const updated = { ...op, _conceptosCosto: conceptos };
  // Guardar aquí tiene que hacer lo MISMO que guardar la OP: un costo con
  // destino elegido nace como orden de compra o como cuenta por pagar. Esta
  // ventana solo grababa el costo —afectaba el margen y nada más—, así que
  // lo que se anotaba aquí no llegaba nunca ni a Órdenes de Compra ni a CxP.
  conceptos.forEach(c => {
    if (!c._ccid) c._ccid = genId();
    // Costos anteriores a la elección de destino: el documento que ya
    // tienen dice cuál era.
    if (!c._via && c._ocpId) c._via = "ocp";
    else if (!c._via && c._cxpId) c._via = "cxp";
  });
  try {
    await sincronizarOcpDeCostos(updated);
    await sincronizarCxpDirectasOP(updated);
  } catch (err) {
    alert("⚠️ No se pudieron generar los documentos de los costos: " + (err.message || err));
  }
  saveData("pedidos", state.data.pedidos.map(p => p.id === op.id ? updated : p));
  state.liqConceptos = JSON.parse(JSON.stringify(conceptos));
  renderLiquidacionModal();
  // Y los que quedaron sin responder se preguntan, como al guardar la OP.
  pedirDestinoCostos(updated);
}

registerActions({
  // ── Ajuste de precios de una OCP recibida (cuadre con la factura) ─────────
  "ajustar-precios-ocp": (d) => {
    const ocp = (state.data.ocp||[]).find(o=>o.id===d.id);
    if (!ocp) return;
    if (ocp._procesado && ocp._procesado.factura) {
      alert("Esta OCP ya fue causada: el asiento contable existe por el valor de la factura.\n\nUna diferencia posterior hay que resolverla por contabilidad (nota del proveedor), no cambiando la OCP.");
      return;
    }
    state.ajustePrecios = { precios: {}, motivo: "" };
    renderAjustePreciosOCP(d.id);
  },
  "cerrar-ajuste-precios": () => {
    state.ajustePrecios = null; state.modal = null; render();
  },
  "aplicar-ajuste-precios": (d) => {
    const ocp = (state.data.ocp||[]).find(o=>o.id===d.id);
    if (!ocp) return;
    const motivo = (state.ajustePrecios && state.ajustePrecios.motivo || "").trim();
    const nuevos = (state.ajustePrecios && state.ajustePrecios.precios) || {};
    const dif = (ocp.items||[]).reduce((s,it,i)=>{
      const puAnt = parseNumber(it.precio_unitario)||0;
      const puNue = nuevos[i] != null ? parseNumber(nuevos[i]) : puAnt;
      return s + (puNue - puAnt) * (parseNumber(it.cantidad)||0);
    },0);
    const sube = dif > 0.5;
    if (sube && !esPropietario()) { alert("Solo un propietario puede aplicar un aumento de costo."); return; }
    if (sube && !motivo) { alert("⚠️ Escribe el motivo: un aumento de costo tiene que quedar justificado."); return; }
    if (!confirm(sube
      ? `El costo SUBE ${fmtCOP(Math.abs(dif))} y castiga el margen.\n\n¿Aplicar asumiendo la diferencia?`
      : `El costo baja ${fmtCOP(Math.abs(dif))} a favor del negocio.\n\n¿Aplicar el ajuste?`)) return;
    const r = aplicarAjustePreciosOCP(d.id, motivo, sube ? (state.usuarioNombre||state.usuarioEmail||"propietario") : null);
    state.ajustePrecios = null; state.modal = null; render();
    if (!r) { alert("No había cambios que aplicar."); return; }
    const inv = r.ajustesInv.length
      ? "\n\nCosto promedio corregido:\n" + r.ajustesInv.map(a=>`• ${a.articulo}: ${fmtCOP(a.antes)} → ${fmtCOP(a.ahora)}`).join("\n")
      : "";
    alert("✅ Precios ajustados. La OCP ya cuadra con la factura." + inv);
  },
  "solicitar-ajuste-precios": (d) => {
    const motivo = (state.ajustePrecios && state.ajustePrecios.motivo || "").trim();
    if (!motivo) { alert("⚠️ Escribe el motivo del aumento para que puedan autorizarlo."); return; }
    const lista = [...(state.data.ocp||[])];
    const i = lista.findIndex(o=>o.id===d.id);
    if (i<0) return;
    const ocp = lista[i];
    const nuevos = (state.ajustePrecios && state.ajustePrecios.precios) || {};
    const dif = (ocp.items||[]).reduce((s,it,k)=>{
      const puAnt = parseNumber(it.precio_unitario)||0;
      const puNue = nuevos[k] != null ? parseNumber(nuevos[k]) : puAnt;
      return s + (puNue - puAnt) * (parseNumber(it.cantidad)||0);
    },0);
    lista[i] = { ...ocp, _ajustePrecios: {
      estado: "pendiente", fecha: today(),
      solicitadoPor: state.usuarioNombre || state.usuarioEmail || "—",
      motivo, diferencia: dif, precios: { ...nuevos }
    }};
    saveData("ocp", lista);
    state.ajustePrecios = null; state.modal = null; render();
    alert("📩 Solicitud enviada.\n\nUn propietario debe autorizarla desde la misma OCP (botón «Ajustar precios»).");
  },
  "autorizar-ajuste-precios": (d) => {
    if (!esPropietario()) { alert("Solo un propietario puede autorizar."); return; }
    const ocp = (state.data.ocp||[]).find(o=>o.id===d.id);
    const pend = ocp && ocp._ajustePrecios;
    if (!pend || pend.estado !== "pendiente") return;
    if (!confirm(`Autorizar el aumento de ${fmtCOP(Math.abs(pend.diferencia))} solicitado por ${pend.solicitadoPor}?`)) return;
    state.ajustePrecios = { precios: pend.precios || {}, motivo: pend.motivo || "" };
    const r = aplicarAjustePreciosOCP(d.id, pend.motivo, state.usuarioNombre || state.usuarioEmail || "propietario");
    state.ajustePrecios = null; state.modal = null; render();
    alert(r ? "✅ Ajuste autorizado y aplicado." : "No había cambios que aplicar.");
  },
  "rechazar-ajuste-precios": (d) => {
    if (!esPropietario()) { alert("Solo un propietario puede rechazar."); return; }
    const lista = (state.data.ocp||[]).map(o => o.id===d.id ? { ...o, _ajustePrecios: null } : o);
    saveData("ocp", lista);
    state.ajustePrecios = null; state.modal = null; render();
    alert("Solicitud rechazada.");
  },

  "fact-prov-tab": (d) => { state.factProvTab = d.tab; render(); },
  "cxp-filter": (d, el, e) => {state.cxpFilter = d.filter;
      render();
      return;},
  "pagar-ocp": (d, el, e) => {
      const _oPag = (state.data.ocp || []).find(o => o.id === d.id);
      if (esOcpInformativa(_oPag)) {
        alert("📋 " + _oPag.numero + " es una orden INFORMATIVA: no se paga por aquí.\n\n" +
          "Lo que se le paga al tercero es su cuenta de cobro, en Cuentas por Pagar.");
        return;
      }
      showPagarOCP(d.id);
      return;},
  "abono-saldo-total": (d, el, e) => {{
      const ocpObj = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
      if (!ocpObj || !state.editingPago) return;
      state.editingPago.monto = getSaldoOCP(ocpObj);
      renderPagoModal();
      return;
    }},
  "abono-por-porcentaje": (d, el, e) => {{
      const ocpObj = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
      if (!ocpObj || !state.editingPago) return;
      const pct = parseNumber(state.editingPago.porcentaje);
      if (pct <= 0 || pct > 100) { alert("⚠️ Ingresa un porcentaje entre 1 y 100"); return; }
      const bruto = parseNumber(ocpObj.subtotal);
      state.editingPago.monto = Math.round(bruto * (pct / 100));
      renderPagoModal();
      return;
    }},
  "confirmar-pago": async (d, el, e) => {await confirmarPagoOCP();
      return;},
  "ver-liquidacion": (d, el, e) => {showLiquidacionOP(d.id);
      return;},
  "ver-anticipos-op": (d, el, e) => {renderAnticiposOP(d.id);
      return;},
  "guardar-anticipo-op": async (d, el, e) => {{
      const _opIdA = d["op-id"] || d.opId;
      const _opA = (state.data.pedidos||[]).find(p=>p.id===_opIdA);
      if (!_opA) return;
      const _monto = parseNumber(document.getElementById("ant-monto")?.value)||0;
      if (_monto<=0) { alert("Ingresa un monto válido"); return; }
      const _fechaA = document.getElementById("ant-fecha")?.value||today();
      const _medioA = document.getElementById("ant-medio")?.value||"transferencia";
      const _refA   = document.getElementById("ant-referencia")?.value||"";
      const _notaA  = document.getElementById("ant-nota")?.value||"";
      const _antId  = genId();

      // ── Asiento contable: ingreso del anticipo ───────────────────────────
      // Débito  1110/1105  Bancos          → entra el dinero
      // Crédito 2710       Anticipos de clientes (pasivo) → obligación de entregar
      const _cuentaBcoA = _medioA === "efectivo" ? "1105" : "1110";
      let _asientoAntId = null;
      try {
        const _clienteOp = (state.data.clientes||[]).find(c=>c.id===_opA.cliente_id);
        const _ast = await crearAsiento(
          `Anticipo recibido — OP ${_opA.numero}${_refA?" Ref:"+_refA:""}`,
          { tipo:"OP", id:_opIdA, numero:_opA.numero },
          [
            { cuenta: _cuentaBcoA, debito:  _monto, credito:0, tercero_id: _opA.cliente_id },
            { cuenta: "2710",      debito:  0, credito: _monto, tercero_id: _opA.cliente_id }
          ],
          _fechaA
        );
        _asientoAntId = _ast.id;
      } catch(err){
        // Sin asiento no hay anticipo: guardarlo igual dejaría dinero recibido
        // sin registrar en la contabilidad (y sin el pasivo con el cliente).
        console.error("Error asiento anticipo:", err);
        alert("⚠️ No se pudo registrar el anticipo: " + err.message +
              "\n\nNo se guardó nada. Revisa que el periodo contable no esté cerrado e intenta de nuevo.");
        return;
      }

      const _ant = {
        id: _antId,
        fecha: _fechaA,
        monto: _monto,
        medio: _medioA,
        referencia: _refA,
        nota: _notaA,
        _cruzado: false,
        _asientoId: _asientoAntId   // referencia al asiento de entrada
      };
      const _anticipos = [...(_opA._anticipos||[]), _ant];
      saveData("pedidos", state.data.pedidos.map(p=>p.id===_opIdA?{..._opA,_anticipos}:p));
      renderAnticiposOP(_opIdA);
      return;
    }},
  "eliminar-anticipo-op": async (d, el, e) => {{
      const _opIdD = d["op-id"] || d.opId;
      const _opD = (state.data.pedidos||[]).find(p=>p.id===_opIdD);
      if (!_opD) return;
      const _idx = parseInt(d.idx);
      const _antDel = (_opD._anticipos||[])[_idx];
      if (_antDel && (anticipoAplicado(_antDel) > 0 || anticipoDevuelto(_antDel) > 0)) {
        alert("Este anticipo ya se usó (aplicado a una factura o devuelto) y no puede eliminarse.");
        return;
      }

      // ── Reversa del asiento de entrada si existe ─────────────────────────
      if (_antDel?._asientoId) {
        try {
          const _astOrig = (state.data.asientos||[]).find(a=>a.id===_antDel._asientoId);
          if (_astOrig) {
            // Asiento inverso: cada débito se vuelve crédito y viceversa
            const _movRev = (_astOrig.movimientos||[]).map(m=>({
              cuenta: m.cuenta, tercero_id: m.tercero_id||null,
              debito: m.credito||0, credito: m.debito||0
            }));
            await crearAsiento(
              `Reversa anticipo eliminado — OP ${_opD.numero}`,
              { tipo:"OP", id:_opIdD, numero:_opD.numero },
              _movRev,
              today()
            );
          }
        } catch(e){ console.error("Error reversa asiento anticipo:", e); }
      }

      const _ants = (_opD._anticipos||[]).filter((_,i)=>i!==_idx);
      saveData("pedidos", state.data.pedidos.map(p=>p.id===_opIdD?{..._opD,_anticipos:_ants}:p));
      renderAnticiposOP(_opIdD);
      return;
    }},
  "cargar-firmado-rc": (d, el, e) => {renderCargarFirmadoRC(d.id);
      return;},
  "eliminar-firmado-rc": (d, el, e) => {{
      const _rcF = (state.data.remisiones||[]).find(r=>r.id===d.id);
      if (!_rcF) return;
      if (!confirm("¿Eliminar el documento firmado?")) return;
      if (_rcF._docFirmado && _rcF._docFirmado.storagePath) eliminarAdjunto(_rcF._docFirmado.storagePath);
      saveData("remisiones", state.data.remisiones.map(r=>r.id===d.id?{..._rcF,_docFirmado:null}:r));
      state.modal=null; render();
      return;
    }},
  "devolver-anticipo": (d, el, e) => {
      const _opIdV = d.opId || d["op-id"];
      const _opV = (state.data.pedidos||[]).find(p=>p.id===_opIdV);
      if (!_opV) return;
      const _antV = (_opV._anticipos||[]).find(a=>a.id===(d.antId||d["ant-id"]));
      if (!_antV) return;
      const _dispV = anticipoDisponible(_antV);
      if (_dispV <= 0) { alert("Este anticipo ya no tiene saldo a favor."); return; }
      const _cliV = (state.data.clientes||[]).find(c=>c.id===_opV.cliente_id) || {};
      const _mediosV = [{v:"transferencia",l:"Transferencia"},{v:"efectivo",l:"Efectivo"},{v:"cheque",l:"Cheque"},{v:"otro",l:"Otro"}];
      state.modal = `
        <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1200;padding:11px;">
          <div data-action="prevent-close" style="background:white;border-radius:14px;width:430px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.3);">
            <div style="padding:14px 18px;border-bottom:1px solid #e2e8f0;background:#fef2f2;border-radius:14px 14px 0 0;">
              <h3 style="margin:0;font-size:15.5px;color:#991b1b;font-weight:800;">↩️ Devolver saldo a favor</h3>
              <div style="font-size:11px;color:#991b1b;margin-top:3px;">${escapeHTML(_cliV.nombre||"Cliente")} · ${escapeHTML(_opV.numero||"OP")} · a favor ${fmtCOP(_dispV)}</div>
            </div>
            <div style="padding:16px 18px;">
              <div style="font-size:11px;color:#64748b;margin-bottom:12px;line-height:1.5;">
                Sale la plata del banco y se cancela el pasivo con el cliente:
                <strong>Débito 2710 Anticipos / Crédito Bancos</strong>.
                Si prefieres dejarlo para su próxima compra, cierra esta ventana: el saldo sigue disponible.
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
                <div>
                  <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Fecha</label>
                  <input type="date" id="dev-ant-fecha" value="${today()}" style="${inputStyle}"/>
                </div>
                <div>
                  <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Monto a devolver</label>
                  <input type="number" id="dev-ant-monto" inputmode="decimal" max="${_dispV.toFixed(0)}"
                    value="${_dispV.toFixed(0)}" style="${inputStyle}text-align:right;font-weight:700;"/>
                </div>
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;">
                <div>
                  <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Medio</label>
                  <select id="dev-ant-medio" style="${inputStyle}cursor:pointer;">
                    ${_mediosV.map(m=>`<option value="${m.v}">${m.l}</option>`).join("")}
                  </select>
                </div>
                <div>
                  <label style="font-size:10px;color:#64748b;font-weight:600;display:block;margin-bottom:3px;">Referencia</label>
                  <input type="text" id="dev-ant-referencia" placeholder="# transferencia / cheque" style="${inputStyle}"/>
                </div>
              </div>
              <div style="display:flex;gap:9px;justify-content:flex-end;">
                <button data-action="close-modal" style="background:#f1f5f9;border:none;color:#1f2937;padding:9px 18px;border-radius:7px;font-weight:700;cursor:pointer;font-size:12px;">Cancelar</button>
                <button data-action="confirmar-devolucion-anticipo" data-op-id="${_opIdV}" data-ant-id="${_antV.id}"
                  style="background:#dc2626;border:none;color:white;padding:9px 18px;border-radius:7px;font-weight:700;cursor:pointer;font-size:12px;">↩️ Devolver</button>
              </div>
            </div>
          </div>
        </div>`;
      render();
      return;
    },
  "confirmar-devolucion-anticipo": async (d, el, e) => {{
      const _opIdW = d.opId || d["op-id"];
      const _opW = (state.data.pedidos||[]).find(p=>p.id===_opIdW);
      if (!_opW) return;
      const _antW = (_opW._anticipos||[]).find(a=>a.id===(d.antId||d["ant-id"]));
      if (!_antW) return;
      const _dispW = anticipoDisponible(_antW);
      const _montoW = parseNumber(document.getElementById("dev-ant-monto")?.value)||0;
      if (_montoW <= 0) { alert("Ingresa un monto válido."); return; }
      if (_montoW > _dispW + 0.01) { alert(`Solo quedan ${fmtCOP(_dispW)} a favor del cliente.`); return; }
      const _fechaW = document.getElementById("dev-ant-fecha")?.value || today();
      const _medioW = document.getElementById("dev-ant-medio")?.value || "transferencia";
      const _refW   = document.getElementById("dev-ant-referencia")?.value || "";

      // Débito  2710  Anticipos de clientes → se cancela el pasivo
      // Crédito 1110/1105 Bancos/Caja       → sale el dinero
      let _asientoDevId = null;
      try {
        const _astW = await crearAsiento(
          `Devolución de anticipo — OP ${_opW.numero}${_refW?" Ref:"+_refW:""}`,
          { tipo:"OP", id:_opIdW, numero:_opW.numero },
          [
            { cuenta:"2710", debito:_montoW, credito:0, tercero_id:_opW.cliente_id },
            { cuenta:_medioW === "efectivo" ? "1105" : "1110", debito:0, credito:_montoW, tercero_id:_opW.cliente_id }
          ],
          _fechaW
        );
        _asientoDevId = _astW.id;
      } catch(err){
        // Sin asiento no se anota la devolución: dejaría el pasivo saldado en
        // la ficha del anticipo pero vivo en la contabilidad.
        console.error("Error asiento devolución anticipo:", err);
        alert("⚠️ No se pudo registrar la devolución: " + err.message +
              "\n\nNo se guardó nada. Revisa que el periodo contable no esté cerrado e intenta de nuevo.");
        return;
      }

      const _devsW = [ ...(Array.isArray(_antW._devoluciones) ? _antW._devoluciones : []),
        { monto:_montoW, fecha:_fechaW, medio:_medioW, referencia:_refW, asientoId:_asientoDevId } ];
      const _antActW = { ..._antW, _devoluciones:_devsW };
      _antActW._cruzado = anticipoDisponible(_antActW) <= 0.01;
      saveData("pedidos", state.data.pedidos.map(p=>p.id===_opIdW
        ? {..._opW, _anticipos:(_opW._anticipos||[]).map(a=>a.id===_antW.id?_antActW:a)} : p));
      state.modal = null; render();
      alert(`↩️ Devueltos ${fmtCOP(_montoW)} al cliente.\n\n` +
            `Asiento generado: Débito 2710 Anticipos / Crédito ${_medioW === "efectivo" ? "1105 Caja" : "1110 Bancos"}.` +
            (anticipoDisponible(_antActW) > 0 ? `\n\nQuedan ${fmtCOP(anticipoDisponible(_antActW))} a favor del cliente.` : ""));
      return;
    }},
  "cruzar-anticipo-factura": async (d, el, e) => {{
      // dataset entrega las claves en camelCase (data-fact-id → factId): leer
      // d["fact-id"] devolvía undefined y la acción moría en silencio — el
      // cruce de anticipos nunca había funcionado desde esta ventana.
      const _factIdC = d.factId || d["fact-id"];
      const _antIdC  = d.antId  || d["ant-id"];
      const _opIdC   = d.opId   || d["op-id"];
      const _factC   = (state.data.facturas||[]).find(f=>f.id===_factIdC);
      const _opC     = (state.data.pedidos||[]).find(p=>p.id===_opIdC);
      if (!_factC || !_opC) return;
      const _antC = (_opC._anticipos||[]).find(a=>a.id===_antIdC);
      if (!_antC) return;

      // Cruce PARCIAL: se aplica lo que se escriba, nunca más de lo que queda
      // vivo del anticipo ni más de lo que falta por cobrar de la factura.
      // Pasarse de lo segundo dejaría la cartera del cliente en negativo, que
      // es justo lo que pasa cuando el anticipo se giró por el bruto y la
      // factura sale con retención.
      const _dispC = anticipoDisponible(_antC);
      if (_dispC <= 0) { alert("Este anticipo ya se aplicó completo."); return; }
      const _saldoC = getSaldoFactura(_factC);
      if (_saldoC <= 0) { alert(`La factura ${_factC.numero} ya está saldada: no hay nada que cruzar.`); return; }
      const _topeC  = Math.min(_dispC, _saldoC);
      const _campoC = document.querySelector(`[data-ant-monto="${_antIdC}"]`);
      const _montoC = _campoC ? (parseNumber(_campoC.value)||0) : _topeC;
      if (_montoC <= 0) { alert("Escribe cuánto del anticipo quieres aplicar a esta factura."); return; }
      if (_montoC > _topeC + 0.01) {
        alert(_dispC < _saldoC
          ? `Del anticipo solo quedan ${fmtCOP(_dispC)} disponibles.`
          : `A la factura ${_factC.numero} solo le faltan ${fmtCOP(_saldoC)} por cobrar.\n\n` +
            `Si aplicas más, la cartera del cliente quedaría en negativo. Los ${fmtCOP(_dispC - _saldoC)} que sobran ` +
            `quedan a su favor: puedes devolvérselos o aplicarlos a su próxima compra.`);
        return;
      }

      // Registrar como pago en la factura
      const _pago = {
        id: genId(), fecha: _antC.fecha, monto: _montoC,
        medio: "anticipo",
        referencia: `Anticipo OP ${_opC.numero}`,
        nota: "Cruce de anticipo registrado en OP",
        _esAnticipo: true, _anticipoId: _antIdC
      };
      const _pagos = [...(_factC._pagos||[]), _pago];
      // Mismo criterio que el abono: pagada cuando el saldo real (neto de
      // retenciones y notas crédito) queda en cero.
      const _nuevoEstado = getSaldoFactura({ ..._factC, _pagos }) <= 0.01 ? "pagada" : _factC.estado;
      saveData("facturas", state.data.facturas.map(f=>f.id===_factIdC?{..._factC,_pagos,estado:_nuevoEstado}:f));

      // ── Asiento de cruce: cancela el pasivo (2710) contra la CxC (1305) ──
      // El dinero ya está en Bancos desde el asiento de entrada del anticipo.
      // Aquí solo se extingue la obligación con el cliente y la cuenta por cobrar.
      //   Débito  2710  Anticipos de clientes  → se cancela el pasivo
      //   Crédito 1305  Clientes CxC           → se reduce la cuenta por cobrar
      let _asientoCruceId = null;
      try {
        const _astCruce = await crearAsiento(
          `Cruce anticipo — OP ${_opC.numero} → Factura ${_factC.numero}`,
          { tipo:"FACT", id:_factIdC, numero:_factC.numero },
          [
            { cuenta:"2710", debito: _montoC, credito:0, tercero_id: _factC.cliente_id },
            { cuenta:"1305", debito:0, credito: _montoC, tercero_id: _factC.cliente_id }
          ],
          today()
        );
        _asientoCruceId = _astCruce.id;
      } catch(e){ console.error("Error asiento cruce anticipo:", e); }

      // Anotar la aplicación. `_cruzado` queda en true solo cuando ya no queda
      // nada vivo, que es lo que el resto del ERP entiende por "consumido".
      const _aplicC = [ ...(Array.isArray(_antC._aplicaciones) ? _antC._aplicaciones : []),
        { facturaId:_factIdC, numero:_factC.numero, monto:_montoC, fecha:today(), asientoId:_asientoCruceId } ];
      const _antAct = { ..._antC, _aplicaciones:_aplicC,
        _cruzadoFactura: _antC._cruzadoFactura || _factIdC,
        _asientoCruceId: _antC._asientoCruceId || _asientoCruceId };
      _antAct._cruzado = anticipoDisponible(_antAct) <= 0.01;
      const _ants2 = (_opC._anticipos||[]).map(a=>a.id===_antIdC ? _antAct : a);
      saveData("pedidos", state.data.pedidos.map(p=>p.id===_opIdC?{..._opC,_anticipos:_ants2}:p));
      const _quedaC = anticipoDisponible(_antAct);
      state.modal=null; render();
      alert(`✅ Anticipo de ${fmtCOP(_montoC)} cruzado con Factura ${_factC.numero}\n\n` +
            `Asiento generado: Débito 2710 Anticipos / Crédito 1305 CxC` +
            (_quedaC > 0
              ? `\n\nQuedan ${fmtCOP(_quedaC)} a favor del cliente en ${_opC.numero}. ` +
                `Puedes devolvérselos (💵 Anticipos → Devolver) o aplicarlos a su próxima factura.`
              : ""));
      return;
    }},
  "add-costo-concepto": (d, el, e) => {{
      if (!state.editingDoc._conceptosCosto) state.editingDoc._conceptosCosto = [];
      state.editingDoc._conceptosCosto.push({ concepto:"", cantidad:"", valor_tercero:"" });
      const _scrollEl1 = document.querySelector("[data-action='prevent-close']");
      const _scroll1 = _scrollEl1 ? _scrollEl1.scrollTop : 0;
      renderDocFormModal();
      requestAnimationFrame(() => {
        const _el1 = document.querySelector("[data-action='prevent-close']");
        if (_el1) _el1.scrollTop = _scroll1;
      });
      return;
    }},
  "remove-costo-concepto": (d, el, e) => {{
      state.editingDoc._conceptosCosto.splice(parseInt(d.idx), 1);
      const _scrollEl2 = document.querySelector("[data-action='prevent-close']");
      const _scroll2 = _scrollEl2 ? _scrollEl2.scrollTop : 0;
      renderDocFormModal();
      requestAnimationFrame(() => {
        const _el2 = document.querySelector("[data-action='prevent-close']");
        if (_el2) _el2.scrollTop = _scroll2;
      });
      return;
    }},
  "add-liq-concepto": (d, el, e) => {{
      if (!state.liqConceptos) state.liqConceptos = [];
      // Con su id estable desde que nace: es lo que amarra el costo a la OCP o
      // a la CxP que salga de él, y sin él editar la orden la borra y la recrea.
      state.liqConceptos.push({ _ccid: genId(), concepto:"", proveedor_nombre:"", cantidad:"", valor_tercero:"" });
      renderLiquidacionModal();
      return;
    }},
  "remove-liq-concepto": (d, el, e) => {{
      if (!state.liqConceptos) return;
      state.liqConceptos.splice(parseInt(d.idx), 1);
      renderLiquidacionModal();
      return;
    }},
  "save-liq-concepto": async (d, el, e) => {{
      const op = state.data.pedidos.find(p => p.id === state.viewLiquidacionOpId);
      if (!op) return;
      // Un segundo clic mientras el primero sigue creando documentos no hace
      // nada: la cola de core/enSerie.js ya impide el duplicado, pero no hay
      // por qué dejar que el usuario encole un guardado que no pidió.
      if (state._guardandoLiq) return;
      state._guardandoLiq = true;
      renderLiquidacionModal();
      try {
        await guardarCostosLiquidacion(op);
      } finally {
        state._guardandoLiq = false;
      }
      return;
    }},
  "save-concepto-proveedor": (d, el, e) => {{
      const provId = d.provId || d["prov-id"];   // dataset da camelCase: la clave kebab era undefined y el concepto nunca se guardaba
      const codigo = el.value || "";
      if (!provId) return;
      const provs = [...(state.data.proveedores||[])];
      const idx = provs.findIndex(p => p.id === provId);
      if (idx >= 0) {
        provs[idx] = { ...provs[idx], concepto_codigo: codigo };
        saveData("proveedores", provs);
      }
      return;
    }},
  // Cerrar una orden informativa: el proveedor cumplió. No mueve inventario ni
  // contabilidad —no hay nada que mover—, deja constancia de cuándo y de quién.
  "cerrar-ocp-informativa": (d, el, e) => {{
      const o = (state.data.ocp || []).find(x => x.id === d.id);
      if (!o) return;
      if (o._cerrada) { alert("🔒 " + o.numero + " ya está cerrada."); return; }
      if (!confirm(`🔒 ¿Cerrar ${o.numero}?\n\nSe cierra cuando el proveedor ya cumplió con lo que dice la orden. Queda el registro de la fecha y de quién la cerró.`)) return;
      const cerrada = { fecha: today(), por: state.usuarioNombre || state.usuarioEmail || "" };
      saveData("ocp", state.data.ocp.map(x => x.id === o.id
        ? { ...x, _cerrada: cerrada, estado: x.estado === "cancelada" ? x.estado : "completada" }
        : x));
      render();
      return;
    }},
  "ver-ingreso-ocp": (d, el, e) => {{
      const _ocp = state.data.ocp.find(o => o.id === d.id);
      if (!_ocp) return;
      alert(`🛒 ${_ocp.numero}\n\n${resumenIngresoOCP(_ocp)}\n\n` +
        "La OCP está bloqueada para nuevos ingresos: repetirlos duplicaría el inventario. " +
        "Si falta mercancía física, el camino es un ajuste de inventario.");
      return;
    }},
  "abrir-ingreso-ocp": (d, el, e) => {{
      const _ocp = state.data.ocp.find(o => o.id === d.id);
      if (!_ocp) return;
      if (esOcpInformativa(_ocp)) {
        alert("📋 " + _ocp.numero + " es una orden INFORMATIVA: es el compromiso que se le mandó al proveedor, no una compra que entre a la bodega.\n\n" +
          "Por eso no se ingresa ni se causa. Cuando el proveedor cumpla, ciérrala con el botón 🔒 Cerrar.");
        return;
      }
      if (ocpTotalmenteIngresada(_ocp) || _ocp.estado === "recibida") {
        // Decir QUÉ se recibió y DÓNDE está ahora, no solo que está bloqueada.
        // "Totalmente procesada" a secas se lee como un error del sistema, y la
        // conclusión natural —volver a ingresar la mercancía— duplicaría el stock.
        alert("✅ Esta OCP ya fue completamente ingresada, así que está bloqueada: " +
          "volver a ingresarla duplicaría el inventario.\n\n" +
          resumenIngresoOCP(_ocp) +
          "\n\nSi falta mercancía física, el camino es un ajuste de inventario, no un segundo ingreso.");
        return;
      }
      // Pre-cargar precios desde los items de la OCP (precio_unitario o costo)
      const _preciosOcp = {};
      (_ocp.items || []).forEach(it => {
        const _p = parseNumber(it.precio_unitario ?? it.costo) || 0;
        if (_p > 0) _preciosOcp[it.articulo] = _p;
      });
      state.ingresoModal = { ocpId: d.id, selected: {}, cantidades: {}, precios: _preciosOcp };
      renderIngresoMercanciaModal();
      return;
    }},
  // ("toggle-ocp-ingreso" y "toggle-ocp-factura" eliminados: eran una segunda
  // vía —ya sin botón en la interfaz— para mover inventario y causar la OCP,
  // paralela a Facturas Proveedor. Dos caminos para el mismo asiento es una
  // invitación a la doble causación.)
  "cxp-fact-toggle": (d, el, e) => {{
      if (!state.cxpSelFact) state.cxpSelFact = {};
      const key = d.key;
      if (!state.cxpSelFact[key]) {
        // Un pago conjunto va a UN tercero: todas las seleccionadas deben ser
        // del mismo proveedor.
        const keysSel = Object.keys(state.cxpSelFact).filter(k => state.cxpSelFact[k]);
        if (keysSel.length > 0) {
          const ocpPrev = state.data.ocp.find(o => o.id === keysSel[0].split("::")[0]);
          if (ocpPrev && ocpPrev.proveedor_id !== d.prov) {
            const provPrev = state.data.proveedores.find(p => p.id === ocpPrev.proveedor_id) || {};
            alert(`⚠️ Ya tienes facturas de ${provPrev.nombre || "otro proveedor"} seleccionadas.\n\nUn pago conjunto agrupa facturas de UN solo proveedor. Limpia la selección para cambiar de proveedor.`);
            render();
            return;
          }
        }
        state.cxpSelFact[key] = true;
      } else {
        delete state.cxpSelFact[key];
      }
      render();
      return;
    }},
  "cxp-pago-multi-limpiar": (d, el, e) => {{
      state.cxpSelFact = {};
      render();
      return;
    }},
  "cxp-pago-multi-abrir": (d, el, e) => {{
      const filas = filasSeleccionadasPago();
      if (filas.length === 0) { alert("Selecciona al menos una factura con saldo"); return; }
      state._pagoMulti = { fecha: today(), medio: "transferencia", referencia: "", notas: "", descuento: "" };
      renderPagoMultiModal();
      return;
    }},
  "pago-multi-desc-pct": (d, el, e) => {{
      if (!state._pagoMulti) return;
      const pct = parseFloat(d.pct) || 0;
      state._pagoMulti.descuento = String(Math.round(baseDescuentoFilas(filasSeleccionadasPago()) * pct / 100));
      renderPagoMultiModal();
      return;
    }},
  "pago-multi-confirmar": (d, el, e) => {{
      confirmarPagoMulti();
      return;
    }},
  "aplicar-desc-condicion": (d, el, e) => {{
      const ocp = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
      if (!ocp || !state.editingPago) return;
      const pct = parseFloat(d.pct) || 0;
      if (pct <= 0) return;
      const vencido = d.vencido === "1";
      if (vencido && !confirm(`Este plazo de pronto pago venció hace ${d.diasVencido || "?"} día(s).\n\n¿Lo renegociaste con el proveedor y quieres aplicarlo de todas formas?\n\nQuedará registrado como descuento renegociado.`)) return;
      const saldo = getSaldoOCP(ocp);
      // El pronto pago se pacta sobre el subtotal sin IVA, no sobre el saldo
      const descuento = Math.round(baseDescuentoOCP(ocp) * pct / 100);
      if (descuento <= 0) return;
      state.editingPago.monto = Math.max(0, saldo - descuento);
      state.editingPago.descuentoAplicado = descuento;
      const nota = `Descuento pronto pago ${pct}% (${fmtCOP(descuento)})${vencido ? " — renegociado con el proveedor, plazo original vencido" : ""}.`;
      state.editingPago.notas = (state.editingPago.notas && !state.editingPago.notas.includes(nota)) ? state.editingPago.notas + " " + nota : (state.editingPago.notas || nota);
      renderPagoModal();
      return;
    }},
  "aplicar-desc-manual": (d, el, e) => {{
      const ocp = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
      if (!ocp || !state.editingPago) return;
      const saldo = getSaldoOCP(ocp);
      const descuento = parseNumber(state.editingPago.descuentoManual);
      if (descuento <= 0) { alert("Escribe el monto del descuento negociado"); return; }
      if (descuento >= saldo) { alert(`El descuento (${fmtCOP(descuento)}) no puede ser igual o mayor al saldo (${fmtCOP(saldo)})`); return; }
      state.editingPago.monto = saldo - descuento;
      state.editingPago.descuentoAplicado = descuento;
      const nota = `Descuento financiero negociado con el proveedor: ${fmtCOP(descuento)}.`;
      state.editingPago.notas = (state.editingPago.notas && !state.editingPago.notas.includes(nota)) ? state.editingPago.notas + " " + nota : (state.editingPago.notas || nota);
      renderPagoModal();
      return;
    }},
  "quitar-descuento-pago": (d, el, e) => {{
      const ocp = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
      if (!ocp || !state.editingPago) return;
      state.editingPago.descuentoAplicado = 0;
      state.editingPago.monto = getSaldoOCP(ocp);
      renderPagoModal();
      return;
    }},
  "aplicar-descuento-financiero": (d, el, e) => {{
      const ocp = state.data.ocp.find(o => o.id === state.editingPagoOcpId);
      if (!ocp || !state.editingPago) return;
      const proveedor = state.data.proveedores.find(p => p.id === ocp.proveedor_id) || {};
      const pctMap = { transferencia: "desc_fin_transferencia", efectivo: "desc_fin_efectivo", tarjeta: "desc_fin_tarjeta" };
      const campo = pctMap[state.editingPago.medio];
      const pct = campo ? (parseNumber(proveedor[campo]) || 0) : 0;
      const descuento = Math.round(parseNumber(ocp.subtotal) * pct / 100);
      const saldo = getSaldoOCP(ocp);
      state.editingPago.monto = Math.max(0, saldo - descuento);
      state.editingPago.descuentoAplicado = descuento;
      const medioLabel = (MEDIOS_PAGO.find(m=>m.value===state.editingPago.medio)||{}).label || state.editingPago.medio;
      const nota = `Incluye descuento financiero ${pct}% (${fmtCOP(descuento)}) por pago ${medioLabel}.`;
      state.editingPago.notas = (state.editingPago.notas && !state.editingPago.notas.includes(nota)) ? state.editingPago.notas + " " + nota : (state.editingPago.notas || nota);
      renderPagoModal();
      return;
    }},
});
